/**
 * English messages — the reference locale.
 *
 * This file defines the message contract. Every other locale is typed against
 * it, so a missing or misspelled key is a compile error rather than a blank
 * label discovered by a customer.
 *
 * Messages that take arguments are plain functions. That keeps interpolation
 * and grammatical agreement in the locale where they belong: English needs
 * plural forms after a numeral, Turkish does not, and a template-string format
 * cannot express that difference.
 */
export const en = {
    meta: {
        /** Written to <html lang>, used by screen readers and by the browser. */
        htmlLang: 'en',
        /** Shown in the language picker, always in the language itself. */
        nativeName: 'English',
    },

    common: {
        currencyNames: {
            TRY: 'Turkish Lira (₺)',
            USD: 'US Dollar ($)',
            EUR: 'Euro (€)',
            GBP: 'British Pound (£)',
        },
        datePlaceholder: 'dd/mm/yyyy',
        pickDate: 'Pick From the Calendar',
        chooseFile: 'Choose File',
        noFileChosen: 'No file chosen yet.',
        dropHint: 'Drop the file here or choose one.',
        loading: 'Loading…',
        // Joins the last two of a list the screen builds from data it cannot
        // phrase itself: "price, stock and delivery time".
        and: 'and',
        // Read after a link's own words by assistive technology (docs/11 §8).
        opensInNewTab: '(opens in a new tab)',
        notFound: {
            title: 'Page Not Found',
            body: 'The address may be mistyped, or the page may have moved.',
            home: 'Go to the Home Page',
        },
        forbidden: {
            title: 'You Do Not Have Access to This Page',
            body: 'Your role in this company does not include it. The company owner or an administrator can change your role.',
            home: 'Go to the Overview',
        },
        // A page that failed to draw (docs/11 §8). It words the failure and never the error.
        error: {
            title: 'The Page Could Not Be Shown',
            body: 'Something unexpected went wrong. Try again; if it keeps happening, reload the page.',
            retry: 'Try Again',
            home: 'Go to the Home Page',
        },
        // A page the role allows and the company's plan leaves out (ADR 0015).
        notInPlan: {
            title: 'Not Included in Your Package',
            body: "Your role allows this page, and your company's package does not include it.",
            subscription: 'Go to Subscription',
        },
        save: 'Save',
        saved: 'Saved.',
        // What a save bar says about its form (docs/11 §3).
        unsaved: 'There are unsaved changes.',
        clean: 'Everything is saved.',
        // Asked before a page is left with something unsaved (docs/11 §3).
        leave: {
            title: 'Unsaved Changes Will Be Lost',
            body: 'This page has changes you have not saved. If you leave it, they are lost.',
            action: 'Leave the Page',
            cancel: 'Stay on the Page',
        },
        delete: 'Delete',
        select: 'Choose…',
        edit: 'Edit',
        more: 'More',
        cancel: 'Cancel',
        close: 'Close',
        breadcrumb: 'Breadcrumb',
        confirm: 'Confirm',
        signOut: 'Sign Out',
        pager: {
            first: 'First Page',
            prev: 'Previous',
            next: 'Next',
            last: 'Last Page',
            position: (page: number, pages: number) => `${page} / ${pages}`,
        },
        language: 'Language',
        theme: {
            label: 'Theme',
            light: 'Light',
            dark: 'Dark',
            system: 'System',
        },
        genericError: 'Something went wrong. Please try again.',
        search: {
            placeholder: 'Search products, orders, customers',
            empty: 'Nothing found.',
            hint: 'Type at least two characters.',
            results: 'Search results',
            kind: {
                product: 'Product',
                order: 'Order',
                customer: 'Customer',
            },
        },
    },

    // What a failed request reads as (ADR 0005). The API names a refusal by a
    // code and these are its words; a code missing here reads as its status.
    errors: {
        status: {
            401: 'Your session has ended. Sign in again.',
            402: 'You have reached a limit your package includes.',
            403: 'You do not have permission to do this.',
            404: 'That record was not found. Refresh the page and try again.',
            409: 'That conflicts with an existing record. Check the details and try again.',
            413: 'That file is too large.',
            422: 'Some details were not accepted. Check them and try again.',
            429: 'Too many requests. Wait a moment and try again.',
        },
        field: 'Check this field.',
        problems: {
            'auth.signed_out': 'Your session has ended. Sign in again.',
            'auth.no_company': 'Choose a company first.',
            'auth.forbidden': 'You do not have permission to do this.',
            'auth.not_in_plan': "Your company's package does not include this. It can be changed on the Subscription page.",
            'auth.subscription_expired':
                'Your subscription has expired, so nothing new can be added. Orders, invoices and stock keep working, and the rest opens as soon as your payment is received.',
            'billing.plan_not_sold': 'This package is not sold here. Leave a request and our team will contact you.',
            'billing.period_not_sold': 'This package is not sold for that period.',
            'billing.method_unavailable': 'That way of paying is not open yet.',
            'billing.charge_not_pending': 'This payment is no longer waiting.',
            'billing.change_scheduled':
                'A change of package is already waiting; nothing else can be bought or extended until it starts. To change it, contact our support team.',
            'billing.charge_not_paid': 'This payment was not made.',
            'billing.card_refused': (quote: string) => `The payment could not be started: ${quote}`,
            'billing.card_unavailable': 'The payment service cannot be reached right now. Try again in a few minutes.',
            'billing.company_incomplete': 'To buy a package, your company details need a legal name, a tax number, an address and a phone.',
            'billing.agreement_not_accepted': 'To continue, tick all three boxes under the Pre-Information Form and the Distance Service Contract.',
            'billing.agreement_changed':
                'The Pre-Information Form and the Distance Service Contract have changed since you read them. Read the current texts and confirm them again.',
            'billing.support_session': 'The contract is accepted by the seller. A package cannot be bought during support access.',
            'billing.nothing_to_cancel': 'There is no paid period to cancel. A trial and a free package end on their own.',
            'billing.subscription_ended': 'The period of the package you cancelled has ended. Buy a package to continue.',
            'billing.cancel_support_session':
                'The seller cancels the subscription or takes the cancellation back. It cannot be done during support access.',
            'identity.terms_not_accepted': 'To create an account, accept the Terms of Use.',
            'identity.terms_changed': 'The Terms of Use were updated after you read them. Reload the page, then read and accept the current text.',
            'identity.terms_support_session': 'The seller accepts the Terms of Use. They cannot be accepted during support access.',
            'auth.terms_not_accepted': 'To go on, accept the current Terms of Use.',
            'identity.tax_number_invalid': 'That tax or identity number is not valid. Check it digit by digit.',
            'identity.phone_invalid': 'Enter a phone number with its area code.',
            'media.not_found': 'That image is no longer there. Refresh the page.',
            'media.too_large': 'That file is larger than 12 MB.',
            'media.not_an_image': 'That file is not an image.',
            'media.too_small': 'That image is too small to publish.',
            'media.too_many': 'This product already has the maximum number of images.',
            'storefront.image_too_large': 'That file is larger than 12 MB.',
            'storefront.image_too_small': 'That image is too small to use.',
            'storefront.logo_too_small': 'The logo must be at least 36 pixels on its shorter side.',
            'storefront.favicon_too_small': 'The site icon must be at least 48×48 pixels.',
            'storefront.not_an_image': 'That file is not an image.',
            'storefront.unsubscribe_link_unknown': 'That newsletter unsubscribe link is not one we issued.',
            'catalog.brand_taken': 'A brand with this name already exists.',
            'catalog.brand_name_unusable': 'A brand name needs at least one letter or digit.',
            'catalog.brand_in_use': 'Products still carry this brand, so it cannot be deleted. Merge it into another brand instead.',
            'catalog.brand_merge_self': 'Choose another brand to merge this one into.',
            'catalog.brand_slug_taken': 'Another brand already uses this shop address.',
            'catalog.brand_slug_unusable': 'The shop address needs at least one letter or digit.',
            'catalog.brand_logo_unknown': 'That logo could not be found. Upload it again.',
            'catalog.brand_logo_too_large': 'The logo is larger than 12 MB.',
            'catalog.brand_logo_too_small': 'The logo must be at least 300 pixels on each side.',
            'catalog.brand_logo_not_an_image': 'The logo must be a PNG, JPEG or WebP image.',
            'catalog.sku_taken': 'That product code is already used by another product.',
            'catalog.barcode_taken': 'That barcode is already used by another product.',
            'catalog.slug_taken': 'That address is already used by another product.',
            'catalog.quota_exceeded':
                'You have reached the product limit your package includes. Your products and orders keep working. To add a product, remove one or choose another package.',
            'catalog.bulk_edit_reverted': 'That bulk edit has already been undone.',
            'catalog.template_name_taken': 'A description template with this name already exists.',
            'catalog.template_incomplete': 'A template needs a name and a description with some text.',
            'catalog.match_decided': 'This match has already been decided.',
            'catalog.no_products': 'Choose at least one product.',
            'catalog.nothing_to_change': 'The selected products are already in that state.',
            'catalog.variant_exists': 'This product already has that variant.',
            'catalog.too_many_variants': 'That is more variant combinations than a product can have.',
            'catalog.duplicate_axis_value': 'A value is repeated on one of the variant attributes.',
            'catalog.no_axes': 'This product has no variant attributes to add to.',
            'catalog.variant_incomplete': 'Every variant needs a value for every variant attribute.',
            'catalog.price_invalid': 'A price cannot be zero, and a list price needs a sale price and cannot be below it.',
            'catalog.price_followed': 'A sales channel uses this price. Give that channel a price of its own before removing it.',
            'catalog.currency_followed':
                'A sales channel uses this price in its current currency. Give that channel a price of its own before changing the currency.',
            // A product import's file and its rows (ADR 0019 §5).
            'catalog.import_too_large': 'The file is larger than 16 MB. Split the products into several files.',
            'catalog.import_workbook_too_large': 'This Excel file is too large to read. Keep only the sheet of products and save it again.',
            'catalog.import_too_many_rows': 'The file has more than 5,000 product rows. Split it into several files.',
            'catalog.import_too_many_columns': 'A row of the file has values in more than 256 columns. Keep only the template’s columns.',
            'catalog.import_unreadable': 'The file could not be read. Save it in Excel as .xlsx and upload it again.',
            'catalog.import_legacy_workbook':
                'This is an old (.xls) or password-protected Excel file. Save it in Excel as .xlsx, without a password.',
            'catalog.import_columns_missing': 'The first row of the file does not name the sku and title columns. Use the template’s headings.',
            'catalog.import_required_missing': 'The product code (sku) and the product name (title) are required.',
            'catalog.import_tax_rate_invalid': 'The VAT rate (taxRate) must be 0, 1, 10 or 20.',
            'catalog.import_sku_too_long': 'The product code is longer than 64 characters.',
            'catalog.import_title_too_long': 'The product name is longer than 300 characters.',
            'catalog.import_sku_repeated': 'This product code is also on an earlier row of the file.',
            'catalog.import_formula_unresolved':
                'A formula on this row has no saved value, or its value is an error. Correct it and save the file in Excel again.',
            // What a row may bring beside the product's facts (ADR 0019 §10).
            'catalog.import_price_invalid':
                'The sale price (salePrice) or the list price (listPrice) is not usable. A sale price is above zero; a list price goes only beside one and is not below it.',
            'catalog.import_number_ambiguous':
                'An amount such as 1.500 reads two ways. Write a decimal comma and no thousands separator: 1500 or 1,5.',
            'catalog.import_currency_invalid': 'The currency (currency) must be ₺, USD, EUR or GBP. Left blank, it is ₺.',
            'catalog.import_stock_invalid': 'The stock (stock) must be a whole number from 0 to 1,000,000.',
            'catalog.import_image_invalid': 'An image address is not usable. Write one public address beginning with https in each image column.',
            'channelaccount.field_missing': 'Fill in every required connection detail.',
            'channelaccount.setting_missing': 'Fill in this setting.',
            'channelaccount.setting_not_listed': 'Choose one of the values listed for this setting.',
            'channelaccount.environment_unavailable': 'This channel is unavailable in this environment.',
            'channelaccount.unknown_channel': 'This channel cannot be connected here.',
            'channelaccount.quota_exceeded':
                'You have reached the marketplace account limit your package includes. Your connected accounts keep working. To connect another, remove a connection or choose another package.',
            'channelaccount.managed_by_shop': 'This sales channel is your own shop; it is managed in Shop Settings.',
            'newsletter.not_draft': 'A campaign that has been sent cannot be changed or deleted.',
            'newsletter.body_empty': 'Write a few words in the campaign text.',
            'newsletter.no_subscribers': 'No subscriber’s consent stands, so there is nobody to send to.',
            'newsletter.shop_closed': 'Your shop must be published to send a campaign; the links in the e-mail lead to it.',
            'notification.type_not_offered': 'Your role does not let you receive that notification by e-mail. Reload the page and choose again.',
            'channelaccount.duplicate': 'This channel is already connected with those details.',
            'channelaccount.credentials_refused': 'The sales channel did not accept the connection details. Update them on Sales Channels.',
            'channelaccount.channel_refused': (quote: string) => quote,
            'channelaccount.channel_unreachable': 'The sales channel cannot be reached right now. Try again shortly.',
            'channelaccount.check_rate_limited':
                'The sales channel is taking no more requests from this connection right now, so the check was not made and the connection keeps its status. Try again in a few seconds.',
            'auth.invalid_credentials': 'Email or password is incorrect.',
            'auth.second_factor_invalid': 'That code is not right. Enter the code your authenticator app shows now, or an unused recovery code.',
            'auth.second_factor_expired': 'This sign-in has expired. Sign in again with your password.',
            'auth.two_factor_enabled': 'Two-step verification is already on.',
            'auth.two_factor_not_started': 'Start setting up two-step verification again.',
            'auth.session_expired': 'Your session has ended. Sign in again.',
            'auth.support_link_invalid': 'This support link is no longer valid.',
            'identity.email_taken': 'That e-mail address already has an account.',
            'identity.company_address_taken': 'That company address is already taken.',
            'identity.no_access': 'You do not have access to that.',
            'identity.company_inactive': 'This company account is not active.',
            'identity.password_wrong': 'That is not your current password.',
            'identity.link_invalid': 'This link is no longer valid. Ask for a new one.',
            'identity.weak_password': 'Choose a password of at least 12 characters, with more than a few different ones.',
            'identity.password_breached': 'This password appears in published data breaches. Choose another one.',
            'identity.name_too_short': 'That name is too short.',
            'identity.email_invalid': 'Enter a valid e-mail address.',
            'identity.verification_link_invalid': 'This verification link is not valid.',
            'identity.token_scope_not_held': 'An API token can only be given permissions you hold yourself.',
            'identity.invitation_invalid': 'This invitation is no longer valid. Ask for a new one.',
            'identity.role_in_use': 'Someone still has this role. Move them to another role first.',
            'identity.role_built_in': 'Built-in roles cannot be changed. Create your own role instead.',
            'identity.role_name_taken': 'A role with that name already exists.',
            'identity.unknown_permission': 'One of those permissions is not recognised.',
            'identity.access_not_held': 'You can only give or change access you hold yourself.',
            'identity.last_admin': 'That would leave the company with nobody who can manage it.',
            'identity.already_member': 'That person is already in this company.',
            'identity.user_limit_reached':
                'You have reached the user limit your package includes. Your team keeps working. To add someone, withdraw a pending invitation, disable a member or choose another package.',
            'identity.unknown_status': 'That status is not recognised.',
            'identity.role_name_required': 'Give the role a name.',
            'storefront.legal_texts_missing': 'Write the distance-sales contract and the privacy notice before publishing the shop.',
            'storefront.same_day_needs_cut_off': 'Same-day dispatch needs an Order-by Hour. Enter one, or set Dispatch to 1 day or more.',
            'storefront.shipping_band_duplicate': 'Two desi steps start at the same desi. Give each step a starting desi of its own.',
            'storefront.promotion_not_found': 'This discount was not found; refresh the page.',
            'storefront.promotion_terms_invalid':
                'Enter a percentage above 0 and below 100, or a Buy number above the Pay number, which is at least 1.',
            'storefront.promotion_category_missing': 'Choose the category the discount applies to.',
            'storefront.promotion_products_missing': 'Add at least one product the discount applies to.',
            'storefront.promotion_window_invalid': 'The end date is before the start date.',
            'storefront.slug_taken': 'That shop address is already taken.',
            'storefront.slug_invalid': 'A shop address can contain only lowercase letters, digits and hyphens.',
            'storefront.domain_unverified':
                'The verification record was not found on your domain. Check that it is added in the domain’s DNS settings; if you have just added it, try again in a few minutes.',
            'storefront.domain_taken': 'Another shop has already verified this domain. If the domain is yours, contact our support team.',
            'storefront.theme_version_unknown': 'That look is no longer in the publish history. Refresh the page.',
            'storefront.size_guide_invalid': 'A size chart needs a title, a heading for every column and at least one row.',
            'storefront.size_guide_not_found': 'That size chart no longer exists.',
            'storefront.size_guides_full': 'The shop already has 100 size charts. Remove one you no longer use.',
            'storefront.size_guide_category_taken':
                'A category you chose is already in another size chart. A category has one chart; take it out of the other first.',
            'storefront.banner_alt_required':
                'A campaign banner without a title has no picture description. Enter what the picture shows or the words on it on the Showcase tab.',
            'order.range_reversed': 'The end date is before the start date.',
            'report.range_reversed': 'The end date is before the start date.',
            'report.period_too_long': 'A report covers a year at most. Choose a shorter period.',
            'report.range_too_long': 'That period holds more than 366 points. Pick a shorter period, or weekly or monthly points.',
            'report.product_not_found': 'That product could not be found. It may have been deleted.',
            'order.note_empty': 'Write something in the note.',
            'order.identity_number_invalid': 'That national identity number is not valid.',
            'order.identity_number_taken':
                'Another customer already has this national identity number. If they are the same person, keep using that record.',
            'order.tax_number_invalid': 'That tax number is not valid.',
            'order.not_awaiting_payment': 'This order awaits no transfer: it is paid at the door, or already paid.',
            'orderaction.line_not_on_order': 'One of the lines does not belong to this order. Refresh the page.',
            'orderaction.quantities_mismatch': 'The package quantities do not match the order lines.',
            'orderaction.no_lines': 'Choose at least one line.',
            'orderaction.unsupported': 'The channel does not support this action.',
            'orderaction.no_carrier': 'Choose a carrier.',
            'orderaction.line_not_eligible': 'The channel does not allow this action for that line.',
            'orderaction.no_label': 'The channel did not provide a label for this package.',
            'orderaction.status_param_required': 'This status needs a value the channel requires. Fill in the marked field and try again.',
            'orderaction.order_closed': 'This order is cancelled or returned. Its status can no longer be changed.',
            'orderaction.awaiting_transfer': 'The bank transfer has not been received yet. Confirm it with Payment Received first.',
            'orderaction.status_backwards': 'The package is already past this status. Choose a later one.',
            'orderaction.credentials_refused': 'The sales channel did not accept the connection details. Update them on Sales Channels.',
            'orderaction.channel_refused': (quote: string) => quote,
            'orderaction.channel_unreachable': 'The sales channel cannot be reached right now. Try again shortly.',
            'claim.no_items': 'Choose at least one item to return.',
            'claim.already_decided': 'This return has already been decided.',
            'claim.cancel_after_dispatch': 'This order has already left. Record what comes back as a return.',
            'claim.return_before_dispatch': 'Nothing of this order has left yet. Record a cancellation instead.',
            'claim.order_closed': 'This order is cancelled or returned. No cancellation or return can be recorded on it.',
            'claim.not_awaiting_pre_approval': 'This request is not waiting for a pre-approval. Reload the page.',
            'claim.pre_approval_unsupported':
                'The marketplace of this request does not take a pre-approval from here. Give it in the marketplace’s own panel.',
            'claim.pre_approval_in_flight': 'This pre-approval was just sent. Its result shows here in a moment.',
            'claim.resolution_not_exchange': 'Only an exchange request is settled with a replacement or a refund. Reload the page.',
            'claim.exchange_refund_unsupported':
                'The marketplace of this request does not take an exchange settled with a refund from here. Settle it in the marketplace’s own panel.',
            'claim.channel_refused': (quote: string) => quote,
            'claim.channel_unreachable': 'The marketplace cannot be reached right now. Try again in a few minutes.',
            'question.answer_too_short': 'The answer is shorter than this marketplace accepts. Write a little more.',
            'question.answer_too_long': 'The answer is longer than this marketplace accepts. Shorten it.',
            'question.already_answered': 'This question has been answered, and this marketplace takes one answer per question.',
            'question.closed': 'The marketplace closed this question; its answering window has passed.',
            'claim.reason_required': 'Say why you are refusing the return. A refusal without a reason is overturned by the marketplace.',
            'claim.item_not_in_claim': 'That item is not part of this return. Refresh the page.',
            'invoice.no_provider': 'Connect an e-invoice service first.',
            'invoice.already_invoiced': 'This order already has an invoice.',
            'invoice.tax_rates_stale': "The order's lines changed while you were looking. Refresh the page and enter the VAT rates again.",
            'invoice.nothing_to_refresh': 'There is nothing more to ask the e-invoice service about this document.',
            'invoice.connection_in_use':
                'Documents filed through this service are still waiting for its answer. You can disconnect it once they are settled.',
            'invoice.template_channel_taken': 'Another template already applies to this channel. Edit that one, or choose another channel.',
            'invoice.already_cancelled': 'This invoice is already cancelled.',
            'invoice.cannot_cancel': 'This e-invoice service cannot withdraw an issued invoice. Issue a credit note instead.',
            'invoice.already_returned': 'A return has already been issued for this invoice.',
            'invoice.not_issued': 'This invoice has not been issued yet.',
            'invoice.lines_unmatched': 'Match every line on the order to a product before invoicing it.',
            'invoice.nothing_to_invoice': 'No line is left to bill on this order.',
            'invoice.unknown_provider': 'This e-invoice service cannot be used here.',
            'invoice.credential_missing': 'Fill in every required connection detail.',
            'payment.unknown_provider': 'This payment service cannot be used here.',
            'payment.credential_missing': 'Fill in every required connection detail.',
            'payment.unknown_setting': 'Choose one of the values the payment service offers.',
            'payment.environment_absent': 'The payment service has no such environment. Choose Test or Live as it offers.',
            'payment.already_connected': 'This payment service is already connected. Change its keys on the connection.',
            'payment.payments_in_flight': 'A shopper is paying through this service right now. Try again in a few minutes.',
            'sms.unknown_provider': 'This SMS service cannot be used here.',
            'sms.credential_missing': 'Fill in every required connection detail.',
            'sms.unknown_setting': 'Choose one of the values the SMS service offers.',
            'sms.already_connected': 'An SMS service is already connected. Disconnect it before connecting another.',
            'sms.not_mobile': 'Enter a Turkish mobile number, such as 0532 123 45 67.',
            'sms.test_limit': 'At most 10 test messages can be sent in an hour. Try again later.',
            'invoice.no_lines': 'An invoice needs at least one line.',
            'invoice.buyer_unidentified': "A company invoice needs the buyer's tax number. Add it to the customer record.",
            'invoice.exemption_reason_required': 'A line without VAT needs an exemption code.',
            'invoice.issued_in_the_future': "An invoice cannot be dated in the future. Check the order's date.",
            'invoice.rate_required': 'A foreign-currency invoice needs an exchange rate.',
            'invoice.registration_unknown':
                'The tax authority did not answer whether the buyer is registered for e-invoicing, so the right document cannot be chosen. Try again in a few minutes.',
            'invoice.no_connection_for_kind':
                'None of your e-invoice services issues the document this buyer needs: an e-Invoice to a business registered for e-invoicing, an e-Archive to everyone else. Turn that document on for a service in Invoice Settings, or connect one that issues it.',
            'invoice.connection_wrong_kind':
                'The chosen e-invoice service does not issue the document this buyer needs: an e-Invoice to a business registered for e-invoicing, an e-Archive to everyone else. Choose another service, or turn that document on for this one in Invoice Settings.',
            'invoice.document_kind_required': 'Choose whether this service issues e-Invoices, e-Archives or both.',
            'invoice.not_recorded':
                'This invoice was not recorded by a service that only books the sale, so there is no serial to record against it.',
            'invoice.already_issued': 'This invoice already has a serial. An issued invoice is never edited; issue a credit note instead.',
            'invoice.upload_not_offered':
                'This invoice already has its document, or its e-invoice service supplies it, so a PDF cannot be uploaded. Refresh the page.',
            'invoice.invalid_tax_number': "The buyer's tax or identity number does not pass its own checksum. Correct it on the customer record.",
            'invoice.internet_sale_incomplete':
                "An online sale's invoice needs the payment method, the payment date and the website. Complete the order's details first.",
            'invoice.no_document': 'There is no document to hand over yet. Upload the PDF, or wait for the service to produce one.',
            'invoice.buyer_has_no_address':
                "The buyer has no e-mail address the document can be sent to: a marketplace's masked address reaches nobody. Add their own address to the customer record.",
            'invoice.unknown_exemption_code': 'That VAT exemption code is not one the tax authority publishes. Choose one from the list.',
            'invoice.not_a_document': 'That file is not a PDF. Upload the invoice as a PDF.',
            'invoice.document_too_large': 'That PDF is larger than 5 MB. Download the invoice from the service again and upload that file.',
            'invoice.unsupported': 'This e-invoice service cannot do that.',
            'invoice.provider_unavailable': 'The e-invoice service cannot be reached right now. Try again in a few minutes.',
            'invoice.range_reversed': 'The end date is before the start date.',
            'shipping.no_carrier': 'Connect a carrier first.',
            'shipping.origin_is_default': 'Make another address the default before removing this one.',
            'shipping.no_origin': 'Add the address parcels are sent from before booking one.',
            'shipping.already_booked': 'That parcel already has a booking.',
            'shipping.not_booked': 'That shipment has not been booked yet.',
            'shipping.no_label': 'This shipment has no label to print.',
            'shipping.courier_unknown': 'That courier cannot be used here. Choose one from the list.',
            'shipping.courier_incomplete': 'For a courier not on the list, enter its name and tax number.',
            'shipping.courier_tax_number_invalid': "The courier's tax number is not valid. Check the 10-digit number.",
            'shipping.unknown_carrier': 'This carrier cannot be used here.',
            'shipping.credential_missing': 'Fill in every required connection detail.',
            'shipping.carrier_unavailable': 'The carrier cannot be reached right now. Try again in a few minutes.',
            'shipping.awaiting_transfer': 'The bank transfer has not been received yet. Confirm it with Payment Received before shipping.',
            'shipping.order_closed': 'This order is cancelled or returned. There is nothing to ship.',
            'feed.name_taken': 'That name is already in use.',
            'feed.already_running': 'This product list is already being read.',
            'feed.address_forbidden': 'That address cannot be read. Only public addresses starting with https are allowed.',
            'feed.unreachable': "The supplier's address cannot be reached. Check the address, or try again later.",
            'feed.unreadable': 'The file could not be read in the format chosen. Check the format.',
            'feed.no_item_path': 'No repeating record was found in the file. Give the path to one, such as Products/Product.',
            'feed.mapping_incomplete': 'Map a stock code (or barcode) and a title before the list can import anything.',
            'feed.schedule_invalid': 'That schedule could not be understood. Use a cron expression, such as 0 */6 * * *.',
            'mapping.category_unknown': 'That marketplace category is no longer in the list. Choose another one.',
            'mapping.category_not_leaf': 'Choose the most specific category. Products cannot go into a category that has sub-categories.',
            'mapping.category_closed': 'The marketplace has closed this category to new products.',
            'mapping.copy_category_mismatch':
                'These two products are in different marketplace categories. Their attributes are different questions, so the answers cannot be copied.',
            'mapping.copy_source_unmapped':
                'The product to copy from has no category on this marketplace. Map its category first, or choose a product whose category is mapped.',
            'mapping.copy_from_self': 'Choose a different product to copy from.',
            'mapping.brand_not_in_directory': "That brand is no longer in the marketplace's list. Search again and choose another one.",
            'mapping.brand_request_unsupported':
                'This marketplace takes no brand request. The brand goes out as text with the product, and its own review judges it.',
            'mapping.brand_request_in_flight': 'This brand was just requested through this connection. Its answer is awaited; it is not sent twice.',
            'mapping.brand_request_refused': (quote: string) => quote,
            'mapping.brand_request_unauthorized':
                "The marketplace refused this connection's credentials. Check them under Sales Channels, then try again.",
            'mapping.brand_request_unreachable': 'The marketplace cannot be reached right now. Try again in a few minutes.',
            'mapping.brand_image_too_large': "A brand's image may be at most 1 MB.",
            'mapping.brand_image_unusable': "A brand's image must be a PNG or a JPEG.",
            'pricing.name_taken': 'A rule with that name already exists.',
            'pricing.already_reverted': 'Those prices have already been put back.',
            'pricing.nothing_to_revert': 'A preview changed no price, so there is nothing to put back.',
            'pricing.rule_unnamed': 'Give the rule a name.',
            'pricing.margin_unreachable': 'A target margin of 100% or more cannot be reached at any price.',
            'pricing.rule_no_price': 'This rule produces no price. Enter a value above zero.',
            'pricing.commission_too_high': 'A commission of 100% or more leaves nothing to price.',
            'pricing.floor_above_ceiling': 'The floor cannot be above the ceiling.',
            'pricing.rule_covers_nothing': 'The rule covers no product. Choose what it applies to.',
            'pricing.commission_needs_one_channel': 'Each channel charges its own commission. Choose the one channel this rule prices.',
            'pricing.commission_unsupported': 'This channel does not report its commission rates. Enter the rate yourself.',
            'pricing.credentials_refused': 'The sales channel did not accept the connection details. Update them on Sales Channels.',
            'pricing.channel_refused': (quote: string) => quote,
            'pricing.channel_unreachable': 'The sales channel cannot be reached right now. Try again shortly.',
            'pricing.undercut_unreachable': 'An undercut of 100% or more leaves no price. Enter less than 100.',
            'pricing.competitor_needs_channel': 'Competitor prices are read per marketplace. Choose the channel this rule prices.',
            'pricing.competitor_needs_floor': 'A rule that follows competitors needs a floor price above zero.',
            'pricing.value_negative': 'Only a rule on the product price takes a negative figure.',
            'pricing.discount_unreachable': 'A discount of 100% or more leaves no price.',
            'pricing.standing_rule_change': 'Choose one price change to keep: a fixed price, a percentage or a sum.',
            'inventory.insufficient': 'There is not enough stock for that. Check the quantity.',
            'inventory.warehouse_code_taken': 'That warehouse code is already in use.',
            'inventory.warehouse_has_stock': 'The warehouse still holds stock. Move it to another warehouse with Move the Stock first.',
            'inventory.transfer_same_warehouse': 'Choose a different warehouse to move the stock into.',
            'inventory.transfer_target_inactive': 'Stock cannot be moved into a paused warehouse. Choose a warehouse that is selling.',
            'inventory.transfer_exceeds_free':
                'The source warehouse does not hold that much stock free to move. Enter at most what is not reserved for orders.',
            'inventory.default_warehouse': 'The default warehouse cannot be removed.',
            'listing.duplicate_row': 'The same product appears twice in the list.',
            'listing.too_many_rows': 'The list has too many rows. Split it into smaller ones.',
            'listing.channel_not_connected': 'That channel is not connected.',
            'listing.distribute_no_source': 'Choose a source channel or the product price, or enter a flat price.',
            'listing.distribute_too_many':
                'This list holds more than 5,000 variants, and one run adds at most 5,000. Narrow it with the search or the filters and add the products in parts.',
            'listing.publish_too_many':
                'The selected products hold more than 5,000 variants on this channel, and one run publishes at most 5,000. Select fewer and publish them in parts.',
            'listing.nothing_to_change': 'Choose something to change.',
            'listing.currency_not_accepted': 'This channel does not sell in that currency.',
            'listing.delivery_days_above_max': 'This channel keeps a shorter dispatch time. Enter no more days than the box allows.',
            'listing.no_product_price': 'A variant has no product price to use. Enter it on the Stock and Price tab.',
            'listing.stock_from_shelf': "This channel's stock follows your shelf. Change it on the Stok page rather than here.",
            'listing.content_kept_by_channel':
                'This channel does not let an integration change the title or description of a product it lists. Make the change in the channel’s seller panel.',
            'listing.sheet_columns_missing':
                'The first row must name the code column (sku) and at least one of salePrice, listPrice, currency and stock. Start from the downloaded file.',
            'listing.sheet_other_channel':
                'This file was downloaded for another sales channel. Choose that channel, or download this channel’s file.',
            'listing.sheet_required_missing': 'The code (sku) is missing.',
            'listing.sheet_sku_repeated': 'This code is also on an earlier row of the file.',
            'listing.sheet_sku_unknown': 'No product variant has this code.',
            'listing.sheet_number_invalid': 'A price is not a number, or the stock is not a whole number.',
            'listing.sheet_number_ambiguous':
                'An amount such as 1.500 reads two ways. Write a decimal comma and no thousands separator: 1500 or 1,5.',
            'listing.sheet_zero_price': 'The sale price must be above zero.',
            'listing.sheet_negative_stock': 'Stock cannot be below zero.',
            'listing.sheet_needs_price': 'This product is not on this channel yet. Enter a sale price.',
            'listing.sheet_list_below_sale': 'The list price must be above zero and not below the sale price.',
            'listing.withdraw_unsupported': 'This channel cannot take a product off sale.',
            'listing.match_decision_unsupported': 'This channel does not take a match decision from here. Give it in the channel’s own panel.',
            'listing.no_match_awaiting': 'No match of this product on this channel is waiting for your decision. Reload the page.',
            'listing.price_unlock_unsupported': 'This channel cannot release a price lock from here. Release it in the channel’s own panel.',
            'listing.no_price_lock': 'This product has no locked price on this channel. Reload the page.',
            'listing.answer_in_flight': 'This answer was just sent to the channel. Its result shows here in a moment.',
            'listing.channel_refused': (quote: string) => quote,
            'listing.channel_unreachable': 'The channel cannot be reached right now. Try again in a few minutes.',
            'contact.account_email_missing': 'Your account has no usable e-mail address. Add one on your account page.',
            'contact.message_required': 'Write a message.',
            'webhook.address_refused': 'That address cannot be used. Enter a public address starting with https.',
            'channelwebhook.unsupported': 'This channel does not send notifications.',
            'channelwebhook.channel_unreachable': 'The channel cannot be reached right now. Try again in a few minutes.',
            'channelwebhook.channel_refused': (quote: string) => quote,
            // A carrier's or an invoicing provider's refusal, shown in its own
            // words: the one diagnosis the seller can act on (docs/15 §6).
            'shipping.carrier_refused': (quote: string) => quote,
            'shipping.carrier_unsupported': (quote: string) => quote,
            'invoice.provider_refused': (quote: string) => quote,
            'invoice.provider_not_ready': (quote: string) => quote,
            'payment.provider_not_ready': (quote: string) => quote,
            'sms.provider_not_ready': (quote: string) => quote,
        },
    },

    auth: {
        secondFactor: {
            title: 'Two-Step Verification',
            hint: 'Enter the code your authenticator app shows. If you do not have your phone, enter one of your recovery codes.',
            code: 'Verification Code',
            submit: 'Verify',
            startOver: 'Sign In Again',
        },
        signInTitle: 'Sign in',
        signInSubtitle: 'Sign in to reach your account.',
        signUpTitle: 'Create an Account',
        signUpSubtitle: (trialDays: number) => (trialDays > 0 ? `Free for ${trialDays} days, no card required.` : 'No card required.'),
        email: 'Email',
        password: 'Password',
        passwordHint: 'At least 12 characters.',
        name: 'Full Name',
        companyName: 'Company Name',
        signIn: 'Sign in',
        signUp: 'Create Account',
        // One box (ADR 0018 §1): the terms accepted, the privacy notice only
        // read — it is information, never consent. Each name is a link, with
        // the words each language puts before and after it.
        terms: {
            before: 'I have read and accept the ',
            link: 'Terms of Use',
            after: '.',
            noticeBefore: 'I have read the ',
            noticeLink: 'Privacy Notice',
            noticeAfter: ' on how my personal data is processed.',
        },
        noAccount: 'No account yet?',
        forgot: {
            link: 'I Forgot My Password',
            title: 'Reset Your Password',
            hint: 'Write your e-mail address and we will send you a link.',
            submit: 'Send the Link',
            sent: 'The link is on its way.',
            sentHint:
                'If an account exists for that address, the message arrives within a few minutes. If it does not, the address may be mistyped; try again.',
            backToSignIn: 'Back to Sign In',
        },
        reset: {
            title: 'Choose a New Password',
            hint: 'This link works once.',
            password: 'New Password',
            passwordHint: 'At least 12 characters.',
            again: 'New Password Again',
            mismatch: 'The two passwords do not match.',
            submit: 'Change the Password',
            done: 'Your password has changed. Sign in with the new one.',
            signIn: 'Sign in',
            noToken: 'This link looks incomplete.',
            askAgain: 'Ask for a New Link',
        },
        confirmEmail: {
            title: 'Your E-mail Address',
            done: 'Your address is updated. Sign in with it from now on.',
            failed: 'This link is no longer valid. It may have expired or already been used.',
            signIn: 'Sign in',
        },
        haveAccount: 'Already have an account?',
        invalidCredentials: 'Email or password is incorrect.',
    },

    // The current terms, accepted before the panel goes on (ADR 0018 §1).
    termsGate: {
        title: 'Accept the Terms of Use',
        body: (company: string) => `To go on in ${company}, read and accept the Terms of Use. You have no recorded acceptance for this company.`,
        updatedTitle: 'The Terms of Use Were Updated',
        updatedBody: (date: string, company: string) =>
            `The Terms of Use were updated on ${date}. To go on in ${company}, read and accept the current text.`,
        changeTitle: 'What Changed',
        read: 'Read the Terms of Use',
        evidence: 'Your acceptance is recorded with the date, time, IP address and browser.',
        accept: 'Accept and Continue',
    },

    company: {
        selectTitle: 'Choose a Company',
        selectSubtitle: 'Pick the company you want to work in.',
        noneTitle: 'No Company',
        noneSubtitle:
            'This account belongs to no company. If you are an entegrepazar admin, use the admin panel; if you are a seller, ask the person who can add you to their company.',
        signOut: 'Sign Out',
    },

    bulkEdit: {
        modes: {
            rule: 'Same Change for All',
            rows: 'Product by Product',
            file: 'From Excel',
        },
        standing: {
            open: 'Save as a Rule',
            title: 'Save as a Rule',
            description:
                'The rule prices the products this selection covers on this channel now, and those that enter it later, every day, ahead of your other rules.',
            fromProductPrice:
                'A percentage or a sum is worked out from each product’s product price every time the rule runs, not from the channel’s current price. A product without a product price is skipped.',
            stockNotKept: 'The stock change is not part of the rule.',
            name: 'Rule Name',
            defaultName: (channel: string) => `Bulk Edit · ${channel}`,
            floor: 'Floor Price',
            floorHint: 'Optional. The rule never sets a price below it.',
            submit: 'Save and Apply',
            saved: (changed: number, skipped: number) =>
                `The rule is saved. ${changed} price(s) changed, ${skipped} skipped. It runs again every day.`,
            viewRules: 'See It on Pricing',
        },
        sheet: {
            title: 'Price and Stock from Excel',
            intro: 'Download this channel’s products, change prices and stock in Excel, and upload the file. Nothing changes until you apply it.',
            download: 'Download Excel File',
            preview: 'Preview',
            apply: (n: number) => `Apply ${n} Change(s)`,
            summary: (committed: boolean, changed: number, unchanged: number, imported: number, refused: number) =>
                `${changed} ${committed ? 'changed' : 'will change'} · ${unchanged} unchanged · ${imported} already imported · ${refused} refused`,
            applied: (n: number) => `${n} offer(s) updated. The changes are queued to the channel.`,
            nothingToApply: 'No row of this file changes an offer.',
            verdicts: {
                changed: 'Will Change',
                changedDone: 'Changed',
                unchanged: 'No Change',
                imported: 'Already Imported',
            },
            stockIgnored: 'Stock comes from the shelf; the file’s stock was not read.',
            stockFromShelf: 'This channel takes its stock from your shelf, so its file has no stock column and a stock typed into one is not read.',
            columnsTitle: 'Columns',
            columns: {
                sku: 'Variant code. Required; the row is found by it.',
                title: 'Product name, for reading. Not imported.',
                salePrice: 'Sale price on this channel.',
                listPrice: 'List price (struck through). Not below the sale price.',
                currency: 'Currency, such as ₺ or USD. Left blank, the offer keeps its own.',
                stock: 'Stock on this channel, a whole number.',
                channelId: 'The sales channel the file was downloaded for. Leave it as it is.',
            },
            blankHint: 'A blank cell leaves that value as it is. A product new to this channel needs a sale price.',
            againHint: 'A row applied once is not applied again: uploading the same file twice changes nothing the second time.',
            limitsHint: 'A file holds at most 5,000 rows and 16 MB. Formulas are not calculated; the value Excel saved is read.',
        },
        rows: {
            title: 'Price and Stock Sheet',
            search: 'Search by code or name',
            empty: 'Nothing on This Channel Yet',
            emptyHint: 'Products appear here once they carry an offer on this channel.',
            count: (shown: number, total: number) => `${shown} of ${total} rows`,
            review: (count: number) => (count === 0 ? 'Review Changes' : `Review ${count} Change(s)`),
            reviewTitle: 'What This Would Do',
            apply: 'Apply',
            state: 'Result',
            willChange: 'Will Change',
            rowProblems: {
                not_in_catalogue: 'Not in the catalogue',
                zero_price: 'Price cannot be zero',
                negative_stock: 'Stock cannot be negative',
                needs_price: 'New on this channel; enter a price',
                list_below_sale: 'List price below the sale price',
            },
            unchanged: 'No Change',
        },
        title: 'Bulk Edit',
        subtitle: 'Change price and stock across many products at once — and see exactly what it will do before it does it.',
        whatToChange: 'Which Products',
        channel: 'Channel',
        pickChannel: 'Choose a Channel',
        category: 'Category',
        allCategories: 'All Categories',
        anyStatus: 'Any Status',
        searchLabel: 'Search',
        theChange: 'The Change',
        price: 'Price',
        priceUnchanged: 'Leave Unchanged',
        priceSet: 'Set to',
        priceAdjust: 'Adjust by %',
        priceAmount: 'Add or Subtract a Sum',
        priceValue: 'Value',
        setStock: 'Set Stock To',
        stockValue: 'Quantity',
        preview: 'Preview',
        whatWillHappen: 'What Will Happen',
        willChange: (n: number) => `${n} will change`,
        willSkip: (n: number) => `${n} skipped`,
        nothingMatches: 'Nothing Matches This Filter',
        priceColumn: 'Price',
        skipped: 'Skipped',
        sampleNote: (shown: number, total: number) => `Showing ${shown} of ${total}`,
        apply: (n: number) => `Apply to ${n} Products`,
        applied: (n: number) => `${n} products updated. The changes are queued to the channel.`,
    },

    nav: {
        support: 'Support',
        menu: 'Menu',
        dashboard: 'Overview',
        products: 'Products',
        orders: 'Orders',
        channels: 'Sales Channels',
        inventory: 'Inventory',
        invoices: 'Invoices',
        shipping: 'Shipping',
        claims: 'Returns',
        customers: 'Customers',
        warehouses: 'Warehouses',
        pricing: 'Pricing',
        bulkUpdate: 'Bulk Edit',
        suppliers: 'Suppliers',
        team: 'Team',
        storefrontSettings: 'Shop Settings',
        reviews: 'Reviews',
        account: 'My Account',
        subscription: 'Subscription',
        questions: 'Questions',
        webhooks: 'Webhooks',
        apiTokens: 'API Access',
        notifications: 'Notifications',
        newsletter: 'Newsletter',
        coupons: 'Discount Codes',
        sizeGuides: 'Size Charts',
        promotions: 'Automatic Discounts',
        mapping: 'Matching',
        finance: 'Finance',
        settlements: 'Settlements',
        profitability: 'Profitability',
        productPerformance: 'Product Performance',
        categories: 'Categories',
        brands: 'Brands',
        brandMapping: 'Brand Matching',
        developer: 'Developer',
        salesTrend: 'Sales Trend',
    },

    navGroups: {
        catalog: 'Catalogue',
        sales: 'Sales',
        storefront: 'Tezgah',
        channels: 'Channels',
    },

    finance: {
        title: 'Finance',
        subtitle: 'What the marketplaces actually paid — sales less commission, fees and returns.',
        fromDate: 'From',
        toDate: 'To',
        clearDates: 'Clear Dates',
        period: (from: string, to: string, count: number) =>
            from && to
                ? `${count} transactions between ${from} and ${to}.`
                : from
                  ? `${count} transactions since ${from}.`
                  : to
                    ? `${count} transactions up to ${to}.`
                    : `${count} transactions, all time.`,
        net: 'Net Paid Out',
        breakdown: 'How It Adds Up',
        breakdownHint: 'What each marketplace paid and took. Open a line for the transactions behind it.',
        filtered: (bucket: string) => `Showing one kind of transaction: ${bucket}. The rest are hidden.`,
        showAll: 'Show All',
        netHint: 'What reaches your bank after deductions',
        bucket: {
            sale: 'Sales',
            commission: 'Commission',
            fee: 'Fees',
            return: 'Returns',
            discount: 'Discounts',
            other: 'Other',
        },
        /** See tr.ts: the marketplaces' documented movement types, keyed by the type folded to letters and digits. */
        txType: {
            sale: 'Sale',
            payment: 'Payment',
            commission: 'Commission',
            commissionrefund: 'Commission Refund',
            commissionagreementinvoice: 'Commission Agreement Invoice',
            return: 'Return',
            returninvoice: 'Return Invoice',
            manualrefund: 'Manual Refund',
            discount: 'Discount',
            tydiscount: 'Marketplace Discount',
            campaigndiscount: 'Campaign Discount',
            coupon: 'Discount Code',
            tycoupon: 'Marketplace Discount Code',
            provision: 'Provision',
            deliveryfee: 'Delivery Fee',
            deliveryprocessingfee: 'Delivery Processing Fee',
            shipmentcostsharingexpense: 'Shipment Cost Sharing',
            customersatisfaction: 'Customer Satisfaction Deduction',
            stoppage: 'Withholding Tax',
            integrationfee: 'Marketplace Integration Fee',
            bnplorder: 'Buy Now, Pay Later Order',
            paybylink: 'Pay by Link',
            cashadvance: 'Cash Advance',
            wiretransfer: 'Wire Transfer',
            incomingtransfer: 'Incoming Transfer',
            paymentorder: 'Payment Order',
            deductioninvoices: 'Deduction Invoices',
            financialitem: 'Financial Item',
            settlementdifference: 'Settlement Difference',
        },
        transactions: 'Transactions',
        noTransactions: 'No Transactions Yet',
        date: 'Date',
        type: 'Type',
        order: 'Order',
        amount: 'Amount',
        empty: 'No Settlement Data Yet',
        emptyHint: 'It appears here once a marketplace is connected and its finance data is first updated.',
        noChannelHint: 'Finance comes from marketplaces. Connect one first.',
    },

    profitability: {
        subtitle: 'What each sale earned once the marketplace, the VAT, the goods and your own parcels are taken out.',
        groupBy: 'Show By',
        grouping: {
            order: 'Order',
            product: 'Product',
            channel: 'Sales Channel',
        },
        channelFilter: 'Sales channel',
        allChannels: 'All Sales Channels',
        rowFilter: 'Rows',
        allRows: 'All Rows',
        lossRows: 'Only Rows at a Loss',
        clearFilters: 'Clear Filters',
        period: (from: string, to: string) => `Orders placed between ${from} and ${to}.`,
        totals: {
            revenue: 'Sales',
            deductions: 'Deductions',
            goodsCost: 'Cost of Goods',
            profit: 'Profit',
            margin: 'Margin',
        },
        columns: {
            order: 'Order',
            date: 'Date',
            channel: 'Sales Channel',
            product: 'Product',
            units: 'Units',
            orders: 'Orders',
            revenue: 'Sales',
            deductions: 'Deductions',
            unattributed: 'Other Deductions',
            vat: 'VAT',
            goodsCost: 'Cost of Goods',
            shipping: 'Shipping',
            profit: 'Profit',
            margin: 'Margin',
        },
        pending: 'Awaiting Settlement',
        costMissing: 'Cost Missing',
        shippingUnpriced: 'Price Unknown',
        unmatched: 'Sales matching no product',
        leftOut: (pending: number, missing: number) =>
            [pending > 0 ? `${pending} awaiting settlement` : '', missing > 0 ? `${missing} with a cost missing` : ''].filter(Boolean).join(' · '),
        pendingAlert: (count: number) => `${count} orders are left out of the totals until the marketplace reports what it paid for them.`,
        costAlert: (count: number) =>
            `${count} orders are left out of the totals because a product in them had no cost recorded when it sold. Record your products' costs so that later sales are counted.`,
        costAlertLink: 'Go to Products',
        hint: "Sales are what the marketplace settled, or what the buyer paid in your shop, VAT included. Deductions are the marketplace's commission, fees, refunds and discounts. Profit is what is left once the VAT, the goods at the cost recorded when they sold and your own parcels are taken out.",
        unattributedHint: 'Other deductions are those a marketplace makes without naming an order, such as withholding tax.',
        empty: 'No Orders in This Period',
        emptyHint: 'Choose another period or sales channel.',
        emptyLoss: 'Nothing Sold at a Loss',
        emptyLossHint: 'No row in this period lost money. Choose another period, or show all rows.',
        count: (shown: number, total: number) => `${shown} of ${total} rows`,
    },

    productPerformance: {
        subtitle: 'Which products bring in your sales, and which stock on the shelf has stopped selling.',
        deadDays: 'No Sale For',
        leadDays: 'Lead Time (Days)',
        coverDays: 'Target Cover (Days)',
        reorderNote: (lead: number, cover: number) =>
            `Suggested orders follow the last 30 days of sales: an order placed now arrives in ${lead} days and should then last ${cover} days. Nothing is suggested for variants a supplier list supplies.`,
        days: (days: number) => `${days} days`,
        clearFilters: 'Clear Filters',
        period: (from: string, to: string) => `Sales in orders placed between ${from} and ${to}.`,
        deadPeriod: (days: number) => `Dead stock is stock on the shelf with no sale in the last ${days} days.`,
        view: 'Show',
        filters: {
            all: 'All',
            A: 'A',
            B: 'B',
            C: 'C',
            dead: 'Dead Stock',
        },
        totals: {
            revenue: 'Sales',
            units: 'Units Sold',
            sold: 'Variants Sold',
            dead: 'Dead Stock',
        },
        columns: {
            product: 'Product',
            units: 'Units',
            revenue: 'Sales',
            share: 'Share',
            class: 'Class',
            stock: 'Stock',
            coverDays: 'Days of Cover',
            suggestedOrder: 'Suggested Order',
            lastSold: 'Last Sale',
        },
        deadBadge: 'Dead Stock',
        neverSold: 'Never sold',
        hint: 'Variants are ranked by what they brought in, VAT included, less what was cancelled or came back. A is the variants that make up the first 80% of your sales, B the next 15% and C the rest.',
        deadHint: 'Variants with stock on the shelf that have not sold in the chosen number of days, those unsold longest first.',
        empty: 'No Sales in This Period',
        emptyHint: 'Choose another period.',
        deadEmpty: 'No Dead Stock',
        deadEmptyHint: (days: number) => `Every variant on the shelf has sold in the last ${days} days.`,
        count: (shown: number, total: number) => `${shown} of ${total} rows`,
    },

    salesTrend: {
        title: 'Sales Trend',
        subtitle: 'Orders, units and revenue over time. Revenue is what stayed sold, as on the Overview: cancellations and returns are taken off.',
        bucket: 'Group By',
        buckets: {
            day: 'Daily',
            week: 'Weekly',
            month: 'Monthly',
        },
        channel: 'Sales Channel',
        allChannels: 'All Sales Channels',
        measure: 'Chart',
        measures: {
            revenue: 'Revenue',
            orders: 'Orders',
            units: 'Units',
        },
        columns: {
            period: 'Period',
            orders: 'Orders',
            units: 'Units',
            revenue: 'Revenue',
        },
        period: (from: string, to: string) => `From ${from} to ${to}. A week is named by its Monday.`,
        summary: (measure: string, from: string, to: string) => `${measure} from ${from} to ${to}.`,
        product: (title: string) => `Showing one product: ${title}. Revenue is its own lines, not whole orders.`,
        productUnnamed: 'Showing one product. Revenue is its own lines, not whole orders.',
        allProducts: 'Show All Products',
        empty: 'No Orders in This Period',
        emptyHint: 'Pick a longer period or another sales channel.',
    },

    mapping: {
        copy: {
            open: 'Copy From Another Product',
            title: 'Copy Channel Answers',
            hint: 'Two products of the same kind take the same answers. Copying fills the gaps and leaves what you have already answered alone.',
            from: 'Copy From',
            preview: 'See What Would Change',
            apply: 'Copy',
            plan: (copied: number, kept: number) =>
                kept > 0
                    ? `${copied} answers would be copied. ${kept} you have already answered differently would be left alone.`
                    : `${copied} answers would be copied.`,
            withCategory: 'The marketplace category would be copied too.',
        },
        channel: 'Channel',
        subtitle: "Map your categories to the channel's own tree — a product cannot be published until it is mapped.",
        noChannel: 'No Marketplace Connected',
        noChannelHint: 'Connect a marketplace first to map categories.',
        mappedCategories: 'Mapped Categories',
        mappableProducts: 'Publishable Products',
        mappableProductsHint: 'Products whose category is mapped',
        categories: 'Your Categories',
        noCategories: 'No Categories Yet',
        editTitle: 'Edit the Category',
        editBody: 'Its address on the shop and its place in the tree stay as they are.',
        categoryName: 'Category Name',
        description: 'Description',
        descriptionHint: 'Up to 400 characters, shown under the category heading on your shop and to search engines. Plain text only.',
        ourCategory: 'Your Category',
        channelCategory: 'Marketplace Category',
        unmapped: 'Unmapped',
        map: 'Map',
        select: 'Select',
        unmap: 'Remove',
        mapTitle: (name: string) => `Pick a Marketplace Category for ${name}`,
        searchPlaceholder: 'Search marketplace categories',
        searchHint: 'Type at least two characters to search.',
        searchOtherHint: 'Not among them? Type at least two characters to search every category.',
        suggested: 'Suggested Categories',
        similarity: 'Similarity',
        noResults: 'No Results',
        noResultsHint: "The marketplace's categories may not be updated yet.",
        matches: 'Matching categories',
        filter: {
            channel: 'Ready for',
            anyChannel: 'Marketplace Readiness',
            readyFor: (marketplace: string) => `Readiness for ${marketplace}`,
            state: 'Mapping state',
            anyState: 'Any State',
            unmapped: 'Not Mapped',
            incomplete: 'Missing Information',
            ready: 'Ready to Send',
        },
        bulk: {
            setCategory: 'Assign Category',
            setCategoryHint: (count: number) => `${count} selected products will be filed under this category.`,
            noCategory: 'No Category',
            setChannelCategory: 'Assign Marketplace Category',
            setChannelCategoryHint: (count: number) =>
                `${count} selected products will be sent to this marketplace category, whatever their own category maps to.`,
            apply: 'Apply',
            overwrite: 'Replace the Existing Product Choices',
            overwriteHint: 'Off by default, so a choice made for a single product is not buried by a bulk change.',
            wouldOverwrite: (count: number) => `${count} of these products already have a marketplace category of their own.`,
            mapDone: (mapped: number, skipped: number) =>
                skipped > 0
                    ? `${mapped} products were mapped, ${skipped} were left with the choice they already had.`
                    : `${mapped} products were mapped.`,
        },
        scope: {
            product: 'This Product Only',
            category: 'From Its Category',
            productHint: 'This product goes to a different marketplace category from the rest of its category.',
            categoryHint: "Comes from this category's mapping. Changing it moves every product in the category.",
            override: 'Choose a Different Category for This Product',
            clearOverride: 'Use the Category Mapping Again',
        },
        stale: {
            title: 'Mappings That Need Checking',
            description: 'The marketplace has reshaped its tree since these were decided.',
            missing: 'The Category No Longer Exists',
            notLeaf: 'The Category Now Has Sub-Categories and Cannot Hold Products',
            unavailable: 'The Category Is Closed to New Products',
            outdated: 'Decided Against an Older Version of the Tree',
        },
        import: {
            title: 'Catalogue Import',
            description: 'Pull the products you already sell on this marketplace into Tezgah.',
            run: 'Import Products',
            running: 'Importing…',
            never: 'Not imported yet',
            lastRun: (imported: number, created: number, matched: number) =>
                `${imported} products · ${created} new · ${matched} recognised from another marketplace`,
            pending: (count: number) => `${count} products need you to say whether they are ones you already sell.`,
            // Left out for the package's product limit (ADR 0017 §2); what matched still updates.
            overLimit: (count: number) =>
                `${count} product(s) were not added: the product limit your package includes. Products already matched keep updating.`,
            pendingLink: 'Review Them',
            failed: (count: number) =>
                `${count} products could not be imported. The reason is in the server log; retrying the sweep tries them again.`,
            queued: 'The import has been queued. It runs in the background and may take a while.',
            alreadyRunning: 'An import is already running.',
        },
    },

    matches: {
        subtitle: 'Marketplace products we could not tell apart from ones you already sell. Nothing is merged until you say so.',
        empty: 'Nothing Waiting',
        emptyHint: 'Every imported product was either recognised or added. Anything we cannot be sure about will appear here.',
        failed: 'That decision could not be saved. It may already have been made.',
        channelId: 'Marketplace Id',
        barcodes: 'Barcodes',
        stockCodes: 'Stock Codes',
        candidates: (count: number) => `${count} possible matches in your catalogue`,
        link: 'This Is the Same Product',
        separate: 'Add as a Separate Product',
        confirmLinkTitle: 'These Will Be Linked',
        confirmLinkBody: (incoming: string, existing: string) =>
            `"${incoming}" will be treated as the marketplace's copy of "${existing}". Your own title, description and category stay as they are; only the marketplace listing is attached.`,
        reason: {
            ambiguous: 'Several of your products match this. Nothing says which one it is.',
            conflict: 'The identifiers disagree: the stock code matches one product and the barcode points somewhere else.',
        },
        evidence: {
            barcode: (codes: string) => `Same Barcode: ${codes}`,
            stockCode: (codes: string) => `Same Stock Code: ${codes}`,
            title: 'Same Name',
        },
    },

    brandMapping: {
        subtitle:
            'Pair your brands with each marketplace’s. A product goes out under the brand paired here, unless it names its own for the channel.',
        listTitle: 'Your Brands',
        noBrands: 'No Brands Yet',
        noBrandsHint: 'Brands arrive with the products you import, or are added from a product’s Brand field.',
        noRows: 'No Brand in This State',
        count: (shown: number, total: number) => `${shown} of ${total} brands`,
        columns: {
            brand: 'Your Brand',
            counterpart: 'Marketplace Brand',
            state: 'Status',
            source: 'Source',
        },
        filter: 'Status',
        anyState: 'All Statuses',
        state: {
            mapped: 'Matched',
            suggested: 'Suggestion',
            missing: 'Not Matched',
            retired: 'Gone from the Marketplace',
        },
        source: {
            imported: 'From an Import',
            matched: 'Matched Automatically',
            suggested: 'Accepted Suggestion',
            manual: 'Your Choice',
            name: 'The Brand’s Name',
        },
        suggestion: (name: string, score: string) => `Suggested: ${name} (${score})`,
        accept: 'Accept',
        change: 'Change',
        lastRefresh: (when: string) => `Last updated ${when}`,
        neverRefreshed: 'This marketplace’s brand list has not been fetched yet. It is fetched every night; until then a search finds nothing in it.',
        refreshing: 'The list is being updated.',
        refreshFailed: 'The last update of the list did not finish. The list already stored stays in use, and publishing is not held up.',
        textChannel: (channel: string) =>
            `${channel} takes the brand as text with the product and judges it in its own review. Your brands are compared with the brand lists of the categories you have matched; where a category has no list, the brand goes as text, written as matched or as the brand’s own name. It takes no brand request through the integration: if it restricts a brand, or a category takes only the brands on its own list, ask through the ${channel} seller panel or your category manager there.`,
        noRequest: (channel: string) =>
            `${channel} takes no brand request through the integration. For a brand missing from its list, ask through the ${channel} seller panel.`,
        acceptAll: 'Accept Suggestions',
        acceptTitle: 'Exact Suggestions Will Be Accepted',
        acceptCount: (count: number) =>
            `${count} brands will be matched to the marketplace brand whose name is exactly theirs. Brands already matched are left as they are.`,
        acceptNone: 'No exact suggestion is waiting on this list.',
        acceptDone: (count: number) => `${count} brands matched.`,
        mapTitle: (brand: string) => `Marketplace Brand for ${brand}`,
        searchPlaceholder: 'Search marketplace brands',
        suggested: 'Suggested Brands',
        noResultsDirectory: 'The marketplace’s list may not have been fetched yet, or the brand is not on it.',
        noResultsText: 'Only the brand lists of the categories you have matched are searched. You can send the name as you typed it.',
        scopeCategory: 'From the brand list of the product’s category.',
        scopeFallback: 'Not in the brand list of the product’s category; from the lists of the other categories you have matched.',
        scopeChannel: 'From the brand lists of the categories you have matched.',
        useTyped: (name: string) => `Send as “${name}”`,
        typedHint: 'The marketplace takes the brand as text, so a name not on any list goes out as written.',
        requestBrand: 'Request Brand',
        requestDescription: (channel: string) =>
            `${channel} judges the request by its images and, if it accepts it, adds the brand to its list at once. A request cannot be taken back.`,
        requestFor: (brand: string) => `When the marketplace creates it, ${brand} is matched to it.`,
        connection: 'Sales Channel',
        nameHint: 'As it is to appear on the marketplace.',
        images: 'Brand Images',
        imagesHint: 'One to three PNG or JPEG images, at most 1 MB each: the logo, and the brand on a product or its packaging.',
        addImages: 'Choose Images',
        removeImage: (name: string) => `Remove ${name}`,
        send: 'Send Request',
        sent: (name: string, channel: string) =>
            `Request sent: ${name}. ${channel} registered the brand at once; it is usually usable within a few minutes. If ${channel} still refuses it on a publish, the reason shows on the product’s channel card.`,
    },

    companyBrands: {
        subtitle: 'Your company’s brands, as your products, your shop and the marketplaces use them.',
        add: 'New Brand',
        edit: 'Edit the Brand',
        search: 'Search brands',
        empty: 'No Brands Yet',
        emptyHint: 'Add a brand here; brands also arrive with the products you import.',
        noMatch: 'No Brand Matches the Search',
        count: (shown: number, total: number) => `${shown} of ${total} brands`,
        columns: {
            brand: 'Brand',
            products: 'Products',
            shop: 'Shop',
            marketplaces: 'Marketplaces',
        },
        products: (count: number) => (count === 1 ? '1 product' : `${count} products`),
        shown: 'Shown',
        hidden: 'Hidden',
        marketplaceState: (channel: string, state: string) => `${channel}: ${state}`,
        name: 'Name',
        slug: 'Shop Address',
        slugHint: 'Your shop shows the brand at /marka/ followed by this address. Left blank, it is made from the name.',
        description: 'Description',
        descriptionHint: 'Plain text up to 400 characters, shown under the brand’s heading in your shop.',
        logo: 'Logo',
        logoChoose: 'Choose a Logo',
        logoHint: 'PNG, JPEG or WebP, at least 300 pixels.',
        logoUploading: 'Uploading…',
        logoRemove: 'Remove the Logo',
        showInShop: 'Show in the Shop',
        showInShopHint: 'The brand gets a page in your shop, and its products link to it.',
        save: 'Save',
        manage: 'Merge or Delete',
        merge: 'Merge',
        mergeTitle: (name: string) => `Merge ${name}`,
        mergeInto: 'Brand to Keep',
        mergeIntoHint: 'The brand’s products move to the brand you choose, which keeps its marketplace pairings; then this brand is deleted.',
        mergePick: 'Choose a Brand',
        noOtherBrand: 'There is no other brand to merge this one into.',
        confirmMerge: {
            title: 'Brands Will Be Merged',
            body: (from: string, into: string, count: number) =>
                `${count === 1 ? '1 product' : `${count} products`} of ${from} move to ${into}. ${from} is deleted with its marketplace pairings; ${into} keeps its own.`,
            action: 'Merge',
        },
        merged: (from: string, into: string, count: number) =>
            `${from} merged into ${into}; ${count === 1 ? '1 product' : `${count} products`} moved.`,
        delete: 'Delete',
        inUse: (count: number) =>
            `${count === 1 ? '1 product carries' : `${count} products carry`} this brand, so it cannot be deleted. Merge it into another brand instead.`,
        confirmDelete: {
            title: 'Brand Will Be Deleted',
            body: (name: string) => `${name} is deleted with its marketplace pairings.`,
            action: 'Delete',
        },
        deleted: (name: string) => `${name} deleted.`,
    },

    sources: {
        title: 'Marketplace Links',
        subtitle:
            'Which marketplaces know this product, and under what id. An id means something only within its own connection; separate a record that is not this product with Remove the Match.',
        unlink: 'Remove the Match',
        unlinkTitle: 'The Match Will Be Removed',
        unlinkBody: (channel: string) =>
            `${channel}'s product will be separated from this one and added as a product of its own on the next import. The correction is remembered, so it will not be matched back.`,
    },

    products: {
        moreActions: 'More Actions',
        addVariant: {
            open: 'Add Variant',
            title: 'Add a Variant',
            description:
                'Everything already on this product keeps its identifier, so orders, listings and stock stay attached. A value a variant attribute does not have yet can be typed; it is added to that attribute.',
            axisHint: 'Pick an existing value or type a new one.',
            submit: 'Add',
        },
        title: 'Products',
        subtitle: 'Everything in your catalogue, across every sales channel.',
        add: 'Add Product',
        distribute: {
            button: 'Add to Channel',
            title: 'Add Products to a Channel',
            subtitle: (count: number) =>
                `Adds the ${count === 1 ? 'product' : `${count} products`} the list shows to the channel you choose. A product already on that channel keeps its price and stock there, and nothing is removed from the source.`,
            target: 'Target Channel',
            source: 'Price Source',
            sourceHint: "Each new listing's starting price and stock are copied from this channel. Every channel is independent afterwards.",
            pick: 'Choose…',
            productPrice: 'Product Price',
            productPriceHint:
                "Each listing is priced at its product's price and moves with it. Products with no price, or priced in a currency this channel does not sell in, are skipped.",
            adjust: 'Price Difference (%)',
            adjustHint: 'Applied to the source price: 10 raises by ten percent, -5 lowers.',
            flatPrice: 'Flat Price',
            flatPriceHint: 'Starting price for products with no price on the source. Applied to every product when no source is chosen.',
            defaultQuantity: 'Default Stock',
            publish: 'Publish Immediately',
            submit: 'Add',
            // What was added, what the channel already had and kept, and what
            // had nothing to start from (ADR 0006 §1).
            done: (created: number, existing: number, skipped: number) =>
                [
                    `${created} ${created === 1 ? 'product' : 'products'} added.`,
                    existing > 0 ? `${existing} already on this channel, left as ${existing === 1 ? 'it was' : 'they were'}.` : '',
                    skipped > 0 ? `${skipped} skipped (no price on the source).` : '',
                ]
                    .filter(Boolean)
                    .join(' '),
            doneFollowing: (created: number, existing: number, skipped: number) =>
                [
                    `${created} ${created === 1 ? 'product' : 'products'} added.`,
                    existing > 0 ? `${existing} already on this channel, left as ${existing === 1 ? 'it was' : 'they were'}.` : '',
                    skipped > 0 ? `${skipped} skipped (no product price, or a currency this channel does not sell in).` : '',
                ]
                    .filter(Boolean)
                    .join(' '),
        },
        // Kanalda Yayınla: the products selected on the list, published on one
        // channel at what each offer there holds (ADR 0006 §1).
        publishSelection: {
            button: 'Publish on Channel',
            title: 'Publish the Selection on a Channel',
            subtitle: (count: string) =>
                `Selected products: ${count}. Each goes out at the price and stock saved for the target channel, and no price is changed.`,
            target: 'Target Channel',
            pick: 'Choose…',
            submit: 'Publish',
            retry: 'Retry the Failed',
            nothing: 'Nothing of the selection is waiting to go to this channel.',
            outcome: {
                creates: 'Goes on Sale for the First Time',
                updates: 'Sends Its Waiting Change',
                current: 'Already Live',
                sending: 'Being Sent',
                unlisted: 'Not on This Channel',
                not_active: 'Product Not Active',
                no_price: 'No Price',
                currency: 'Currency Not Sold Here',
                in_review: 'Awaiting Approval',
                withdrawn: 'Taken Off Sale',
                held: 'Waiting for the Channel',
            },
            hint: {
                current: 'On this channel as saved, with no change to send.',
                sending: 'Already on its way, so it is not sent twice.',
                unlisted: 'Put it on this channel with Add to Channel first.',
                not_active: 'A product that is not active is put on no sales channel. Make it Active on the Products page.',
                no_price: 'A variant has no price on this channel. Enter it on the product’s Channels tab.',
                currency: 'Priced in a currency this channel does not sell in.',
                in_review: 'The channel is reviewing it, and nothing is sent until it decides.',
                withdrawn: 'Taken off sale on this channel. Put it back on sale from the product’s page.',
                held: 'The channel takes no more just now. The change goes on its own within a few minutes.',
            },
            // Where the session may, what keeps these back is completed below
            // rather than on another page (ADR 0006 §1).
            hintCompletable: {
                unlisted: 'Add it to this channel below.',
                not_active: 'A product that is not active is put on no sales channel. Make it Active below.',
                no_price: 'A variant has no price on this channel. Enter it below.',
            },
            activate: {
                title: 'Products Not Active',
                hint: (count: string) =>
                    `Products not active: ${count}. Made Active, they may be put on your sales channels; nothing is sent until you start the publish.`,
                submit: 'Make Active',
                done: (count: string) => `Products made Active: ${count}.`,
            },
            add: {
                title: 'Add to This Channel',
                hint: (count: string) =>
                    `Products not on this channel: ${count}. Choose where their price starts from. Added, they join what goes out, and you start the publish.`,
                submit: 'Add to Channel',
            },
            price: {
                title: 'Missing Prices',
                hint: 'These variants have no price on this channel. The price you enter is this channel’s own and does not follow the product price.',
                label: (sku: string) => `${sku} Sale Price`,
                submit: 'Save the Prices',
                done: (count: string) => `Variants priced: ${count}.`,
                refused: (sku: string, reason: string) => `${sku}: ${reason}`,
            },
            more: (count: string) => `and ${count} more`,
            failure: (sku: string, reason: string) => `${sku}: ${reason}`,
            sent: (count: string) => `Products sent to the channel: ${count}.`,
            batches: 'Submissions',
            batch: (state: string, count: string) => `${state} · ${count}`,
            published: 'Live',
            failed: 'Not Sent',
            failedHint: 'Each reason is beside its product. Fix it, then retry only the failed.',
        },
        search: 'Search by name or code',
        empty: 'No Products Yet',
        emptyHint: 'Add your first one to start selling.',
        noMatches: 'Nothing Matched That Search',
        count: (shown: number, total: number) => `Showing ${shown} of ${total}`,

        columns: {
            mpn: 'MPN',
            product: 'Product',
            code: 'Code',
            variants: 'Variants',
            channels: 'Channels',
            status: 'Status',
            updated: 'Updated',
            barcode: 'Barcode',
            cost: 'Cost',
            desi: 'Desi',
            weightGram: 'Weight (g)',
            stock: 'Stock',
            listPrice: 'List Price',
            salePrice: 'Sale Price',
        },

        status: {
            draft: 'Draft',
            active: 'Active',
            archived: 'Passive',
            all: 'All Statuses',
        },

        filter: {
            channel: 'Channel',
            allChannels: 'All Channels',
            unlisted: 'On No Channel',
            // With a channel chosen, the status control asks after that
            // channel's offer rather than the product's own status.
            channelStatus: 'State on the Channel',
            anyChannelStatus: 'Any State on the Channel',
        },

        // Named per sales channel, the way the channel filter names them.
        // A chip's tooltip and accessible name: the connection and the state of
        // its offer there, and for an offer on sale that sells nothing — no
        // stock, or the product not active — that it is not selling (ADR 0009 §2).
        channelState: {
            offer: (channel: string, state: string) => `${channel}: ${state}`,
            notSelling: (channel: string, state: string) => `${channel}: ${state}, not selling`,
        },

        variantCount: (count: number) => (count === 1 ? '1 variant' : `${count} variants`),
        // The product's own state in the catalogue. Sending it to a channel is
        // listings.publish, a different action with a different word (docs/15 §2).
        publish: 'Activate',
        archive: 'Deactivate',
        salesTrend: 'View Sales Trend',
        delete: 'Delete',
        templates: {
            choose: 'Choose a Template',
            chooseHint: 'The template’s text is written into the description, where you can adjust it for this product.',
            use: 'Use',
            useLabel: (name: string) => `Use the ${name} Template`,
            delete: 'Delete',
            deleteLabel: (name: string) => `Delete the ${name} Template`,
            empty: 'No saved templates',
            emptyHint: 'Write a description and keep it with Save as Template to choose it on later products.',
            save: 'Save as Template',
            saveHint: 'The description in the editor is added to your company’s templates under this name.',
            name: 'Template Name',
            confirmReplace: {
                title: 'The Description Will Be Replaced',
                body: (name: string) => `The text in the editor is replaced with the “${name}” template.`,
                action: 'Use the Template',
            },
            confirmDelete: {
                title: 'The Template Will Be Deleted',
                body: (name: string) => `The “${name}” template is deleted. Products that started from it keep their description.`,
                action: 'Delete',
            },
        },
        duplicate: {
            action: 'Copy',
            title: 'Copy the Product',
            hint: 'A new draft product with the description, category, brand, VAT, variants and attributes of this one. Images, barcodes, stock and sales channels are not copied.',
            noPrices: 'Prices are not copied either.',
            submit: 'Copy',
        },
        confirmDelete: {
            title: 'The Product Will Be Deleted',
            body: 'It leaves the catalogue and its code is free to use again. Past orders are kept.',
            action: 'Delete',
        },
        confirmBulkDelete: {
            title: 'The Products Will Be Deleted',
            body: (count: number) =>
                `${count} products leave the catalogue and are withdrawn from every channel that holds them. Past orders are kept. This cannot be undone here.`,
            action: 'Delete',
        },
        tabs: {
            general: 'General',
            images: 'Images',
            stock: 'Stock and Price',
            channels: 'Channels',
            variants: 'Variants',
        },
        basics: 'Basics',
        editBasics: 'Edit Basics',
        editBasicsHint: 'Title, description, category, brand and VAT. The code and the variants have their own tabs.',
        brand: 'Brand',
        noBrand: 'No Brand',
        addBrand: 'New Brand',
        // The description editor's controls. Every one is an accessible name,
        // so each says what it does rather than what it looks like.
        editor: {
            editor: 'Product Description',
            block: 'Text Style',
            paragraph: 'Paragraph',
            heading2: 'Heading',
            heading3: 'Subheading',
            bold: 'Bold',
            italic: 'Italic',
            underline: 'Underline',
            strike: 'Strikethrough',
            bulletList: 'Bulleted List',
            orderedList: 'Numbered List',
            quote: 'Quote',
            link: 'Add Link',
            linkPrompt: 'Address to link to (leave empty to remove the link)',
            linkApply: 'Apply',
            linkCancel: 'Cancel',
            source: 'HTML',
        },
        brandSearch: 'Search brands',
        brandSearchHint:
            'Searches your brands and the brand lists of your connected marketplaces. A marketplace’s brand you choose is added to your company, paired with that marketplace’s.',
        brandOwn: 'Your Brands',
        brandMarketplace: 'Marketplace',
        brandNotFound: 'No brand found. Add a brand that is on no list with New Brand.',
        brandChosen: (name: string) => `Chosen brand: ${name}`,
        clearBrand: 'Remove Brand',
        addBrandNote: 'The brand is added to your company only; nothing is sent to a marketplace.',
        addBrandRequestable: (channels: string) => `${channels}: a brand missing from its list can be requested under Matching, Brand Matching.`,
        addBrandText: (channels: string) => `${channels}: the brand goes out as text with the product and is judged by the marketplace’s own review.`,
        addBrandAsk: (channels: string) => `${channels}: for a brand missing from its list, ask through the marketplace’s seller panel.`,
        addBrandRequestHere: (channels: string) => `${channels}: a brand missing from its list can be requested here once the brand is added.`,
        brandAdded: (name: string) => `${name} was added to your company and chosen as the product’s brand.`,
        requestOffer: (channel: string) => `${channel}: if this brand is missing from its list, you can request it.`,
        requestBrandOn: (channel: string) => `Request Brand on ${channel}`,
        publishDrawer: {
            title: 'Put Back on Sale',
            subtitle: 'The product goes on sale here, and on the channels you tick it is opened again on the channel itself.',
            channels: 'Channels to Reopen',
            noChannels: 'This product is on no channel yet. Publish it from the Channels tab to put it on one.',
        },
        brandName: 'Brand Name',
        noCategory: 'No Category',
        basicsSaved: 'Saved.',
        noDescription: 'No description yet.',
        summary: 'Summary',
        imagesCount: 'Images',
        // Pictures still being fetched from a supplier's list or a marketplace
        // are on their way, not missing (ADR 0007, EPZ-0289).
        imagesCountPending: (stored: number, pending: number) => `${stored} · ${pending} being processed`,
        blockers: 'Still Missing',
        noBlockers: 'Nothing is missing.',
        blocker: {
            draft: 'Draft, Not Activated Yet',
            noImage: 'No Image Uploaded',
            imagesPending: (count: number) => `Images Being Processed (${count})`,
            noDescription: 'No Description Added',
        },
        // The blocker's own verb, as "Add images" is for a missing image: a
        // second "Edit" beside the basics card's would be two buttons with one name.
        addDescription: 'Add a Description',

        form: {
            title: 'New Product',
            subtitle: 'Declare the variant attributes and the combinations are generated for you.',
            sku: 'Product Code',
            skuHint: 'Your own reference. Variant codes are derived from it.',
            name: 'Product Name',
            description: 'Description',
            taxRate: 'VAT Rate',
            category: 'Category',
            axes: 'Variant Attributes',
            axesHint: 'Colour, size and so on; separate the values with commas. Leave empty for a product with no variants.',
            axisName: 'Attribute Name',
            axisValues: 'Attribute Values',
            axisNameHint: 'Colour',
            axisValuesHint: 'Red, Blue, Black',
            addAxis: 'Add Attribute',
            removeAxis: 'Remove',
            preview: 'Variants to Be Created',
            previewCount: (count: number) => (count === 1 ? '1 variant will be created' : `${count} variants will be created`),
            tooMany: 'That is more variant combinations than a product can have.',
            prices: 'Price and Stock',
            pricesHint: 'In Turkish lira. Each sales channel can use this price or have its own; the currency is changed on the Stock and Price tab.',
            variant: 'Variant',
            salePrice: 'Sale Price',
            listPrice: 'List Price',
            openingStock: 'Opening Stock',
            submit: 'Create Product',
        },
    },

    channels: {
        title: 'Sales Channels',
        subtitle: (ownStore: string) => `Marketplaces and ${ownStore}, connected from one place.`,
        connected: 'Connected Channels',
        available: 'Available Channels',
        connect: 'Connect',
        empty: 'No Channel Connected Yet',
        emptyHint: 'Connect one to start selling.',

        status: {
            pending: 'Not Checked',
            connected: 'Connected',
            failed: 'Connection Problem',
            disabled: 'Disabled',
        },

        problem: {
            auth: 'Your store details were not accepted. Check them and try again.',
            credentials_lost: 'The stored connection details can no longer be read. Enter them again on the Settings tab.',
            sandbox_credentials:
                "These details belong to the marketplace's test environment. This connection works on the live one; enter the live API details from the marketplace's seller panel.",
            validation: 'The marketplace did not accept the details sent. Check them and try again.',
            conflict: 'The record changed on the marketplace in the meantime. Refresh the page and try again.',
            not_found: 'The marketplace could not find this record.',
            deprecated: 'The marketplace no longer supports this operation. Please contact support.',
            rate_limited: 'The marketplace received too many requests.',
            transient: 'The marketplace cannot be reached right now.',
            channel_down: 'The marketplace cannot be reached right now.',
            unknown: 'Something unexpected went wrong.',
        },
        problemRetry: {
            automatic: 'It will be retried automatically.',
            manual: 'Try again in a few minutes.',
        },
        events: {
            created: 'Connection added',
            updated: 'Connection details changed',
            verified: 'Connection checked',
            failed: 'Connection check found a problem',
            disabled: 'Connection turned off',
        },

        kind: {
            marketplace: 'Marketplace',
            own_store: 'Your Own Store',
            b2b: 'B2B',
        },

        environment: {
            label: 'Environment',
            sandbox: 'Test',
            production: 'Live',
            sandboxNote: 'Live store details are not accepted here.',
            sandboxForm: (channel: string) =>
                `This installation connects to the ${channel} test environment. Live store details are not accepted here.`,
        },

        feeds: {
            title: 'Update Status',
            orders: 'Orders',
            products: 'Products',
            claims: 'Returns',
            settlements: 'Finance',
        },

        verify: 'Check Connection',
        verifiedAt: (when: string) => `Last checked ${when}`,
        neverVerified: 'Not checked yet',
        sync: {
            now: 'Update Now',
            never: 'Orders not updated yet',
            neverShort: 'Not updated yet',
            lastSynced: (when: string) => `Orders updated ${when}`,
        },
        disconnect: 'Disconnect',
        confirmDisconnect: {
            title: 'The Channel Will Be Disconnected',
            body: 'Order and product updates from this account stop. Listings stay on the channel.',
            action: 'Disconnect',
        },
        active: 'Active',
        passive: 'Passive',
        activate: 'Activate',
        deactivate: 'Deactivate',
        manageShop: 'Shop Settings',
        setUpShop: 'Set Up Shop',
        edit: 'Edit',
        add: 'Add Channel',
        syncAll: 'Update All',
        manage: 'Manage',
        connectedShort: 'Connected',
        searchPlaceholder: 'Search channels',
        summary: (count: number) => (count === 1 ? '1 connected channel' : `${count} connected channels`),
        filters: {
            all: 'All',
            active: 'Active',
            broken: 'Needs Attention',
        },
        columns: {
            channel: 'Channel',
            status: 'Status',
            lastSync: 'Last Update',
        },
        detail: {
            overview: 'Overview',
            settings: 'Settings',
            unavailable: 'Sales Channel Unavailable',
            unknownChannel: "This Connection's Channel Is Missing",
            unknownChannelHint: (key: string) =>
                `The connection points at the "${key}" channel, which this build does not carry. It may have been removed or renamed. Delete the connection and set it up again.`,
            history: 'Connection History',
            noHistory: 'No History Yet',
            webhook: 'Webhook',
        },
        unavailableHere: 'This channel is unavailable in this environment.',
        settings: {
            title: 'Channel Settings',
            hint: 'The operational settings required to publish products on this channel.',
            save: 'Save',
            saved: 'Channel settings saved',
            blocked: {
                pending:
                    'The connection has not been checked yet. These settings come from the channel; confirm it with Check Connection and they appear here once it works.',
                failed: 'These settings cannot be read from the channel while the connection has a problem. Correct the connection details and try Check Connection again; the settings appear here once it works.',
                disabled: 'These settings cannot be read from the channel while the connection is switched off.',
            },
            optionsUnavailable: "The options could not be read from the channel; the value can be typed as it is named in the marketplace's panel.",
            optionsEmpty:
                "The channel holds no option for this setting yet. Create one in the marketplace's panel to choose it here; until then its name can be typed.",
        },
        carrier: {
            pick: 'Choose…',
            none: 'Not chosen',
            unavailable: "The cargo company list could not be read from the channel; the company's code can be typed instead.",
            manualHint: 'The code the marketplace gives the cargo company.',
        },

        webhook: {
            title: 'Inbound Notifications',
            description:
                'The marketplace notifies this address of order changes. Give the address to the channel; when a notification arrives, the affected orders are updated at once.',
            urlLabel: 'Notification Address',
            copy: 'Copy',
            copied: 'Copied',
            register: 'Register With Channel',
            reRegister: 'Re-register',
            unregister: 'Remove',
            registered: (at: string) => `Registered · ${at}`,
            notRegistered: 'Not Registered Yet',
            recent: 'Recent Deliveries',
            noEvents: 'No Deliveries Yet',
            status: {
                processed: 'Processed',
                ignored: 'Ignored',
                failed: 'Failed',
                received: 'Received',
            },
        },

        form: {
            title: (channel: string) => `Connect ${channel}`,
            editTitle: (channel: string) => `Edit ${channel} Connection`,
            name: 'Connection Name',
            nameHint: 'Your own label for telling this sales channel apart in the panel. It is not sent to the channel.',
            secretStored: 'Stored. Not shown, for your security.',
            secretMissing: 'Not entered yet.',
            replaceSecrets: 'Change Secret Details',
            replacingSecrets: 'Enter the new values; saving replaces the stored ones.',
            credentialsLost: 'The saved connection details can no longer be read. Enter all of them again to reconnect.',
            submit: 'Save and Connect',
            saveChanges: 'Save Changes',
        },
    },

    listings: {
        tabPending: (count: number) => `${count} to Send`,
        // Where a channel's price comes from (ADR 0014).
        priceSource: {
            legend: 'Price',
            product: 'Use Product Price',
            productHint: 'Moves with the product price.',
            own: 'Channel Price',
            ownHint: 'Set for this channel alone.',
            noProductPrice: 'This product has no price of its own yet.',
            currencyRefused: (currency: string) => `This channel does not sell in ${currency}, the product price's currency.`,
            openPrices: 'Open Stock and Price',
        },
        approve: {
            title: 'Waiting to Be Sent to This Channel',
            description:
                'Stock and price changes to products the channel already holds go out on their own. Putting a product on the channel for the first time happens here, because it is a decision rather than an update.',
            creating: (count: number) => `${count} product(s) would go on sale on this channel for the first time.`,
            updating: (count: number) => `${count} listing(s) have a price, stock or content change waiting.`,
            confirmTitle: 'Send to the Channel',
            confirmCreating: (creating: number, updating: number) =>
                `${creating} product(s) will go on sale on this channel for the first time, and ${updating} existing listing(s) will be updated.`,
            confirmUpdating: (updating: number) => `${updating} existing listing(s) will be updated.`,
            waitingProduct: 'Product',
            waitingChange: 'Waiting to Be Sent',
            waitingChangedAt: 'Changed',
            waitingNew: 'Not on the Channel Yet',
            catalogueKept:
                "The parts marked with a lock come from this channel's own catalogue: publishing does not change them on products the channel already holds. Ask for the change in the channel's seller panel.",
            contentPartKept: (part: string) => `${part}, kept by the channel's catalogue`,
            waitingMore: (count: number) => `And ${count} more product(s) with changes waiting.`,
        },
        stockPolicy: {
            title: 'Stock Shown to This Channel',
            description:
                'A marketplace learns a new figure when we next push it, and another order can arrive in that gap. Withholding a little trades unsold stock for not overselling.',
            none: 'The whole available quantity is sent.',
            current: (buffer: number, cap: number | null) => (cap != null ? `${buffer} withheld, at most ${cap} sent.` : `${buffer} withheld.`),
            buffer: 'Withhold',
            bufferHint: 'Kept back from this channel, on top of the product\u2019s own buffer.',
            cap: 'Most to Send',
            capHint: 'For the marketplaces that penalise a listing running out. Leave empty for no ceiling.',
        },

        // What our own code decided about a listing or a publish, stored by its
        // code with the specifics the sentence quotes (ADR 0005 §6, docs/15
        // §6). A channel's own refusal carries no code and keeps its words.
        failure: {
            'listing.connection_unavailable': 'The channel connection cannot be used.',
            'listing.listing_write_unimplemented': 'Sending prices and stock to this channel is not built yet.',
            'listing.product_write_unimplemented': 'Sending products to this channel is not built yet.',
            'listing.facet_unsupported': 'The channel cannot update this field.',
            'listing.content_update_unsupported': 'This channel cannot update the content of a product already on it.',
            'listing.nothing_sendable': 'None of the products can be sent as they are.',
            'listing.result_mismatch': "The marketplace's answer did not match the products sent. Check the product and publish it again.",
            'listing.result_expired': 'The channel stopped keeping the result before it was read, so the outcome is unknown.',
            'listing.result_unreadable':
                "The channel's result could not be read, so the outcome is unknown; anything still different will be sent again.",
            'listing.batch_refused': 'The channel refused the batch.',
            'listing.create_unconfirmed':
                'The channel did not confirm whether it created the product, so it was not sent again. Check the product on the channel: match it under Matching if it is there, publish again if it is not.',
            'listing.sku_missing': 'This channel recognises a product by its stock code, and this product has none.',
            'listing.not_yet_published': 'This product is not on the channel yet, so it cannot be updated. Publish it first.',
            'listing.variant_missing': "This product's variant could not be found.",
            'listing.barcode_missing': 'This channel recognises a product by its barcode, and this product has none.',
            'publish.setting_missing': (setting: string) => `A channel setting is missing: ${setting}`,
            'publish.product_not_found': 'The product could not be found in the catalogue.',
            'publish.variant_not_found': 'The variant could not be found in the catalogue.',
            'publish.barcode_missing': (sku: string) => `No barcode entered: ${sku}`,
            'publish.sku_too_short': (least: string, sku: string) => `A stock code must be at least ${least} characters: ${sku}`,
            'publish.price_missing': (sku: string) => `No sale price entered: ${sku}`,
            'publish.category_missing': 'The product has no category.',
            'publish.category_unmapped': "The product's category is not matched to this channel.",
            'publish.attributes_missing': (names: string) => `Required channel attributes are missing: ${names}`,
            'publish.image_missing': 'The product has no image.',
            'publish.brand_missing': 'No brand is chosen.',
            'publish.brand_not_in_catalogue': 'The brand could not be found in the catalogue.',
            'publish.brand_not_mapped': (brand: string) =>
                `The brand ${brand} is not matched to one of this channel's brands. Match it under Matching, Brand Matching, or choose this product's own brand for the channel.`,
            'publish.brand_retired': (brand: string) =>
                `The channel no longer lists the brand ${brand}. Choose another under Matching, Brand Matching, or this product's own brand for the channel.`,
            'publish.brand_not_in_category': (brand: string) =>
                `This category takes only the brands in its own list, and ${brand} is not one of them. Choose one of its brands for this product on the channel.`,
        },

        batches: {
            title: 'Recent Submissions',
            description: 'What was actually sent to this channel, and what it made of it.',
            empty: 'Nothing Has Been Sent to This Channel Yet',
            what: 'Operation',
            items: 'Products',
            when: 'Finished',
            counts: (succeeded: number, failed: number) => `${succeeded} sent, ${failed} refused`,
            // What a submission did, in a seller's words rather than the
            // pipeline's. The facet names alone ("content", "deletion") named
            // the mechanism and told the seller nothing.
            operation: {
                update: (what: string) => `${what} Update`,
                content: 'Product Content',
                withdrawal: 'Withdrawal from Sale',
                reinstatement: 'Back on Sale',
                deletion: 'Removal from the Channel',
            },
            // The channel's own words for a refusal, so the seller can act on
            // it without opening the marketplace's panel.
            error: 'Why It Was Refused',
            state: {
                queued: 'Queued',
                pending: 'Sent, Waiting',
                succeeded: 'Accepted',
                partial: 'Partly Accepted',
                failed: 'Refused',
                expired: 'Outcome Unknown',
            },
        },

        listPrice: 'List Price',
        brand: {
            title: 'This Channel’s Brand',
            scope: {
                product: 'This Product’s Own',
                mapping: 'Brand Matching',
                name: 'The Brand’s Name',
            },
            line: (brand: string, scope: string) => `${brand} · ${scope}`,
            pick: 'Choose This Product’s Own Brand',
            change: 'Change',
            clear: 'Use the Brand Matching',
            drawerTitle: 'This Product’s Own Brand',
            drawerHint: (channel: string) =>
                `This product goes out on ${channel} under this brand, whatever its own brand and the brand matching say. Other channels are not affected.`,
            listedLocked: (channel: string) =>
                `${channel} does not let the integration change the brand of a product it already lists. The brand chosen here is not sent there. Ask for the correction in ${channel}'s seller panel.`,
            listedNote: (channel: string) =>
                `This product is already on ${channel}. A changed brand goes out with the next publish, and ${channel} may hold it for its own review; if the change does not show, ask ${channel}'s seller support.`,
            openMapping: 'Go to Brand Matching',
        },
        requirements: {
            title: 'What Publishing Needs',
            intro: (channel: string) => `${channel} refuses a product without these.`,
            ok: 'Done',
            missing: 'Missing',
            category: 'Marketplace Category',
            attributes: 'Required Category Attributes',
            attributesMissing: (names: string) => `Missing: ${names}.`,
            attributesDone: (count: number) => `All ${count} answered.`,
            attributesNone: 'This category requires none.',
            images: 'Images',
            imagesMissing: 'The product has no image.',
            imagesDone: (count: number) => `${count} image(s).`,
            brand: 'Brand',
            barcode: 'Barcode',
            barcodeMissing: (count: number) => `${count} variant(s) have no barcode. Type them into the boxes outlined in red below and save.`,
            barcodeDone: 'Every variant has one.',
            code: (least: number) => `Stock Code, At Least ${least} Characters`,
            codeShort: (codes: string) => `Too short: ${codes}. Change it on the Variants tab.`,
            codeDone: 'Every variant’s code is long enough.',
            openImages: 'Go to Images',
            openGeneral: 'Go to General',
            openVariants: 'Go to Variants',
            settingUnset: (channel: string) => `Not chosen; ${channel} uses your account's default.`,
            settingMissing: 'Not chosen; products are not sent without it.',
            settingUnknown: (value: string) => `${value} is not on the channel's list; products are refused with it.`,
            openSettings: 'Go to Channel Settings',
        },
        content: {
            title: 'Channel Content',
            hint: "The text shown on this channel alone, instead of the catalogue's title and description. It changes no other channel and not the product itself; a field left empty uses the catalogue's text.",
            reachOwnStore: 'What you save shows in your shop at once.',
            reachMarketplace: (channel: string) =>
                `${channel} receives this text when you first publish the product; later changes are sent when you press Publish on the card.`,
            titleLocked: (channel: string) =>
                `${channel} does not let an integration change the title of a product it lists. The title written here is not sent there; the description is.`,
            keptByChannel: (channel: string) =>
                `${channel} does not let an integration change the title, description or pictures of a product it lists. Ask for the change in the channel’s seller panel.`,
            needsPrice: 'Enter a price for this channel before saving its content.',
            edit: 'Edit Content',
            overridden: 'Using channel-specific content',
            fromCatalogue: 'Using the catalogue text',
            titleField: 'Channel Title',
            titleHint: 'Leave empty to use the product title.',
            descriptionField: 'Channel Description',
            currency: 'Currency',
            currencyHint: 'The currency this offer is priced in.',
            deliveryDays: 'Days to Dispatch',
            deliveryDaysHintOwnStore:
                'How many working days this product takes to reach the courier; 0 means the same day. Leave empty to use the dispatch time in your shop settings.',
            deliveryDaysHintMarketplace:
                'How many days this product takes to reach the courier on this channel; 0 means the same day. Leave empty and no time is sent: the channel keeps the one it holds.',
            deliveryDaysMax: (days: number) => `This channel keeps at most ${days} days; a longer time is not saved.`,
            reset: 'Use the Catalogue Text',
        },
        title: 'Sales Channels',
        subtitle: 'What each channel should show for this product.',
        channelImage: 'The Picture This Channel Shows',
        channelImageHint: 'This marketplace uses its own catalogue picture for the product; it stays out of your gallery.',
        noChannels: 'Connect a Sales Channel to Start Publishing This Product',
        channel: 'Channel',
        price: 'Price',
        stock: 'Stock',
        stockFromShelf:
            "This channel sells what your shelf holds; its stock follows the shelf. Change it on the product's Stok ve Fiyat tab or on the Stok page. To show this channel less, use its Stock Shown to This Channel setting.",
        status: 'Status',
        pending: 'Waiting to be sent',
        nothingPending: 'Up to date',
        notOnChannel: 'Not on this channel yet',
        save: 'Save',
        publish: 'Publish',
        // Automatic dispatch carries price and stock; a first listing and content
        // wait for a person (ADR 0006 §2), so the card says which is which.
        flowHint:
            'Saved prices and stock reach the channel on their own. Press Publish to put the product on the channel for the first time or to send content changes.',
        // A channel that renders the catalogue shows a content edit once it is saved.
        flowHintFromCatalogue:
            'Saved prices and stock reach the channel on their own, and changes to the product’s details show on this channel as soon as you save them. Press Publish to put the product on the channel for the first time.',
        nothingToPublish: 'Nothing waiting',
        publishQueued: (channel: string) => `Queued for ${channel}. The result shows on this card within a few minutes; there is no need to reload.`,
        publishNothing: 'Nothing to send right now: the previous submission is still going, or the changes have already been sent.',
        // Taking a product off sale on one channel and putting it back (EPZ-0147).
        // The channel confirms later, so the card says what was asked and where
        // the answer will show.
        withdraw: 'Take Off Sale',
        reinstate: 'Put Back on Sale',
        confirmWithdraw: {
            title: 'The Product Will Be Taken Off Sale',
            body: (channel: string) =>
                `It is taken off sale on ${channel} only and keeps selling on your other channels. Until you put it back on sale, no stock or price is sent to ${channel}; the latest figures wait and go out when you do.`,
            action: 'Take Off Sale',
        },
        withdrawQueued: (channel: string) =>
            `Taking it off sale on ${channel} is queued. The result shows on this card within a few minutes; there is no need to reload.`,
        withdrawNothing: 'Nothing to take off sale: it is already off sale on this channel, or was never published there.',
        reinstateQueued: (channel: string) =>
            `Putting it back on sale on ${channel} is queued. The result shows on this card within a few minutes; there is no need to reload.`,
        withdrawn: 'Off Sale',
        // A product not Active in the catalogue sells nowhere, whatever its
        // offer here says (EPZ-0305).
        notSelling: 'Not Selling',
        productNotActive: (status: string) =>
            `This product is ${status}: your shop does not show it until it is made Active. Use Activate at the top of the page.`,
        productNotActiveChannel: (status: string) =>
            `This product is ${status}: it is not put on this channel until it is made Active. An offer already on sale here keeps receiving its stock and price; take it off sale with Take Off Sale.`,
        withdrawnNotice: (channel: string) =>
            `This product is off sale on ${channel}. No stock or price is sent there until you press Put Back on Sale, and then the current figures go out.`,
        // The seller's own answers to a channel: a match it holds the product
        // for, and a price lock it put on a listing (EPZ-0173). Both are sent
        // at once and drawn only where the channel declares them.
        awaitingMatch: 'Awaiting Your Match Decision',
        matchHint: (channel: string) =>
            `${channel} matched this product to one in its catalogue and is waiting for your decision. Approve the match if it is the same product; reject it if not.`,
        approveMatch: 'Approve Match',
        rejectMatch: 'Reject Match',
        confirmApproveMatch: {
            title: 'The Match Will Be Approved',
            body: (channel: string) =>
                `${channel} is told this product is the one it matched it to in its catalogue. The decision is sent to ${channel} at once.`,
            action: 'Approve Match',
        },
        confirmRejectMatch: {
            title: 'The Match Will Be Rejected',
            body: (channel: string) =>
                `${channel} is told this product is not the one it matched it to. The decision is sent to ${channel} at once; what ${channel} does next shows on this card.`,
            action: 'Reject Match',
        },
        matchDecided: (channel: string) =>
            `Your decision reached ${channel}. The product shows its new state on this card once ${channel} finishes its review.`,
        priceLocked: 'Price Locked',
        priceLockedHint: (channel: string) =>
            `${channel} locked this product at its previous price because the new one is outside the range it allows. Enter a price within the range and publish, or release the lock.`,
        releasePriceLock: 'Release Price Lock',
        confirmReleasePriceLock: {
            title: 'The Price Lock Will Be Released',
            body: (channel: string) =>
                `The lock is released on ${channel} at once. The price you entered keeps waiting to be sent; published unchanged, ${channel} may lock the product again.`,
            action: 'Release Lock',
        },
        priceLockReleased: (channel: string) =>
            `The price lock was released on ${channel}. The product's state shows here once ${channel} next reports it.`,
        queued: (count: number) => (count === 1 ? '1 submission queued' : `${count} submissions queued`),

        facet: {
            price: 'Price',
            stock: 'Stock',
            delivery_time: 'Delivery Time',
            status: 'Status',
            content: 'Content',
        },
        // What changed of a product's content, as a channel's waiting list names it.
        contentPart: {
            title: 'Title',
            description: 'Description',
            category: 'Category',
            brand: 'Brand',
            vat: 'VAT',
            variants: 'Variants',
            images: 'Images',
            attributes: 'Channel Attributes',
            channel_category: 'Marketplace Category',
            channel_content: 'Channel Content',
        },
        contentPartLink: (part: string, product: string) => `${product}: open the ${part} change`,

        attributes: {
            title: 'Channel Attributes',
            edit: 'Edit Attributes',
            hint: "The marketplace asks for its own category's required fields before it accepts a product.",
            notMapped: "This product's category is not mapped to a category on this channel yet.",
            noCategory: 'The product has no category. Pick one under General, with Edit.',
            mapLink: 'Go to Category Mapping',
            channelCategory: 'Marketplace Category',
            required: 'Required',
            missing: (count: number) => (count === 1 ? '1 Required Attribute Missing' : `${count} Required Attributes Missing`),
            ready: 'Every Required Attribute Is Filled',
            pick: 'Choose…',
            customPlaceholder: 'Free Text',
            save: 'Save Attributes',
            saved: 'Attributes saved',
            none: 'The channel asks nothing extra for this category.',
            noneRequired: 'The marketplace requires none of these for this category; they are optional.',
        },

        // Where the listing stands among the marketplace's offers for the
        // product, on a channel that declares product.buybox (EPZ-0171, EPZ-0223).
        buyBox: {
            column: 'Buy Box Rank',
            rank: (rank: number, offers: number) => `${rank} / ${offers}`,
            notRanked: 'Not Ranked',
            notRead: 'Not Read Yet',
            history: {
                title: 'Competitor Prices',
                hint: 'Each change in where you stand among the offers on this marketplace. Read every hour and kept for 90 days.',
                variant: 'Variant',
                date: 'Date',
                rank: 'Your Rank',
                ownPrice: 'Your Price',
                winnerPrice: 'Winning Price',
                lowestRival: 'Lowest Competitor Price',
                offers: 'Offers',
                empty: 'Not Read Yet',
                emptyHint: 'The first reading appears here within an hour of the product going live.',
                more: 'Show More',
            },
        },

        state: {
            draft: 'Not Sent',
            pending: 'Sending',
            in_review: 'In Review',
            active: 'Live',
            passive: 'Paused',
            rejected: 'Rejected',
            error: 'Failed',
        },
    },

    orders: {
        channelFilter: 'Channel',
        allChannels: 'All Channels',
        fromDate: 'From',
        toDate: 'To',
        clearFilters: 'Clear Filters',
        title: 'Orders',
        subtitle: 'Every channel in one list.',
        search: 'Search by order number, name or phone',
        empty: 'No Orders Yet',
        emptyHint: 'Orders arrive here as soon as a channel reports one.',
        noChannelHint: 'Connect a sales channel to start receiving orders.',
        count: (shown: number, total: number) => `Showing ${shown} of ${total}`,
        // Which step an order waiting on the seller is waiting for
        // (EPZ-0061): short under the badge in a narrow column, and as a
        // sentence on the order's own page, where there is room to say it.
        actionReason: {
            payment: 'Awaiting transfer',
            pack: 'Needs packing',
            ship: 'Needs dispatch',
            invoice: 'Needs an invoice',
        },
        actionReasonLong: {
            payment: 'Action Required: awaiting a bank transfer. Do not ship the order before the payment reaches your account.',
            pack: 'Action required: the order needs to be packed.',
            ship: 'Action required: the package needs to be dispatched.',
            invoice: 'Action required: the order needs an invoice.',
        },
        // The button opens the tab the step is performed on, named for the
        // step rather than for the tab: a button called "Kargo" beside a tab
        // called "Kargo" says nothing about what pressing it does.
        actionReasonCta: {
            payment: 'Payment Received',
            pack: 'Pack the Order',
            ship: 'Dispatch the Package',
            invoice: 'Issue the Invoice',
        },
        // A request the seller has not answered, beside the status and not
        // instead of it: the goods are still with the buyer (EPZ-0120).
        claimState: {
            cancellation_requested: 'Cancellation Requested',
            return_requested: 'Return in Progress',
        },
        unmatchedShort: 'Unmatched',
        unmatchedWarning: (count: number) =>
            count === 1 ? '1 sold item could not be matched to a product.' : `${count} sold items could not be matched to a product.`,
        showUnmatched: 'Show Them',
        clearUnmatched: 'Show All Orders',

        columns: {
            number: 'Order',
            channel: 'Channel',
            customer: 'Customer',
            total: 'Total',
            status: 'Status',
            invoice: 'Invoice',
            date: 'Date',
        },

        /** The list's own filter: which orders still owe a document. */
        invoiceFilter: {
            all: 'Any Invoice',
            invoiced: 'Invoiced',
            notInvoiced: 'Not Invoiced',
        },

        /** What an order's invoice has got to, for the list's own column. */
        invoiceState: {
            issued: 'Issued',
            none: 'Not Issued',
            failed: 'Failed',
            recorded: 'Not Filed',
            cancelled: 'Cancelled',
            pending: 'Issuing',
        },

        status: {
            all: 'All Statuses',
            pending: 'Waiting',
            action_required: 'Needs Action',
            shipped: 'Shipped',
            delivered: 'Delivered',
            cancelled: 'Cancelled',
            returned: 'Returned',
            unknown: 'Unknown',
        },

        source: {
            channel: 'Channel',
            seller: 'You',
            customer: 'Customer',
            system: 'System',
        },

        event: {
            received: 'Order received',
            updated: 'Order updated',
            rematched: 'Products rematched',
            channel_cancel: 'Cancellation sent to channel',
            channel_split: 'Package split on channel',
            channel_status: 'Channel status updated',
            channel_carrier: 'Carrier changed',
            channel_tracking: 'Tracking number sent to channel',
            channel_invoice_link: 'Invoice link sent to channel',
            channel_action_failed: 'Channel action failed',
            channel_action_duplicate: 'Repeated action not sent to channel',
            channel_action: 'Action sent to channel',
            shipped: 'Handed to the carrier',
            claim_return: 'Return opened',
            claim_cancellation: 'Cancellation request opened',
            claim_missing: 'Reported as not delivered',
            claim_damaged: 'Reported as arrived damaged',
            claim_exchange: 'Exchange request opened',
            claim_other: 'Request opened',
            payment_received: 'Transfer received',
            payment_refunded: 'Payment refunded to the card',
            payment_refund_manual: 'Refund to be made in the payment service’s own screen',
            payment_refund_failed: 'The payment service refused the refund',
        },

        channelActions: {
            sent: 'Sent to the channel. The outcome shows here within a few minutes; there is no need to refresh.',
            done: 'The channel accepted the action.',
            duplicate:
                'The same action had already been sent to the channel, so it was not sent again. The order is being read from the channel again; to repeat the action on purpose, send it again in 10 minutes.',
            lastFailed: (when: string, reason: string) => `The last channel action failed (${when}): ${reason}`,
            more: 'More Actions',
            openStatus: 'Update Channel Status',
            statusButton: 'Send Status',
            timeOfDay: (label: string) => `${label}, Time of Day`,
            timePlaceholder: 'HH:MM',
            statusLabel: 'New Status',
            unknownStatus: 'Channel Status',
            statuses: {
                Picking: 'Approved',
                Invoiced: 'Invoiced',
                Packaged: 'Packaged',
                ReadyToShip: 'Ready to Ship',
                Shipped: 'Shipped',
                Delivered: 'Delivered',
                DeliveredWithoutShipment: 'Delivered (No Shipment)',
                Cancelled: 'Cancelled',
                UnSupplied: 'Not Supplied',
                Unpacked: 'Unpack',
                InTransit: 'In Transit',
                Undelivered: 'Undelivered',
                OutForDelivery: 'Out for Delivery',
                ReturnedToSeller: 'Returned to Seller',
                // The shop's own words for its statuses (ADR 0003 §7).
                confirmed: 'Confirmed',
                packed: 'Packed',
                shipped: 'Shipped',
                delivered: 'Delivered',
                cancelled: 'Cancelled',
            },
            // Only the statuses whose name cannot carry their meaning. A
            // seller reading "Unpack" in a list of statuses has no way to know
            // it undoes the packaging at the marketplace (EPZ-0057).
            statusHints: {
                Unpacked: 'The package is broken up at the marketplace and its products return to the lines waiting to be packed.',
                DeliveredWithoutShipment:
                    'Only for orders of products the marketplace holds as not shipped, such as digital products; the marketplace refuses it for a shipped order.',
            },
            title: 'Channel Actions',
            hint: 'Actions taken on the order’s sales channel; the outcome lands on the order history.',
            openCancel: 'Cancel at Channel',
            openSplit: 'Split Package',
            openCarrier: 'Change Carrier',
            package: 'Package',
            pickReason: 'Select a Reason',
            penaltyTable: 'Cancellation Fees',
            penaltyValue: 'Value of the Goods',
            penaltyFee: 'Fee',
            penaltyUpTo: (from: string, to: string) => `${from} – ${to}`,
            penaltyAbove: (from: string) => `${from} and above`,
            penaltyAccepted: 'I have read and accept the cancellation fee terms.',
            pickCarrier: 'Choose a Carrier',
            cancelQuantity: 'Amount to Cancel',
            statusLines: 'Items to Advance',
            statusLinesHint: 'Leave empty to advance the whole package.',
            statusPackages: 'Packages',
            cancelLines: 'Lines to Cancel',
            reason: 'Reason',
            reasonHint: 'Optional',
            reasonCodeHint:
                "This marketplace requires a numeric reason code. The named reasons its own seller panel shows may not match this code one for one; if you don't know it, ask the marketplace's integration support.",
            cancelButton: 'Cancel at Channel',
            confirmCancel: {
                title: 'Lines Will Be Cancelled at the Channel',
                body: 'The selected lines are cancelled on the sales channel.',
                action: 'Cancel at Channel',
            },
            carrier: 'Carrier',
            carrierHint: "The channel's carrier code",
            carrierButton: 'Change Carrier',
            carrierQueued: 'The carrier change was sent to the channel. The outcome lands on the History tab.',
            splitLines: 'Lines to Move Into a New Package',
            splitQuantityHint: 'Quantity to move to the new package. The remainder stays in the other package.',
            splitHint: 'The ticked lines become a new package at the channel; the rest stay in the current one.',
            splitButton: 'Split Package',
            confirmSplit: {
                title: 'Lines Will Move to a Separate Package',
                body: 'The selected lines become a new package on the marketplace; each ships on its own.',
                action: 'Split Package',
            },
        },

        openLineActions: {
            title: 'Open Lines',
            hint: "Lines the channel has not packaged yet, performed directly at the marketplace; the outcome lands on the order's history.",
            openPack: 'Create Package',
            openCancel: 'Cancel Line',
            openCarrier: 'Change Carrier',
            packHint: 'Only lines the channel reports eligible are listed. Quantities left at 0 stay open.',
            packLines: 'Lines',
            packButton: 'Create Package',
            confirmPack: {
                title: 'A Package Will Be Created at the Channel',
                body: 'The selected lines and quantities become a new package on the marketplace.',
                action: 'Create Package',
            },
            cancelButton: 'Cancel at Channel',
            confirmCancel: {
                title: 'Lines Will Be Cancelled at the Channel',
                body: 'The selected lines are cancelled on the marketplace before any package exists for them.',
                action: 'Cancel at Channel',
            },
            carrierHint: 'Only carriers the channel currently accepts for this line are listed.',
            noCarriers: 'No carrier available for this line right now.',
        },

        detail: {
            tabs: {
                summary: 'Summary',
                shipping: 'Shipping',
                claims: 'Claims',
                invoice: 'Invoice',
                timeline: 'History',
            },
            shipTo: 'Delivery',
            billTo: 'Billing',
            noBilling: 'The channel sent no billing details.',
            billSameAsShip: 'The billing address is the same as the delivery address.',
            // What the order's invoice will name the buyer by, masked: the
            // customer's page shows the numbers whole (docs/07 §3).
            taxIdentity: {
                title: 'Tax Details',
                taxOffice: 'Tax Office',
                taxNumber: 'Tax Number',
                identityNumber: 'National ID Number',
                none: 'No tax number or national ID number is recorded for this buyer.',
                masked: 'The full numbers are on the customer record.',
            },
            cancellation: {
                title: 'Cancellation',
                by: 'Cancelled By',
                reason: 'Reason',
                at: 'Cancelled On',
                line: 'Cancelled',
                parties: { customer: 'Customer', seller: 'You', channel: 'Marketplace' },
            },
            // Units of a line an approved return has taken back (EPZ-0121).
            returnedQuantity: (count: number) => `${count} returned`,
            // What a shop's buyer chose at checkout (ADR 0003 §7): a
            // marketplace order has none of it.
            purchase: {
                title: 'Payment',
                method: 'Method',
                status: 'Status',
                coupon: 'Discount Code',
                note: 'Customer Note',
                email: 'Buyer’s E-mail',
                methods: { cod: 'Cash on Delivery', bank_transfer: 'Bank Transfer', card: 'Card Payment' },
                statuses: {
                    on_delivery: 'Paid at the door',
                    awaiting_transfer: 'Awaiting transfer',
                    paid: 'Paid',
                    refunded: 'Refunded',
                    partially_refunded: 'Partly refunded',
                },
            },
            // The columns an order's product table can be asked for
            // (EPZ-0064). They start hidden: the table a seller meets is the
            // one the job needs, and these are what an operation reaches for
            // now and then.
            columns: 'Columns',
            image: 'Image',
            barcode: 'Barcode',
            package: 'Package',
            noPackage: 'Not packed',
            // The four things that happen to an order, at the top of its
            // summary, so whether it is packed, gone and invoiced is one
            // glance rather than two tabs (EPZ-0063).
            process: {
                title: 'Process',
                order: 'Order',
                packages: 'Packages',
                shipping: 'Shipping',
                invoice: 'Invoice',
                noPackages: 'Not created',
                packageCount: (count: number) => `${count} package(s)`,
            },
            lines: 'Items',
            quantity: 'Qty',
            unitPrice: 'Unit Price',
            parcels: 'Parcels',
            noAmount: 'No amount came from the channel',
            placedOn: (when: string) => `on ${when}`,
            carrier: 'Carrier',
            tracking: 'Tracking',
            channelLabel: 'Label',
            printChannelLabel: 'Print Label',
            printSlip: 'Package Slip',
            slipTitle: 'Print a Package Slip',
            slipHint:
                'Not a shipping label. It books nothing and no courier is told anything; it is a barcode sheet for matching a parcel to its order.',
            slipProducts: 'List What Is in the Parcel',
            slipPrices: 'Show Amounts',
            slipPricesHint: 'The slip is visible for the whole journey.',
            timeline: 'Timeline',
            unmatchedNotice: (count: number) =>
                count === 1
                    ? 'One item on this order matched no product, so it has not affected stock and cannot be invoiced.'
                    : `${count} items on this order matched no product, so they have not affected stock and cannot be invoiced.`,
            rematch: 'Try Matching Again',
            transferAwaited: 'The bank transfer is still awaited. Once it reaches your account, record it with Payment Received.',
            // A request waiting for the seller, above everything else the order
            // asks for (EPZ-0127): worded by kind, since only a cancellation holds
            // the parcel.
            pendingClaim: {
                cancellation_requested: 'A cancellation request is waiting for your answer. Do not ship the order until you have decided.',
                return_requested: 'A return request is waiting for your answer.',
            },
            answerClaim: 'Answer the Request',
            rematched: (count: number) => (count === 0 ? 'Still nothing matched.' : `${count} resolved.`),
        },
    },

    stock: {
        title: 'Stock',
        subtitle: 'One quantity per product, sent to every channel.',
        // The product's own tab, where the product price sits beside the count.
        withPrice: {
            title: 'Stock and Price',
            subtitle: 'The product price and one quantity, each sent to every channel that uses it.',
            currency: 'Price Currency',
            currencyHint: 'The currency of every variant of this product.',
            hint: 'Type the price and the shelf count; changed rows are marked and saved together.',
            // Under a count box whose variant also has stock in a warehouse other than the one shown.
            elsewhere: (count: number) => `Stock in this warehouse; ${count} more in other warehouses.`,
            warehouse: 'Warehouse',
            warehouseHint: "On Hand and Held Back are this warehouse's figures; a count you type is saved to it.",
        },
        // Moving part of one variant's stock between two warehouses.
        move: {
            action: 'Move',
            actionLabel: (sku: string) => `Move ${sku} Stock`,
            title: (sku: string) => `Move ${sku} Stock`,
            hint: 'The quantity you choose moves from one warehouse to another. Units reserved for orders stay. Each move is written to the stock history as a transfer.',
            from: 'From Warehouse',
            to: 'To Warehouse',
            free: (count: number) => `${count} free to move.`,
            submit: 'Move the Stock',
        },
        onHand: 'On Hand',
        buffer: 'Held Back',
        available: 'Offered to Channels',
        save: 'Save',
        empty: 'No Stock Recorded for This Product Yet',
        low: 'Running Out',
        lowEmpty: 'Nothing Is Running Low',
        count: (shown: number, total: number) => `Showing ${shown} of ${total}`,
        bulkSet: 'Set to',
        bulkIncrease: 'Increase by',
        bulkDecrease: 'Decrease by',
        quantity: 'Quantity',
        apply: 'Apply',
        bulkApplied: (n: number) => `${n} updated`,
        bulkFailed: (n: number) => `${n} could not be updated`,
        movements: 'Stock History',
        movementsOf: (sku: string) => `Stock History · ${sku}`,
        saveChanges: (n: number) => (n === 0 ? 'Save Changes' : `Save Changes (${n})`),
        changedHint: 'Type the shelf count; changed rows are marked and saved together.',
        search: 'Search by product name or code',
        product: 'Product',
        code: 'Code',
        noMatch: 'No Running-Out Product Matches',
        movementsEmpty: 'No Movements Yet',
        balance: 'Left',

        // A shelf opened from a marketplace's figure, before anybody counted it.
        openedFromChannel: 'Opened From the Marketplace',
        reason: {
            manual: 'Manual Correction',
            order: 'Sale',
            cancellation: 'Cancellation',
            return: 'Return',
            stocktake: 'Stocktake',
            damage: 'Written off',
            transfer: 'Transfer',
        },
    },

    images: {
        title: 'Images',
        subtitle: 'The first one is what a marketplace shows in search results.',
        add: 'Add Image',
        empty: 'No Images Yet',
        emptyHint: 'A product without one rarely sells.',
        remove: 'Remove',
        makeFirst: 'Make First',
        first: 'First',
        alt: 'Description',
        uploading: 'Uploading…',
        // A supplier's list brings pictures as a marketplace does, so these name
        // no source (ADR 0007). Their counts arrive formatted (docs/15 §3).
        pending: (count: string) => (count === '1' ? '1 image is being brought in' : `${count} images are being brought in`),
        failed: (count: string) => (count === '1' ? '1 image could not be fetched' : `${count} images could not be fetched`),
        emptyPending: 'Images Are Being Processed',
        emptyPendingHint: 'They will appear here within minutes.',
        // Why an image address was given up on, stored by its code
        // (media.FailureCode, ADR 0005 §6). One given up on before the codes has
        // none and reads as not fetched.
        failure: {
            'media.import_unreachable': 'The address did not answer, or did not answer with an image.',
            'media.import_not_served': 'The site refused to give this image: the address is wrong, removed or closed to us.',
            'media.import_refused': 'Images are not fetched from this address. It has to be a public address starting with https.',
            'media.import_not_an_image': 'The file at the address is not an image.',
            'media.import_too_small': 'The image is too small: its shorter side has to be at least 300 pixels.',
            'media.import_too_large': 'The image file is larger than 12 MB.',
            'media.import_gallery_full': "The product's gallery is full: a product carries at most 12 images.",
            'media.import_not_stored': 'The image was fetched but could not be stored. Try again.',
        },
        failureUnknown: 'The image could not be fetched.',
    },

    dashboard: {
        byChannel: {
            title: 'By Channel',
            orders: (count: number) => `${count} orders`,
            unknown: 'Disconnected Channel',
        },
        title: 'Overview',
        metrics: {
            todayOrders: "Today's Orders",
            todayRevenue: "Today's Revenue",
            returnedOrders: 'Returned, 30 Days',
            returnedRevenue: 'Returned Value, 30 Days',
            periodOrders: 'Orders, 30 Days',
            periodRevenue: 'Revenue, 30 Days',
        },
        work: {
            title: 'Waiting for You',
            none: 'Nothing Waiting',
            noneHint: 'Everything is up to date.',
            actionRequired: 'Orders Needing Action',
            unmatched: 'Sold Items With No Product',
            pending: 'Changes Waiting to Be Sent',
            failed: 'Listings a Channel Rejected',
            failedInvoices: 'Invoices That Were Rejected',
            openClaims: 'Returns Waiting for an Answer',
            failingChannels: 'Connections Not Working',
        },
        lowStock: {
            title: 'Running Out',
            none: 'Nothing Is Running Low',
        },
        plan: {
            trialLeft: (days: number, date: string) => `Your trial ends on ${date}, ${days === 1 ? '1 day' : `${days} days`} left.`,
            trialOver: (date: string) => `Your trial ended on ${date}.`,
        },
        channelSummary: {
            counts: (connected: number, active: number) => `${connected === 1 ? '1 channel' : `${connected} channels`} connected · ${active} active`,
            allWorking: 'All your channels are working.',
            broken: (count: number) => (count === 1 ? '1 channel has a problem.' : `${count} channels have a problem.`),
            manage: 'Manage Sales Channels',
        },
        welcome: (name: string) => `Welcome, ${name}`,
        emptyState: 'No Sales Channel Connected Yet',
        emptyStateHint: 'Add one to start publishing your products.',
        // The steps a person's role may do (docs/11 §4).
        setup: {
            title: 'Setup',
            progress: (done: number, total: number) => `${done} / ${total} completed`,
            done: 'Done',
            todo: 'To Do',
            optional: 'Optional',
            steps: {
                channel: {
                    title: 'Connect a Sales Channel',
                    hint: 'Connect your marketplace account. Your orders and products then come together in this panel.',
                },
                products: {
                    title: 'Add Products',
                    hint: 'Add your products one by one, or import them from a marketplace.',
                },
                mapping: {
                    title: 'Map Categories',
                    hint: "Match your categories to the marketplace's. Publishing needs it.",
                },
                carrier: {
                    title: 'Connect a Carrier',
                    hint: 'Connect the carrier you have an agreement with to book shipments for your orders.',
                },
                invoice: {
                    title: 'Connect an E-Invoice Service',
                    hint: 'Connect your e-invoice service to issue invoices from the panel.',
                },
                shop: {
                    title: 'Publish Your Shop',
                    hint: 'Open your own shop with Tezgah. This step is optional.',
                },
            },
        },
    },

    impersonation: {
        banner: 'Support is viewing this account. Every action is recorded.',
    },

    // The connection form every declared service shares: e-invoice and
    // payment services (ADR 0011 §2).
    providerForm: {
        name: 'Connection Name',
        nameHint: 'Your own label, for when you hold more than one.',
        environment: 'Environment',
        sandbox: 'Test',
        production: 'Live',
        submit: 'Save and Connect',
        useDefault: 'Default',
        testNote: (service: string) => `${service} connects to its test side: nothing real is filed or taken through it.`,
    },

    invoices: {
        manual: {
            title: 'Invoice Something Sold Elsewhere',
            hint: 'A trade sale, a repair, anything invoiced by arrangement — so part of your turnover does not live in another system.',
            open: 'New Invoice',
            issue: 'Issue',
            buyerKind: 'Buyer',
            person: 'A Person',
            company: 'A Company',
            buyerName: 'Name',
            taxNumber: 'Tax Number',
            taxNumberHint: 'A company invoice is filed against it; there is no document without one.',
            taxOffice: 'Tax Office',
            identityNumber: 'National Number',
            identityNumberHint: 'Optional. Leave empty if you do not have it.',
            address: 'Address',
            city: 'City',
            lines: 'Items',
            lineName: 'Description',
            quantity: 'Qty',
            unitPrice: 'Unit Price',
            taxRate: 'VAT %',
            addLine: 'Add an Item',
            lineIncomplete: 'Fill this in to put the item on the invoice, or delete the item.',
            total: 'Total, VAT Included',
            notes: 'Note on the Invoice',
        },
        fromDate: 'From',
        toDate: 'To',
        clearDates: 'Clear Dates',
        title: 'Invoices',
        subtitle: 'Every document issued, and what is waiting to be.',
        empty: 'No Invoices Yet',
        emptyFiltered: 'No Invoice Matches the Filters',
        emptyFilteredHint: 'Change or clear the filters.',
        emptyHint: 'Invoice an order and it appears here.',
        count: (shown: number, total: number) => `Showing ${shown} of ${total}`,
        search: 'Search by buyer or serial',

        kind: {
            all: 'All Documents',
            einvoice: 'E-Invoice',
            earchive: 'E-Archive',
            credit_note: 'Credit Note',
        },

        status: {
            all: 'All Statuses',
            draft: 'Draft',
            queued: 'Queued',
            sent: 'Sent',
            delivered: 'Delivered',
            failed: 'Failed',
            cancelled: 'Cancelled',
            // See tr.ts.
            recorded: 'Recorded, Not Filed',
        },

        problem: {
            unavailable: 'The e-invoice service cannot be reached right now. Try again in a few minutes.',
            unsupported: 'The connected e-invoice service cannot do this.',
            failed: 'The e-invoice service could not complete this. Try again in a few minutes.',
        },

        columns: {
            serial: 'Serial',
            buyer: 'Buyer',
            kind: 'Document',
            status: 'Status',
            total: 'Total',
            issued: 'Issued',
        },

        // A draft has no serial, which is exactly the difference between a legal
        // document and a record of intent.
        // The number is missing, which is not the same as the document being
        // missing: a service can hold a document it has not numbered.
        noSerial: 'No number',
        detail: {
            download: 'Download Invoice',
            downloadXml: 'Download the XML (UBL)',
            refresh: 'Refresh the Status',
            refreshed: 'The e-invoice service was asked again, and what it holds is shown here.',
            recordSerial: 'Record the Number',
            // Names the service, because "your provider" left the seller
            // guessing which one.
            recordSerialHint: (provider: string) =>
                `Issue the document in ${provider}’s own screen, then record its number here. The invoice counts from that moment and its link reaches the marketplace.`,
            serial: 'Invoice Number',
            issuedAt: 'Issue Date',
            // Said, because the field is optional and an empty date box beside a
            // required one reads as something forgotten.
            issuedAtHint: 'The date on the document. Left empty, today is used.',
            uploadDocument: 'Upload the Document',
            uploadHint:
                'This provider does not produce a rendered invoice. Upload the PDF you downloaded from it; this is what the marketplace links to.',
            sendToBuyer: 'Send to the Buyer',
            title: (serial: string, status?: string) =>
                serial
                    ? `Invoice ${serial}`
                    : // A document the service holds is not a draft: a draft never
                      // left this system, and calling it one hides that the sale
                      // already reached the provider.
                      status === 'recorded'
                      ? 'Invoice Without a Number'
                      : 'Draft Invoice',
            summary: 'Summary',
            // Named for what it does rather than for its category.
            actions: 'Invoice Actions',
            buyer: 'Billed to',
            lines: 'Lines',
            order: 'Order',
            subtotal: 'Net',
            tax: 'VAT',
            total: 'Total',
            exemption: (code: string) => `VAT Exemption ${code}`,
            cancel: 'Cancel Invoice',
            confirmCancel: {
                title: 'The Invoice Will Be Cancelled',
                body: 'An issued document is withdrawn at the e-invoice service.',
                action: 'Cancel Invoice',
            },
            creditNote: 'Issue Credit Note',
            confirmCreditNote: {
                title: 'A Credit Note Will Be Issued',
                body: 'It reverses this invoice. An issued invoice cannot be edited.',
                action: 'Issue Credit Note',
            },
            reason: 'Reason',
        },

        issue: {
            action: 'Issue Invoice',
            title: 'Invoice This Order',
            serial: 'Serial',
            serialHint: 'Only needed when you issued the document elsewhere.',
            exemptionCode: 'Exemption Code',
            exemptionCodeHint:
                'The Revenue Administration code for why this sale carries no VAT. Your accountant or your e-invoice service has the list.',
            exemptionCodeEmpty: 'Choose a code',
            exemptionReason: 'Exemption Note',
            exemptionLines: (names: string[]) =>
                names.length === 1 ? `"${names[0]}" carries no VAT.` : `${names.length} lines carry no VAT: ${names.join(', ')}.`,
            invalidLines: (names: string[]) =>
                names.length === 1
                    ? `"${names[0]}" cannot go on a document, so it is not among the lines below.`
                    : `${names.length} lines cannot go on a document, so they are not among the lines below: ${names.join(', ')}.`,
            notes: 'Note on the Invoice',
            submit: 'Issue',
            // Keyed on the code the server sends, so what a seller is told and
            // what the document builder actually refused stay the same thing.
            // What an internet sale's e-Archive lacks, one by one (ADR 0008 §11).
            missing: {
                website: 'the web address the sale was made at',
                payment_method: 'the payment method',
                paid_at: 'the payment date',
                carrier_tax_number: "the carrier's tax number; if you shipped under your own contract, choose the courier on the parcel's booking",
                shipped_at: 'the shipping date',
            },
            blocked: {
                unmatched_lines: 'Match every sold item to a product on the Summary tab before invoicing this order.',
                nothing_to_bill: 'Every item on this order was cancelled or could not be supplied, so there is nothing to bill.',
                buyer_unidentified: 'A company invoice needs the buyer’s tax number. Add it to the customer record:',
                invalid_tax_number: 'The buyer’s tax or identity number does not pass its own checksum. Correct it on the customer record:',
                invalid_lines: 'Something on this order cannot go on a document yet — check the tax rates and discounts.',
                exemption_required: 'A line carries no VAT. Turkish law needs a coded reason for that, and the marketplace did not send one.',
                internet_sale_incomplete: "This internet sale's e-Archive invoice lacks something the law requires:",
            },
            previewHint: 'What the invoice would contain',
            previewItem: 'Item',
            previewQuantity: 'Qty',
            previewTaxRate: 'VAT %',
            previewTaxRateFor: (line: string) => `VAT rate for ${line}`,
            taxRateSource:
                'VAT rates come from the sales channel the order arrived on; the carriage line takes the highest rate on the order. Change them here before issuing if they do not match the sale.',
            previewTotal: 'Total',
            excludedLines: (count: number) => (count === 1 ? 'One line left off: not supplied.' : `${count} lines left off: not supplied.`),
            issued: (serial: string) => `Issued as ${serial}`,
            existing: 'This order has been invoiced.',
            notIssued: 'This Order Has Not Been Invoiced Yet',
            view: 'Open the Invoice',
        },

        settings: {
            title: 'Invoice Settings',
            subtitle: 'Who issues your documents, and what they carry.',
            tabs: {
                providers: 'E-Invoice Services',
                templates: 'Templates',
            },
            addProvider: 'Add E-Invoice Service',
            providers: 'E-Invoice Services',
            connected: 'Connected',
            notReady: 'Not Yet Available',
            connect: 'Connect',
            verify: 'Check Connection',
            disconnect: 'Disconnect',
            confirmDisconnect: {
                title: 'The E-Invoice Service Will Be Disconnected',
                body: "No new documents are issued through it. Issued documents are kept. Returns and cancellations are then made in the service's own panel.",
                action: 'Disconnect',
            },
            default: 'Default',
            // What the service is called where the connection that filed a
            // document has since been withdrawn and cannot be named.
            providerFallback: 'your e-invoice service',
            makeDefault: 'Use by Default',
            issuesEInvoice: 'Issues e-Invoice',
            issuesEArchive: 'Issues e-Archive',
            empty: 'No E-Invoice Service Connected Yet',
            emptyHint: 'Invoices are issued through an e-invoice service. Connect yours to invoice your orders from here.',
            form: {
                title: (provider: string) => `Connect ${provider}`,
                settingsHint: "Choices that decide how this service files a document. What you leave empty takes the service's own default.",
                editTitle: (connection: string) => `Edit ${connection}`,
                secretsNote:
                    'The stored passwords and keys are kept and never shown. To change them, enter all of them again; left empty, they stay as they are.',
                save: 'Save and Check',
                documents: 'Documents It Issues',
                documentsHint:
                    'The buyer decides the document: a business registered for e-invoicing receives an e-Invoice, everyone else an e-Archive. Turn off what you do not issue through this service; that document then goes through the service that issues it.',
            },
            edit: 'Edit',
            moreActions: 'More Actions',
        },

        templates: {
            title: 'Invoice Templates',
            subtitle: 'What a document carries beyond the sale itself.',
            add: 'New Template',
            name: 'Template Name',
            sellerFromCompany: 'The seller details on a document come from Company Details.',
            editCompany: 'Edit Company Details',
            sellerLegalName: 'Legal Name',
            sellerTaxNumber: 'Tax Number',
            sellerTaxOffice: 'Tax Office',
            sellerAddress: 'Address',
            note: 'Note',
            footer: 'Footer',
            channel: 'Only for This Channel',
            allChannels: 'Every Channel',
            isDefault: 'Use as the Default',
            save: 'Save',
            delete: 'Delete',
            confirmDelete: {
                title: 'The Template Will Be Deleted',
                body: 'Documents already issued with it do not change.',
                action: 'Delete',
            },
            empty: 'No Template Yet',
            emptyHint: 'Documents are issued without a logo or a footer until you add one.',
        },
    },

    support: {
        title: 'Support Session',
        working: 'Opening the account…',
        failed: 'That support link is no longer valid. Ask for a new one.',
    },

    team: {
        title: 'Team',
        subtitle: 'The people in this company, and what each of them may do.',
        members: 'People',
        you: 'you',
        role: 'Role',
        status: {
            invited: 'Invited',
            disabled: 'Disabled',
            active: 'Active',
        },
        lastSeen: (when: string) => `Last signed in ${when}`,
        neverSignedIn: 'Has not signed in yet',
        disable: 'Disable',
        enable: 'Enable',

        pending: 'Waiting to Accept',
        expires: (when: string) => `expires ${when}`,
        revoke: 'Withdraw',

        invite: 'Invite Someone',
        email: 'E-mail',
        emailHint: 'They receive a link that is valid for seven days.',
        chooseRole: 'Choose a Role',
        sendInvite: 'Send Invitation',
        invited: 'The invitation has been sent.',

        roles: 'Roles',
        rolesHint:
            'What a role permits is what everyone holding it can do. Built-in roles are shared by every company and cannot be changed — create your own instead.',
        grantableHint: 'You can grant only permissions you hold yourself, and cannot change a role that holds more than you do.',
        builtin: 'Built-in',
        memberCount: (count: number) => (count === 1 ? '1 person' : `${count} people`),
        permissionCount: (count: number) => (count === 1 ? '1 permission' : `${count} permissions`),
        newRole: 'New Role',
        roleName: 'Role Name',
        roleDescription: 'Description',
        deleteRole: 'Delete',
        confirmDeleteRole: {
            title: 'The Role Will Be Deleted',
            body: 'Nobody may hold it any more. Built-in roles cannot be deleted.',
            action: 'Delete',
        },

        module: {
            identity: 'Company and People',
            catalog: 'Products',
            channel: 'Sales Channels',
            listing: 'Listings',
            inventory: 'Stock',
            pricing: 'Pricing',
            order: 'Orders',
            invoice: 'Invoicing',
            billing: 'Subscription',
            analytics: 'Reports',
        },
    },

    verifyEmail: {
        title: 'E-mail Verification',
        banner: 'Your e-mail address is not verified yet. Follow the link in your inbox.',
        resend: 'Send Again',
        sent: 'Verification e-mail sent. Check your inbox.',
        sendFailed: 'The e-mail could not be sent. Try again later.',
        done: 'Your e-mail address is verified.',
        invalid: 'This verification link is no longer valid. Request a new one from the panel.',
        continue: 'Go to the Panel',
    },
    invite: {
        title: 'Invitation',
        heading: (company: string) => `Join ${company}`,
        sentTo: (email: string) => `This invitation was sent to ${email}.`,
        existingAccount: 'You already have an account. Accepting adds this company to it.',
        accept: 'Accept and Join',
        invalid: 'This invitation is no longer valid. Ask for a new one.',
    },

    permissions: {
        'tenant.read': 'View the Company Profile',
        'tenant.write': 'Edit the Company Profile',
        'user.read': 'See Who Is in the Company',
        'user.write': 'Invite People, Change Roles and Disable Accounts',
        'role.write': 'Create and Edit Roles',
        'audit.read': 'View the Audit Log',
        'api_token.read': 'View API Tokens',
        'api_token.write': 'Create and Revoke API Tokens',
        'product.read': 'View Products',
        'product.write': 'Create and Edit Products',
        'product.delete': 'Delete Products',
        'category.write': 'Manage Categories',
        'feed.read': 'View Suppliers and Their Catalogues',
        'feed.write': 'Add Suppliers and Import Their Catalogues',
        'channel.read': 'View Sales Channels',
        'channel.write': 'Connect and Configure Sales Channels',
        'channel.credentials.write': 'Add or Replace Channel Credentials',
        'listing.read': 'View Channel Listings',
        'listing.publish': 'Publish and Update Channel Listings',
        'inventory.read': 'View Stock Levels',
        'inventory.write': 'Change Stock Levels',
        'price.write': 'Change Prices',
        'price_rule.read': 'View Pricing Rules',
        'price_rule.write': 'Create, Run and Undo Pricing Rules',
        'storefront.read': 'View the Shop, Its Codes, Reviews and Newsletter',
        'storefront.write': 'Change the Shop, Its Codes, Reviews and Newsletter',
        'webhook.read': 'View Webhooks',
        'webhook.write': 'Create, Change and Delete Webhooks',
        'order.read': 'View Orders',
        'order.write': 'Act on Orders — Pack, Split, Ship',
        'order.cancel': 'Cancel Order Items',
        'claim.write': 'Approve or Reject Returns',
        'invoice.read': 'View Invoices',
        'invoice.write': 'Issue Invoices',
        'billing.read': 'View the Subscription',
        'billing.write': 'Change the Subscription',
        'report.read': 'See Revenue and Reports',
    },

    storefront: {
        title: 'Tezgah',
        subtitle: 'Your site sells the same catalogue as the marketplaces, at your own prices.',
        noChannel: (ownStore: string) => `Set Up Your ${ownStore} Store First`,
        noChannelHint: 'Discount codes are used in your shop. Set up your shop first.',
        settings: 'Shop Settings',
        channel: 'Which Connection',
        name: 'Shop Name',
        slug: 'Address',
        slugHint: 'Lowercase letters, digits and hyphens. Your shop is reachable here immediately.',
        domain: 'Your Own Domain',
        domainHint:
            'Optional. Once saved, you verify it with the DNS record shown here; until then your shop and its links stay on the shop address above.',
        domainVerification: {
            verified: 'Verified',
            unverified: 'Awaiting Verification',
            verifiedNote: 'Your shop’s links in e-mails, on invoices and in this panel use this domain.',
            instructions: (type: string) =>
                `Add the ${type} record below in your domain’s DNS settings, then press Verify Domain. A new record can take up to an hour to be seen.`,
            recordName: 'Record Name',
            recordNameHint: 'Some DNS panels add your domain themselves; there, enter only the part before your domain.',
            recordValue: 'Record Value',
            copy: { copy: 'Copy', copied: 'Copied' },
            verify: 'Verify Domain',
            done: 'Your domain is verified.',
        },
        description: 'About the Shop',
        descriptionHint: 'Shown on the About page. Without a home page description on the SEO tab, the home page’s search result uses this text too.',
        theme: {
            appearanceHint:
                'Pick one of five ready-made looks or enter your own colour. Every choice shows in the preview on the right at once; the shop changes when you publish.',
            presets: 'Ready-Made Looks',
            presetsHint: 'Colour, type and corners come together; change any of them afterwards.',
            preset: {
                turuncu: 'Orange',
                kiraz: 'Cherry',
                gece: 'Night',
                orman: 'Forest',
                antrasit: 'Anthracite',
            },
            custom: 'Custom Colour',
            customHint: 'Buttons, links and badges use this colour. We pick the text colour on top of it for readability.',
            contrast: (ink: string) => (ink === '#FFFFFF' ? 'Text on buttons will be white.' : 'Text on buttons will be dark grey.'),
            contrastWarning:
                'White text is not readable enough on this colour; buttons will use dark grey text, drawn a shade lighter where that is what it takes to read. A darker shade would look better.',
            font: 'Typeface',
            fontOptions: {
                outfit: 'Outfit (Default)',
                manrope: 'Manrope',
                dm_sans: 'DM Sans',
                nunito_sans: 'Nunito Sans',
            },
            fontScale: 'Text Size',
            scaleOptions: { small: 'Small', normal: 'Normal', large: 'Large' },
            radius: 'Corner Roundness',
            radiusOptions: { sharp: 'Sharp', soft: 'Soft (Default)', round: 'Round' },
            density: 'Density',
            densityOptions: {
                comfortable: 'Comfortable',
                normal: 'Normal',
                compact: 'Compact',
            },
            gridColumns: 'Columns on Desktop',
            gridColumnsHint: 'Cards side by side in the product list. Always 2 on phones.',
            headerStyle: 'Header',
            headerOptions: { white: 'White', colored: 'Accent Colour' },
            buttonStyle: 'Button Style',
            buttonOptions: { filled: 'Filled', outline: 'Outlined' },
            logo: 'Logo',
            logoHint: 'PNG or WebP; fitted to 36 pixels tall. Without one the shop name is written.',
            logoFormat: 'Choose a PNG or WebP file for the logo.',
            favicon: 'Site Icon (Favicon)',
            faviconHint: 'Square PNG, at least 48×48; 64×64 is enough.',
            faviconFormat: 'Choose a PNG file for the site icon.',
            remove: 'Remove',
            reset: 'Back to Default',
            preview: 'Preview',
            previewButton: 'Add to Cart',
            previewText: 'Wooden Learning Puzzle, 24 pieces',
            previewNote: 'Free shipping · delivered in 2 working days',
            // The miniature shop drawn beside the settings. Every word in it is a
            // stand-in for the seller's own, so all of it is translated.
            previewShop: 'Shop',
            previewCart: 'Cart',
            previewAll: 'All',
            previewBrand: 'Brand',
            previewCategories: ['Toys', 'Baby', 'Puzzles'],
            previewRating: '4.6 (128)',
            // The draft's way to the shop and the looks published before (docs/14 §9.3).
            newShop: 'Save the shop first; its look is then edited and published here.',
            publish: 'Publish',
            saveDraft: 'Save Draft',
            discard: 'Delete Draft',
            unsaved: 'The look has unsaved changes. The shop keeps showing the live look.',
            draftWaiting: (at: string) => `A draft saved on ${at} waits to be published. Until then the shop shows the live look.`,
            upToDate: 'The shop shows this look.',
            publishedNotice: 'Published. The shop shows the new look.',
            draftSaved: 'Draft saved. Nothing changed on the shop.',
            discarded: 'Draft deleted.',
            restored: 'Restored. The shop shows this look again.',
            confirmDiscard: {
                title: 'Draft Will Be Deleted',
                body: 'The unpublished changes are deleted. The shop keeps its live look.',
                action: 'Delete',
            },
            confirmRestore: {
                title: 'Look Will Be Restored',
                body: 'This look is published again at once and the shop shows it.',
                bodyWithDraft: 'This look is published again at once and the shop shows it. Your unpublished changes are deleted.',
                action: 'Restore',
            },
            history: 'Publish History',
            historyHint: 'The last 10 published looks. Restoring one publishes it again at once.',
            historyEmpty: 'Nothing published here yet. The first publish also keeps the current look here.',
            live: 'Live',
            restore: 'Restore',
        },
        shippingFee: 'Shipping Fee',
        freeShippingOver: 'Free Shipping Over',
        freeShippingHint: 'Leave empty to always charge for shipping.',
        desiBands: {
            title: 'Shipping Fee by Desi',
            none: 'Not set. Every basket pays the Shipping Fee.',
            step: (desi: string, fee: string) => `from ${desi} desi ${fee}`,
            stepNumber: (n: number) => `Step ${n}`,
            edit: 'Edit Desi Steps',
            hint: "A basket pays the step its desi reaches: each line's desi or its weight in kg, whichever is greater, times its quantity. A basket below the first step, or holding a product with neither desi nor weight, pays the Shipping Fee. Free Shipping Over and free-shipping codes still apply. Saved with the page.",
            fromDesi: 'From Desi',
            fee: 'Fee',
            add: 'Add a Step',
            remove: 'Remove',
            apply: 'Apply',
        },
        couriers: 'Carriers Shown in the Shop',
        couriersNone: 'No carrier chosen yet; the checkout says "Standart Kargo".',
        couriersEdit: 'Choose Carriers',
        dispatchDays: 'Dispatch (days)',
        dispatchDaysHint: 'Working days before the order leaves. 0 = same day. A product with its own Days to Dispatch ships on those instead.',
        cutOffHour: 'Order-by Hour',
        cutOffHourHint:
            'Orders before this hour are handled the same day, later ones the next dispatch day. For example 16. Leave empty to handle every order on the day it is placed; same-day dispatch needs one. On an arife and on 28 October orders count until noon.',
        transitDays: 'Transit (days)',
        transitDaysHint: 'Days the carrier takes to deliver. Carriers deliver Monday to Saturday.',
        dispatchWeekdays: 'Dispatch Days',
        dispatchWeekdaysHint: 'The days you hand parcels to the carrier. Delivery dates count only these, and never a public holiday.',
        weekdayNames: { 1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday', 5: 'Friday', 6: 'Saturday' },
        dispatchLastDay: 'At least one day must stay chosen.',
        slowerDispatch: (count: number, names: string) =>
            `${count} products on sale keep a Days to Dispatch of their own longer than the shop's (${names}). While they do, the home page's trust line leaves the dispatch time out, because it would not hold for them.`,
        deliveryText: 'Delivery Note',
        deliveryTextHint: 'Shown on the product page and at checkout wherever no delivery date can be worked out. Up to 80 characters.',
        deliveryTextPlaceholder: 'Ships in 2–4 working days',
        contactPhone: 'Contact Phone',
        contactEmail: 'Contact E-mail',
        address: 'Business Address',
        published: 'Shop Active',
        accounts: 'Customer Accounts',
        accountsHint: 'Shoppers can open an account to see their orders, keep addresses and ask for returns. Guests can always check out.',
        basketReminders: 'Basket Reminder',
        basketRemindersHint:
            'A member who leaves items in the basket gets one e-mail a day later. It is a commercial message: it goes only to members who agreed to receive the shop’s e-mails, and every one carries a way to stop them.',
        saved: 'Saved.',
        visit: 'View the Shop Online',
        // A shop taken off the air for a while, not closed (docs/14 §13).
        maintenance: {
            title: 'Maintenance Mode',
            hint: 'While it is on, every page of the shop shows your message and no order can be placed. Search engines are told the shop is coming back, so it keeps its place. Closing the shop instead makes it disappear.',
            on: (since: string) => `The shop has been in maintenance since ${since}. Visitors see this message:`,
            off: 'The shop is open.',
            offClosed: 'The shop is closed, so nobody can reach it. Maintenance matters once it is open.',
            switchOn: 'Turn On Maintenance Mode',
            switchOff: 'Turn Off Maintenance Mode',
            preview: 'Preview the Shop',
            modalHint: 'Every page of the shop shows this message until you turn maintenance off. You can still see the shop with Preview the Shop.',
            message: 'Maintenance Message',
            messageHint: 'Up to 280 characters, for example when you will be back. Left empty, the shop says it will be back shortly.',
        },
        legal: {
            hint: 'Templates fill in with your business details and are a general starting point. Adapt them to your shop, and ask a lawyer where you are unsure.',
            useTemplate: 'Fill From Template',
            confirmReplace: {
                title: 'The Text Will Be Replaced With the Template',
                body: 'The current text is removed and the template takes its place. Reload the page before saving to get the old text back.',
                action: 'Replace With Template',
            },
        },
        publishCheck: {
            missing: 'Before opening the shop, complete:',
            liveMissing: 'Your shop is open but legally required details are missing:',
            taxId: 'Tax Number or MERSIS No',
            contact: 'Contact E-mail or Phone',
        },
        sections: {
            general: 'General',
            appearance: 'Appearance',
            sales: 'Shipping, Payment and Returns',
            shipping: 'Shipping and Currency',
            payment: 'Payment',
            paymentHint: 'How a customer can pay. Card payment connects to a payment service later.',
            returns: 'Returns',
            legal: 'Legal Texts',
            business: 'Business and Contact',
            businessHint: 'Shown on the About page and in the legal texts as the seller’s details.',
            sms: 'SMS',
            seo: 'SEO',
            seoHint:
                'Changes only the home page’s title and description, which search engines list your home page with. Product, category and other pages use their own names and descriptions. Nothing in the shop itself changes.',
        },
        showcase: {
            title: 'Showcase',
            hint: "The shop's top menu, the collections on the front page and the campaign banners. Left empty, the categories fill the menu and the standard vitrines the front page.",
            navigation: 'Top Menu',
            navigationHint:
                'A link is a shop path (/kategori/giyim, /ara?q=kupa, /koleksiyon/yaz) or an https:// address. Empty shows the top-level categories.',
            navLabel: 'Label',
            navHref: 'Link',
            navAdd: 'Add Menu Item',
            shortcuts: 'Category Shortcuts',
            shortcutsHint:
                'The pictured categories under the campaign banners on the home page. Up to 8; without a choice the first 8 top categories show.',
            shortcutPick: 'Pick a Category…',
            shortcutAdd: 'Add Shortcut',
            shortcutImage: 'Picture',
            shortcutImageHint: "Without one, the category's first product with a photograph shows. At least 300 pixels; JPEG, PNG or WebP.",
            shortcutImageRemove: 'Remove Picture',
            collections: 'Collections',
            collectionsHint: 'A vitrine on the front page and its own page under /koleksiyon/<slug>. Without a category, every product.',
            collectionTitle: 'Title',
            collectionCategory: 'Category',
            collectionAllCategories: 'All Products',
            collectionSort: 'Sort',
            collectionAdd: 'Add Collection',
            sortDefault: 'Featured',
            sortPriceAsc: 'Price: Low to High',
            sortPriceDesc: 'Price: High to Low',
            sortNewest: 'Newest',
            sortDiscount: 'Biggest Discount',
            banners: 'Campaign Banners',
            bannersHint: 'The sliding band at the top of the home page; up to 5 banners. Without a picture, the accent colour is used.',
            bannerTitle: 'Title',
            bannerSubtext: 'Subtext',
            bannerHref: 'Link',
            bannerImage: 'Picture',
            bannerImageHint: 'Shown 3:1 without a title, 4:3 with one; 2560 pixels wide is recommended.',
            bannerAlt: 'Picture Description',
            bannerAltHint:
                'Write what the picture shows or the words on it. Visitors who cannot see the picture and search engines know it by this text; required on a banner without a title.',
            bannerAdd: 'Add Banner',
            bannerMobileImage: 'Phone Image',
            bannerMobileImageHint: 'Optional; shown 4:3 on a phone, 1200×900 pixels recommended.',
            bannerMobileRemove: 'Remove the Phone Image',
            previewDesktop: 'Desktop',
            previewPhone: 'Phone',
            remove: 'Remove',
            moveUp: 'Move up',
            uploadFailed: 'The picture could not be uploaded.',
        },
        paymentCod: 'Cash on Delivery',
        paymentBankTransfer: 'Bank Transfer',
        paymentCard: 'Card Payment',
        paymentCardAwaiting: 'Offered to shoppers once a payment service is connected and checked.',
        paymentService: {
            title: 'Payment Service',
            hint: 'Cards are taken through the service you connect, with your own merchant account. Your keys are stored encrypted and never shown again.',
            add: 'Connect a Payment Service',
            empty: 'No payment service connected yet.',
            callbackUrl: (name: string) => `${name}: Notification Address`,
            copy: { copy: 'Copy', copied: 'Copied' },
            connect: 'Connect',
            connected: 'Connected',
            notReady: 'Not Available Yet',
            verify: 'Check Connection',
            disconnect: 'Disconnect',
            confirmDisconnect: {
                title: 'Payment Service Will Be Disconnected',
                body: 'Shoppers are not offered card payment until a service is connected again. Payments already taken are not affected.',
                action: 'Disconnect',
            },
            formTitle: (service: string) => `Connect ${service}`,
            settingsHint: "Choices that decide how this service takes a payment. What you leave empty takes the service's own default.",
            problem: {
                unavailable: 'The service could not be reached. Try again in a few minutes.',
                failed: 'The service did not accept these keys.',
            },
        },
        sms: {
            title: 'SMS Service',
            hint: 'Texts to your buyers go through the SMS service you connect, with your own account and sender name. Your password is stored encrypted and never shown again.',
            add: 'Connect an SMS Service',
            empty: 'No SMS service connected yet.',
            connect: 'Connect',
            notReady: 'Not Available',
            verify: 'Check Connection',
            test: 'Send Test SMS',
            moreActions: 'More actions for the SMS service',
            edit: 'Edit',
            disconnect: 'Disconnect',
            confirmDisconnect: {
                title: 'SMS Service Will Be Disconnected',
                body: 'Buyers are not texted until a service is connected again. Texts already sent are not affected.',
                action: 'Disconnect',
            },
            formTitle: (service: string) => `Connect ${service}`,
            editTitle: (name: string) => `Edit ${name}`,
            settingsHint: 'The sender name your texts go out under, exactly as your SMS service approved it.',
            secretsNote: 'Your stored password is kept unless you type a new one.',
            save: 'Save',
            lastError: (when: string) => `Last text not sent, ${when}:`,
            problem: {
                unavailable: 'The service could not be reached. Try again in a few minutes.',
                failed: 'The service did not accept this connection.',
            },
            testTitle: 'Send Test SMS',
            testHint: 'One test sentence is sent to the number you enter, at once. Enter your own mobile number.',
            testPhone: 'Mobile Number',
            testSend: 'Send',
            testFailed: 'The test SMS was not sent:',
            messages: 'Sent SMS',
            messagesHint: 'The latest texts, their numbers masked; the text itself is not kept.',
            messagesEmpty: 'No SMS sent yet.',
            kind: { order_received: 'Order Received', dispatched: 'Dispatched', claim_decided: 'Request Decided', test: 'Test' },
            status: { pending: 'Sending', sent: 'Sent', failed: 'Not Sent' },
            switches: 'Texts to Buyers',
            switchesHint: 'Each text uses one SMS on your account. Only news of the order goes out; never advertising.',
            switchesNeedService: 'Connect an SMS service above to text your buyers.',
            orderReceived: 'Order Received',
            orderReceivedHint: 'The order number and its tracking link, as soon as the order is placed.',
            dispatched: 'Dispatched',
            dispatchedHint: "With the carrier's tracking link, when the parcel leaves.",
            claimDecided: 'Request Decided',
            claimDecidedHint: 'When a return or cancellation request is approved or refused.',
        },
        paymentAwaitingIban: 'Awaiting IBAN',
        // Without an account the shopper is told to pay and never told where,
        // so the order waits for money that cannot arrive.
        bank: 'Bank Account',
        bankHint: 'A shopper who chooses bank transfer is shown this account on the confirmation and in their e-mail.',
        bankAccountName: 'Account Holder',
        bankName: 'Bank',
        bankIban: 'IBAN',
        bankIbanHint: '26 characters, starting with TR.',
        bankMissing: 'The shop offers bank transfer once the account below is filled in: without it the shopper could not see where to pay.',
        paymentLastMethod: 'At least one method has to stay on so customers can pay.',
        currency: 'Currency',
        announcementEnabled: 'Show the Announcement Bar',
        announcementText: 'Announcement Text',
        returnDays: 'Return Window (Days)',
        returnDaysHint: 'Leave 0 to hide the returns badge. e.g. 14 → “returns within 14 days”.',
        whatsappHint: 'A phone number (0535 123 45 67) or a wa.me link.',
        socialHint: 'Your user name (e.g. yourshop) or your profile address.',
        policyReturns: 'Return Policy Text',
        policyShipping: 'Shipping Policy Text',
        distanceSalesTerms: 'Distance Sales Agreement',
        privacyNotice: 'Privacy Policy',
        legalHint: 'Required of every shop selling online in Turkey.',
        legalName: 'Legal Name',
        taxOffice: 'Tax Office',
        taxNumber: 'Tax Number',
        businessFromCompany: 'The legal name, tax office and tax number come from Company Details.',
        editCompany: 'Edit Company Details',
        mersis: 'MERSIS No',
        tradeRegistryNo: 'Trade Registry No',
        kep: 'KEP Address',
        kepHint: 'Your registered electronic mail address. Publishing it is required where you have one; leave it empty otherwise.',
        metaTitle: 'Home Page Title',
        metaTitleHint:
            'Your shop’s name and what you sell, within 60 characters: “Mavi Atölye — Handmade Ceramics”. Do not write a list of keywords. When empty, the shop name is used.',
        metaDescription: 'Home Page Description',
        metaDescriptionHint:
            'Say what the home page offers in a sentence or two, within 160 characters; a longer text is cut off in search results. When empty, the About the Shop text is used.',
        dangerZone: 'Danger Zone',
        delete: 'Delete Shop',
        deleteHint: 'The shop and its discount codes close and its address and data are hidden. You can open it again on the same address.',
        confirmDelete: {
            title: 'The Shop Will Be Deleted',
            body: 'The shop and its discount codes close; its address and data are hidden. You can reopen at the same address.',
            action: 'Delete Shop',
        },
    },

    shipping: {
        awaitingTransfer: 'The transfer for this order has not arrived. It can be shipped once you confirm it with Payment Received.',
        title: 'Shipping',
        subtitle: 'Who carries your parcels, where they leave from, and what is on its way.',
        tabs: {
            shipments: 'Shipments',
            carriers: 'Carriers',
            origins: 'Pickup Addresses',
        },
        couriers: {
            title: 'Carriers You Work With',
            hint: 'Parcels go with your own contract. Choose the carriers you use: the first is the default when you book a parcel, and your shop names them to shoppers.',
            add: 'Add Carrier',
            addTitle: 'Add a Carrier',
            addHint: 'Carriers are listed by name and nothing is connected. The tracking number is the one your courier gives you.',
            addOne: 'Add',
            allChosen: 'Every carrier on the list is already chosen.',
            empty: 'No Carrier Chosen Yet',
            emptyHint: 'Add the carriers you ship with. When booking a parcel you can still name a courier that is not on the list.',
            moveUp: 'Move Up',
            remove: 'Remove',
        },
        editOrigin: 'Edit the Address',
        removeOrigin: 'Remove the Address',
        removeOriginBody: 'This pickup address will be deleted. Parcels leave from the addresses that remain.',
        addOrigin: 'Add Address',
        openParcels: 'On Its Way',
        nothingOpen: 'Nothing Waiting to Go Out',
        default: 'Default',
        problem: {
            unavailable: 'The carrier cannot be reached right now. Try again in a few minutes.',
            unsupported: 'This carrier cannot do this.',
            failed: 'The carrier could not complete this. Try again in a few minutes.',
        },

        origins: 'Where Parcels Are Sent From',
        originsHint:
            'Every carrier books against a pickup address. It is not the address on your invoices, and a second warehouse is a second address.',
        originName: 'Name',
        contactName: 'Contact',
        phone: 'Phone',
        address: 'Address',
        district: 'District',
        city: 'City',

        parcels: 'Parcels',
        open: 'Book a Shipment',
        openHint: 'Book when the box is ready; the tracking number goes to the order and the customer.',
        parcelStatus: {
            pending: 'Pending',
            ready: 'Ready',
            readytoship: 'Ready to Ship',
            // The shop's own tokens (ADR 0003 §7), as the seller sets them.
            confirmed: 'Confirmed',
            packed: 'Packed',
            picking: 'Picking',
            invoiced: 'Invoiced',
            shipped: 'Shipped',
            delivered: 'Delivered',
            cancelled: 'Cancelled',
            returned: 'Returned',
            unpacked: 'Unpacked',
            open: 'Open',
            packaged: 'Packaged',
            package: 'Packaged',
            intransit: 'Shipped',
            cancelledbymerchant: 'Cancelled by Seller',
            cancelledbycustomer: 'Cancelled by Customer',
            cancelledbysap: 'Cancelled',
            claimcreated: 'Return Requested',
            // The stages of a marketplace that keeps a status on each item and
            // gives a parcel its least advanced item's: an order received, a
            // cancellation or a return asked for and decided, a parcel waiting
            // at a store or a pickup point. Every marketplace's parcel at one
            // of these tokens reads the same words.
            created: 'Order Received',
            cancelrequested: 'Cancellation Requested',
            atstore: 'Waiting at the Store',
            atdeliverypoint: 'Waiting at the Pickup Point',
            undelivered: 'Undelivered',
            unsupplied: 'Not Supplied',
            returnrequested: 'Return Requested',
            returnrejected: 'Return Rejected',
            returnaccepted: 'Return Approved',
            outfordelivery: 'Out for Delivery',
            returnedtoseller: 'Returned to Seller',
        },
        parcelCount: (count: number) => (count === 1 ? '1 parcel' : `${count} parcels`),
        parcelsLabel: 'Parcels',
        notBooked: 'Not Booked',
        carrier: 'Carrier',
        defaultCarrier: 'Default Carrier',
        trackingNumber: 'Tracking Number',
        trackingHint: 'The number your courier gave you. Leave empty when the carrier assigns one.',
        courier: 'Courier',
        courierHint: 'The company carrying the parcel. The invoice, the customer and the sales channel see it.',
        courierPick: 'Choose',
        courierOther: 'Not Listed',
        courierGroupChosen: 'Your Carriers',
        courierGroupOther: 'Other Carriers',
        courierName: "Courier's Name",
        courierTaxNumber: "Courier's Tax Number",
        courierTaxNumberHint: "An e-Archive invoice carries the carrier's 10-digit tax number.",
        desi: 'Desi',
        desiHint: 'Turkish carriers price on volume. Without it they reprice the parcel at their number.',
        cod: 'Cash on Delivery',
        book: 'Book the Parcel',
        printLabel: 'Print',
        track: 'Track',
        cancel: 'Cancel',
        confirmCancel: {
            title: 'The Booking Will Be Cancelled',
            body: 'The tracking number stops working. If the parcel has left, call the carrier.',
            action: 'Cancel Booking',
        },

        status: {
            draft: 'Draft',
            booked: 'Booked',
            in_transit: 'On Its Way',
            delivered: 'Delivered',
            returned: 'Returned',
            cancelled: 'Cancelled',
            failed: 'Failed',
        },
    },

    claims: {
        pageTitle: 'Returns',
        subtitle: 'What customers sent back, and what you decided about it.',
        title: 'Returns and Claims',
        empty: 'No Returns Yet',
        emptyHint: 'A return raised on a marketplace or in your shop appears here.',
        kindLabel: 'Kind',
        kind: {
            return: 'Return',
            cancellation: 'Cancellation',
            missing: 'Never Arrived',
            damaged: 'Arrived Damaged',
            other: 'Other',
            exchange: 'Exchange',
        },
        status: {
            all: 'All',
            open: 'Waiting on You',
            approved: 'Approved',
            rejected: 'Refused',
            resolved: 'Done',
            cancelled: 'Withdrawn',
            // An open case the marketplace holds for the seller's pre-approval.
            preApproval: 'Awaiting Pre-Approval',
        },
        preApprove: 'Give Pre-Approval',
        preApprovalApiHint:
            'The marketplace is waiting for your pre-approval before this request can be answered. A pre-approval says you will approve or reject the request once you have examined the goods.',
        confirmPreApprove: {
            title: 'Pre-Approval Will Be Given',
            body: 'The marketplace is told you will approve or reject this request after examining the goods. The request stays open; you give your decision afterwards.',
            action: 'Give Pre-Approval',
        },
        preApproved: 'Your pre-approval reached the marketplace. The request now waits for your decision.',
        // An exchange settled with the money rather than the replacement
        // (ADR 0003 §5).
        settleWithRefund: 'Settle With a Refund',
        confirmSettleWithRefund: {
            title: 'The Exchange Will Be Settled With a Refund',
            body: 'No replacement goes to the customer. The marketplace refunds what they paid, and the goods count as returned on the order.',
            action: 'Settle With a Refund',
        },
        exchangeRefunded: 'This exchange request was settled with a refund.',
        moreActions: 'More Actions',
        preApprovalHint:
            'The marketplace is waiting for your pre-approval before this request can be answered. Give it in the marketplace’s own panel.',
        reasonLabel: 'What the Customer Said',
        reasonHint: 'In their words. A dropdown loses the sentence that explains what happened.',
        restockOnApproval: 'Put the Goods Back on the Shelf When Approved',
        restockNow: 'Put the Goods Back on the Shelf Now',
        restockNowHint: 'The return is approved as you record it. Tick this only if the goods are already back with you.',
        recordApprovedHint: 'A return or cancellation is approved as you record it.',
        recordOpenHint: 'This record stays open until you decide it.',
        open: 'Open a Return',
        openHint: 'When a customer asks for a return by phone or through the shop, open it here.',
        none: 'No Returns on This Order',
        rejectTitle: 'The Return Will Be Rejected',
        rejectHint: 'The reason goes to the customer and the marketplace; a rejection without one is overturned.',
        approve: 'Approve',
        approveAndRestock: 'Approve and Restock',
        reject: 'Refuse',
        rejectReason: 'Why Are You Refusing?',
        rejectReasonHint: 'A refusal with no reason is one the marketplace overturns.',
        rejectCode: 'Coded Reason for the Channel',
        rejectCodeHint: 'Sent to the marketplace so it can read the reason, rather than your own words.',
        pickReason: 'Select a Reason',
        refund: 'Refund',
        respondBy: 'Answer by',
        openedAt: 'Requested',
        count: (shown: number, total: number) => `Showing ${shown} of ${total}`,
        // Why goods come back (EPZ-0190): a marketplace's reason in its own
        // words, a shop's from shopReasons below.
        returnReasons: {
            title: 'Return Reasons',
            description: (days: number) => `Returns opened in the last ${days} days, by the reason given.`,
            reason: 'Reason',
            channel: 'Channel',
            count: 'Returns',
            share: 'Share',
            unspecified: 'Not given',
            empty: (days: number) => `No Returns in the Last ${days} Days`,
            emptyHint: 'Each return opened on a marketplace or in your shop is counted here by its reason.',
        },
        channelConfirmed: 'Channel Confirmed',
        channelConfirmedHint: "The order's own status updates once the channel processes and reports this decision — that can take a moment.",
        channelFailed: 'Channel Did Not Confirm',
        // A request a shop's buyer made from their account (ADR 0010), and
        // the shop's fixed reasons it carries.
        fromAccount: 'From the Customer’s Account',
        shopReasons: {
            damaged: 'Arrived damaged',
            wrong_item: 'Wrong item sent',
            not_as_described: 'Not as described',
            size: 'Size or fit did not suit',
            changed_mind: 'Changed their mind',
            late: 'Order was late',
            other: 'Other',
        },
    },

    pricing: {
        title: 'Pricing',
        subtitle: 'What you charge, worked out from what it costs you.',
        preview: 'Preview',
        apply: 'Apply',
        applyRules: 'Apply Rules',
        applied: (changed: number, considered: number, skipped: number) =>
            `${changed} prices changed out of ${considered} considered, ${skipped} left alone.`,

        previewTitle: 'What This Would Do',
        previewDescription: 'Nothing is written. Each row shows what would be left after the marketplace, the tax and the parcel.',
        previewSummary: (considered: number, changed: number, skipped: number) =>
            `${considered} offers considered · ${changed} would change · ${skipped} would be left alone`,
        noSamples: 'No Offer Matched a Rule',

        columns: {
            product: 'Product',
            cost: 'Cost',
            currentPrice: 'Now',
            newPrice: 'Would Be',
            margin: 'Margin',
        },

        modes: {
            markup: 'Cost Plus',
            margin: 'Target Margin',
            multiply: 'Multiply Cost By',
            fixed: 'Fixed Price',
            competitor: 'Relative to the Cheapest Competitor',
            product_percent: 'Product Price by %',
            product_amount: 'Product Price Plus a Sum',
        },

        rounding: {
            none: 'Leave as Is',
            up: 'Up to the Next Whole',
            nearest: 'To the Nearest Whole',
            ninetyNine: 'Up to the Next x.99',
        },

        scopes: {
            all: 'Every Product',
            category: 'categories',
            brand: 'brands',
            product: 'products',
            selection: (kind: string, count: number) => `${count} ${kind}`,
            filter: 'Bulk edit selection',
        },

        rules: {
            currency: 'Rule Currency',
            currencyHint:
                'What this rule’s own amounts are stated in — a fixed price, the floor, the ceiling and the shipping cost. A fixed rule in dollars is a foreign selling price: said once, converted at the day’s rate on every run.',
            cadence: 'How Often',
            cadenceHint: 'Only the automatic run is held back. Applying the rule yourself always works.',
            cadences: {
                daily: 'Every Night',
                every3days: 'Every Three Days',
                weekly: 'Weekly',
                monthly: 'Monthly',
            },
            title: 'Rules',
            description: 'Rules do not stack: the first that matches a product decides its price, so you can always say which rule set it.',
            add: 'Add Rule',
            edit: 'Edit Rule',
            name: 'Rule',
            mode: 'How the Price Is Worked Out',
            modeHint: 'Cost plus 35% leaves a margin of 26%, not 35%. Target margin asks for what is actually left.',
            value: 'Amount',
            undercut: 'Below the Competitor (%)',
            undercutHint: 'How far below the cheapest competing offer on the channel. 0 matches it.',
            floor: 'Floor Price',
            floorHint: 'The rule never prices below this.',
            ceiling: 'Ceiling Price',
            ceilingHint: 'The rule never prices above this. Leave empty for no ceiling.',
            formula: 'Formula',
            channel: 'Channel',
            channelHint: 'Leave empty to price every connected channel with this rule.',
            competitorChannelHint: 'Competitor prices are read per marketplace, so this rule prices one channel.',
            everyChannel: 'Every Channel',
            oneChannel: 'one channel',
            commission: 'Commission (%)',
            commissionHint: 'What the marketplace keeps. A margin that ignores it is wrong by more than the margin.',
            readCommission: 'Read From Channel',
            commissionRead: (listed: number) => `The channel was asked about ${listed} offers this rule covers.`,
            commissionMore: 'The rule covers more offers on the channel; these are read from the first of them.',
            commissionUnread: (count: number) => `The channel gave no rate for ${count} of them.`,
            commissionNoneListed: 'None of the offers this rule covers is on the channel yet.',
            commissionPick: 'Choose a rate to put it in Commission (%). Nothing is saved until you save the rule.',
            commissionShare: (rate: string, listings: number) => `${rate} · ${listings} offers`,
            shipping: 'Shipping Cost',
            shippingHint: 'What sending one costs you, where you pay for delivery.',
            rounding: 'Rounding',
            costIncludesVat: 'The Recorded Cost Already Includes VAT',
            neverBelowCost: 'Never Price Below Cost',
            autoApply: 'Apply This Rule Automatically Every Night',
            priority: 'Order',
            state: 'State',
            active: 'Active',
            paused: 'Paused',
            automatic: 'Automatic',
            empty: 'No Rules Yet',
            emptyHint: 'Add one, preview what it would do, then apply it.',
            confirmDelete: {
                title: 'The Rule Will Be Removed',
                body: 'The prices it set stay where they are.',
                action: 'Remove',
            },
        },

        runs: {
            title: 'Repricing History',
            description: 'Every price that moved, and how to put it back.',
            when: 'When',
            result: 'Result',
            status: 'State',
            counts: (changed: number, considered: number) => `${changed} changed of ${considered}`,
            skipped: (count: number) => `${count} left alone`,
            revert: 'Put Back',
            confirmRevert: {
                title: 'Prices Will Be Restored',
                body: 'Every price this run changed goes back to its previous value and is sent to the channels.',
                action: 'Restore',
            },
            empty: 'Nothing Has Been Repriced Yet',
        },

        runStatus: {
            running: 'Running',
            succeeded: 'Done',
            failed: 'Failed',
            reverted: 'Put Back',
        },

        rates: {
            title: 'Exchange Rates',
            description: 'From the central bank, used to convert a supplier’s cost into lira.',
            currency: 'Currency',
            rate: 'Lira',
            validOn: 'Published',
            empty: 'No Rates Have Been Fetched Yet',
            emptyHint: 'They are fetched a few times a day and only matter for costs in another currency.',
        },
    },

    warehouses: {
        edit: 'Edit Warehouse',
        title: 'Warehouses',
        subtitle: 'Where stock is held and shipped from.',
        add: 'Add Warehouse',
        name: 'Warehouse',
        code: 'Short Name',
        codeHint: 'The warehouse’s short name in channel settings and shipping. It cannot be changed later.',
        city: 'City',
        priority: 'Priority',
        priorityHint: 'The warehouse with the smaller number is used first: an order is filled from its stock before the others.',
        stock: 'Sellable',
        onHand: (onHand: number, variants: number) => `${onHand} on hand · ${variants} products`,
        state: 'State',
        isDefault: 'Default',
        paused: 'Paused',
        selling: 'Open for Sale',
        sellingHint:
            'A paused warehouse’s stock is not counted as sellable on any channel, and no order is filled from it. The channels are told as soon as you save.',
        makeDefault: 'Make Default',
        empty: 'No Warehouses Yet',
        confirmDelete: {
            title: 'The Warehouse Will Be Removed',
            body: 'It must be empty first. Its stock records go with it.',
            action: 'Remove',
        },
        transfer: {
            action: 'Move the Stock',
            title: (name: string) => `Move the Stock of ${name}`,
            hint: 'Everything this warehouse holds, except stock reserved for orders, moves to the warehouse you choose. Each move is recorded in the stock history as a Transfer.',
            target: 'Target Warehouse',
            submit: 'Move the Stock',
        },
    },

    suppliers: {
        title: 'Suppliers',
        subtitle: 'Catalogues somebody else maintains, kept in step with yours.',
        tabs: {
            mapping: 'Mapping',
            rules: 'Rules',
            runs: 'Read History',
            items: 'Products',
            images: 'Images',
        },
        supplier: 'Supplier',
        code: 'Code',
        codeHint: 'Prefixes imported stock codes, so two suppliers numbering from 1001 do not collide.',
        backToList: '← Suppliers',
        // Runs read the saved mapping and rules, never the form (docs/11 §3).
        saveFirst: 'Preview, Import and Send Again work from the saved mapping and rules. Save your changes first.',

        list: {
            title: 'Suppliers',
            description: 'Who the catalogues belong to.',
            add: 'Add Supplier',
            empty: 'No Suppliers Yet',
            edit: 'Edit Supplier',
            feedCount: 'Product Lists',
        },

        feeds: {
            title: 'Product Lists',
            description: 'A supplier publishes a file; this reads it and imports what changed.',
            add: 'Add Product List',
            name: 'Product List',
            format: 'Format',
            url: 'Address',
            urlHint: 'The public address of the file, starting with https. An address inside our own network is not accepted.',
            schedule: 'Schedule',
            manual: 'On demand',
            lastRun: 'Last Read',
            never: 'Never read',
            itemCount: (count: number) => `${count} records`,
            empty: 'No Product Lists Yet',
            emptyHint: 'Add a supplier, then the address of the file they publish.',
            confirmDelete: {
                title: 'The Product List Will Be Removed',
                body: 'The products it imported stay in the catalogue. It is not read again.',
                action: 'Remove',
            },
        },

        mapping: {
            title: 'Field Mapping',
            description: 'Which part of the supplier’s file goes into which of your fields.',
            read: 'Read the File',
            reading: 'Reading…',
            found: (records: number, fields: number) => `${records} records read, ${fields} fields found.`,
            itemPath: 'Record Path',
            itemPathHint:
                'Where one product sits in the file, such as Products/Product. Found automatically; change it if the wrong thing was picked.',
            unmapped: '— Not Mapped —',
            noFields: 'The File Has Not Been Read Yet',
            noFieldsHint: 'Read it, and every field is offered here with the values that were in it.',
            // Reading shows samples; only Save keeps a choice (EPZ-0288).
            readHint: 'Reading the file only shows sample values beside each field. A mapping is kept when you save it.',
            fields: {
                stockCode: 'Stock Code',
                title: 'Product Name',
                description: 'Description',
                barcode: 'Barcode',
                mpn: 'Manufacturer Code',
                brand: 'Brand',
                category: 'Category',
                salePrice: 'Supplier Price',
                listPrice: 'List Price',
                currency: 'Currency',
                vatRate: 'VAT Rate',
                quantity: 'Stock',
                desi: 'Desi',
                weight: 'Weight',
                images: 'Images',
            },
            // A supplier who writes each picture into a field of its own names
            // each here, in the gallery's order (ADR 0007, EPZ-0287).
            imagesHint:
                'Read in this order: the first field’s first picture is the main image. An empty field is skipped, an address repeated across fields is taken once, and at most 12 pictures are taken.',
            imagesLater:
                'Pictures are taken when the list creates a product. Send Again adds pictures only to products that have none, so a field added later, or a picture the supplier changes, does not reach a product that already has pictures.',
            imageField: (position: number) => `Image Field ${position}`,
            mainImage: 'Main Image',
            noImageFields: 'No image field is mapped.',
            addImageField: 'Add Image Field',
            moveUp: 'Move Up',
            moveImageFieldUp: (position: number) => `Move image field ${position} up`,
            remove: 'Remove',
            removeImageField: (position: number) => `Remove image field ${position}`,
        },

        rules: {
            title: 'Pricing and Stock Rules',
            description: 'What to do with the values once they are read.',
            margin: 'Margin (%)',
            marginHint: '35 sells the supplier’s 100 at 135.',
            rounding: 'Rounding',
            roundingNone: 'Leave as Is',
            roundingUp: 'Up to the Next Whole',
            roundingNearest: 'To the Nearest Whole',
            rounding99: 'Up to the Next x.99',
            skuPrefix: 'Stock Code Prefix',
            skuPrefixHint: 'Prepended to the supplier’s own code.',
            minQuantity: 'Stock Floor',
            minQuantityHint: 'At or below this, treat the product as out of stock.',
            maxQuantity: 'Stock Ceiling',
            maxQuantityHint: 'A file claiming 99999 of everything is a placeholder, not a warehouse. Leave at 0 to believe it.',
            currency: 'Currency',
            currencyAsFile: 'As the File States',
            currencyHint: 'Used where the file does not say. A supplier selling only in lira sees no reason to write it forty thousand times.',
            vatRate: 'VAT Rate (%)',
            vatRateHint: 'Used where the file does not say.',
            desi: 'Desi',
            desiHint: 'Used where the file does not say.',
            listMultiplier: 'Struck-Through Price Multiplier',
            listMultiplierHint: '1.2 shows 120 crossed out beside 100. Used where the supplier gives no list price.',
            force: 'Ignore the File and Always Use This',
            requireBarcode: 'Import Only Products With a Valid Barcode',
            requireBarcodeHint:
                'Records without a real GTIN are refused here, with the supplier’s own code on them, rather than as rejections at the marketplace. A missing barcode is never invented: it identifies a manufacturer’s product to the whole trade, and one we made up would identify somebody else’s.',
            createMissing: 'Create Products the Catalogue Does Not Have',
            updatePrice: 'Update Cost and Details on Later Runs',
            updateQuantity: 'Update Stock on Later Runs',
            zeroMissing: 'Set Stock to Zero When the Supplier Stops Carrying a Product',
            targetChannels: 'Target Channels',
            targetChannelsHint:
                'Products are offered to these channels (price + stock). With none selected they only fill the catalogue and appear on no channel.',
            targetChannelsEmpty: 'Connect a channel first.',
            schedule: 'Schedule',
            scheduleHint: 'A cron expression, such as 0 */6 * * * for every six hours. Leave empty to read it only when you ask.',
        },

        runs: {
            title: 'Read History',
            description: 'What happened each time the file was read.',
            preview: 'Preview',
            import: 'Import',
            resync: 'Send Again',
            resyncHint:
                'Re-applies every product with the current settings, even unchanged ones: offers to the target channels, sets stock and fills empty galleries.',
            running: 'The file is being read. This screen updates itself.',
            empty: 'This Product List Has Not Been Read Yet',
            when: 'When',
            mode: 'Kind',
            status: 'Result',
            result: 'Products',
            counts: (seen: number, created: number, updated: number) => `${seen} read · ${created} new · ${updated} changed`,
            overLimit: (count: number) => `${count} not added: the package’s product limit`,
            untouched: (unchanged: number, failed: number) => `${unchanged} unchanged · ${failed} not imported`,
        },

        runStatus: {
            running: 'Reading',
            succeeded: 'Done',
            failed: 'Failed',
            cancelled: 'Stopped',
        },

        items: {
            title: 'Products in This List',
            description: 'What the supplier carries and what became of it here.',
            search: 'Search',
            searchPlaceholder: 'Search by code or name',
            filter: 'Show',
            all: 'Everything',
            code: 'Supplier Code',
            status: 'State',
            cost: 'Supplier Price',
            price: 'Your Price',
            quantity: 'Stock',
            // How many pictures the record gave, read from the image fields before
            // any is fetched, so a preview shows what each product would bring.
            images: 'Images',
            product: 'Product',
            open: 'Open',
            empty: 'Nothing Has Been Read Yet',
            emptyHint: 'Preview or import the list, and its products appear here.',
            count: (shown: number, total: number) => `${shown} of ${total} products`,
            // A record the package had no room for (ADR 0017 §2); every import tries it again.
            overLimitHint: 'Not added: the product limit your package includes. It is added on the next import once your package has room.',
        },

        itemStatus: {
            pending: 'Waiting',
            created: 'New',
            updated: 'Changed',
            unchanged: 'Unchanged',
            skipped: 'Not Imported',
            failed: 'Failed',
            missing: 'Withdrawn',
            over_limit: 'Package Limit',
        },

        // The pictures of the list's products, fetched after the run that
        // imported the products (ADR 0007, EPZ-0289).
        images: {
            title: 'Product Images',
            description:
                'A run imports the products; their images are fetched afterwards, in the background. This screen updates itself while any are waiting.',
            // The counts here arrive formatted (docs/15 §3).
            summary: (pending: string, stored: string, failed: string) => `Images: ${pending} waiting · ${stored} fetched · ${failed} not fetched.`,
            stage: {
                pending: 'Products imported; their images are being processed.',
                failed: 'Products imported; some of their images could not be fetched.',
                done: 'Products and their images are in.',
            },
            none: 'This list has not offered any images yet.',
            retry: 'Try Again',
            retried: (count: string) =>
                count === '1' ? '1 image is waiting to be fetched again.' : `${count} images are waiting to be fetched again.`,
            caption: 'Products With Images Not Fetched',
            failures: 'Images Not Fetched',
            empty: 'No Images Missing',
            emptyHint: 'Every image this list offered was fetched, or is still on its way.',
            count: (shown: number, total: number) => `${shown} of ${total} products`,
        },
    },

    customers: {
        anonymise: 'Erase Personal Data (KVKK)',
        anonymiseConfirm: {
            title: 'Customer Details Will Be Erased',
            body: 'Name, e-mail and phone are permanently erased. Orders remain but no longer name anyone.',
            action: 'Erase Permanently',
        },
        title: 'Customers',
        subtitle: 'Everyone who has bought something, wherever they bought it.',
        search: 'Search by name, phone, e-mail or tax number',
        empty: 'No Customers Yet',
        emptyHint: 'A customer appears here as soon as an order arrives.',
        name: 'Customer',
        company: 'Company',
        // A customer with an account at the seller's own shop (ADR 0010).
        member: 'Tezgah Member',
        membersOnly: 'Tezgah Members',
        contact: 'Contact',
        orderCount: 'Orders',
        totalSpent: 'Spent',
        lastOrder: 'Last Order',
        count: (shown: number, total: number) => `Showing ${shown} of ${total}`,
        noContact: 'No contact details',
        spentSoFar: (amount: string, count: number) => (count === 1 ? `${amount} over 1 order` : `${amount} over ${count} orders`),
        details: 'Details',
        editDetails: 'Edit Details',
        editDetailsHint: 'A field left blank keeps what is already there.',
        email: 'E-mail',
        phone: 'Phone',
        taxNumber: 'Tax Number',
        taxOffice: 'Tax Office',
        identityNumber: 'National ID Number',
        noTaxNumber: 'No tax or identity number — an invoice cannot be issued yet.',
        tags: 'Tags',
        noTags: 'No tags',
        tagsLabel: 'Tags',
        tagsHint: 'Separated by commas. Your own words: wholesale, careful, repeat.',
        orders: 'Orders',
        noOrders: 'No Orders',
        notes: 'Notes',
        noNotes: 'Nothing Written Down Yet',
        notesHint: 'What somebody learned by dealing with them. Notes are kept, never overwritten.',
        addNote: 'Write a Note',
        saveNote: 'Add Note',
    },

    sellerSupport: {
        title: 'Support',
        subtitle: 'Ask us anything about your account or the panel.',
        formTitle: 'Write to Us',
        formHint: 'We reply by e-mail, usually within one working day.',
        replyingTo: (email: string) => `We will reply to ${email}.`,
        subject: 'Subject',
        message: 'Your Message',
        messageHint: 'Tell us what you were doing and what happened. Order or product numbers help.',
        send: 'Send',
        sent: 'Your message has reached us. We will reply by e-mail.',
    },
    subscription: {
        title: 'Subscription',
        subtitle: 'Your package, its limits and this month’s usage.',
        currentPlan: 'Current Package',
        status: 'Status',
        trialEnds: 'Trial Ends',
        // A meter for each limit the package sets; a limit left empty has none (ADR 0017 §1).
        limits: {
            products: 'Products',
            channels: 'Marketplace Accounts',
            users: 'Users',
            orders_monthly: 'Orders This Month',
        },
        availablePlans: 'Packages',
        planDescription: {
            starter: 'For a seller getting their first channels connected.',
            growth: 'For a growing catalogue across several marketplaces.',
            scale: 'For high-volume sellers who need room without limits.',
        },
        perMonth: '/ month',
        perYear: '/ year, paid upfront',
        free: 'Free',
        // A package's price states its VAT (docs/15 §4): the figures are the API's.
        price: {
            net: (net: string) => `${net} + VAT`,
            total: (total: string) => `${total} incl. VAT`,
        },
        switchTo: 'Switch to This Package',
        switchTitle: (plan: string) => `Switch to ${plan}`,
        switchBody: 'Until billing is connected, we change packages for you. Your request reaches our team; we reply the same day.',
        switchAction: 'Send Request',
        message: 'Note (Optional)',
        messageHint: 'When you would like to switch, or a question.',
        requestSent: 'Request received. Our team will reply by e-mail.',
        // A package priced by offer: its price, and its action, which asks for one (docs/15 §2).
        contactSales: 'Priced by offer',
        requestOffer: 'Ask for an Offer',
        offerTitle: (plan: string) => `Ask for an Offer on ${plan}`,
        offerBody: 'Your request reaches our team; we send you an offer for your volume and needs by e-mail.',
        recommended: 'Recommended Package',
        includes: 'What the package includes',
        notIncluded: 'Not included',
        footnote:
            'Every package includes product, stock, order, shipping, customer and team management. There is no fee per order. Your e-invoice service’s, payment service’s and carrier’s fees are not part of the package price.',
        viewPlans: 'See Packages',
        trialStarted: 'Trial Started',
        trialLeft: 'Time Left',
        days: (count: number) => (count === 1 ? '1 day' : `${count} days`),
        trialOver: 'Ended',
        // Buying a package (ADR 0016).
        period: 'Period',
        periods: { monthly: 'Monthly', yearly: 'Yearly' },
        periodEnds: 'Period Ends',
        yearlyNotSold: 'Not sold by the year',
        buy: 'Buy',
        extend: 'Extend',
        buyTitle: (plan: string) => `Buy the ${plan} Package`,
        plan: 'Package',
        amount: 'Amount',
        netAmount: 'Package Price',
        vatAmount: (rate: number) => `VAT (${rate}%)`,
        totalAmount: 'Amount to Pay',
        // A different package bought while a paid period runs takes over when it ends (ADR 0017 §8).
        scheduled: (date: string, plan: string, period: string) => `Your package changes to ${plan} (${period}) on ${date}.`,
        // A seller cancelling their own package at the end of the paid period (ADR 0016 §6).
        cancel: {
            action: 'Cancel Subscription',
            confirm: {
                title: 'Subscription Will Be Cancelled',
                body: (plan: string, date: string) =>
                    `Your ${plan} package can be used until ${date} and will not be renewed. After that date your orders, invoices and stock keep working; no new product, marketplace account, user or automation can be added. The period paid for is not refunded. You can take the cancellation back until that date.`,
                action: 'Cancel Subscription',
            },
            cancelled: (plan: string, date: string) =>
                `You cancelled your subscription. Your ${plan} package can be used until ${date} and will not be renewed.`,
            resume: 'Take Back Cancellation',
            resumed: 'The cancellation was taken back. Your package continues as before.',
            ended: (date: string) => `The period of the package you cancelled ended on ${date}. Buying a package again opens everything at once.`,
        },
        // One change waits at a time (ADR 0017 §8): while it does, nothing is bought.
        scheduledLocked:
            'A change of package is waiting, so no other package can be bought and no period extended for now. To change it, contact our support team.',
        contactSupport: 'Contact Support',
        appliesAt: (date: string, plan: string) =>
            `Your package changes to ${plan} on ${date}, when the period you have paid for ends. Until then your current package stays.`,
        loses: 'These features close on the new package:',
        // The six features a package sets, by the names docs/15 gives them.
        features: {
            storefront: 'Tezgah',
            invoicing: 'e-Invoice',
            price_rules: 'Pricing',
            suppliers: 'Suppliers',
            integrations: 'API Access and Webhooks',
            finance: 'Finance',
        },
        exceeding: 'Your usage is above the new package’s limits. What you have is kept; nothing new can be added:',
        method: 'How to Pay',
        methods: { bank_transfer: 'Bank Transfer', card: 'Card' },
        transferHint: 'Your package starts as soon as the transfer is approved.',
        // The buttons that take on the payment say so (ADR 0018 §2).
        createTransfer: 'Confirm and Show Transfer Details',
        payByCard: 'Confirm and Pay',
        // The texts a purchase is made on, and the three boxes under them.
        agreement: {
            title: 'Pre-Information Form and Distance Service Contract',
            loading: 'Preparing the contract',
            preInformation: 'I have read and confirm the Pre-Information Form and the Distance Service Contract.',
            commercialPurpose: 'I am making this purchase for my commercial or professional activity.',
            immediateStart: 'I confirm that the service starts as soon as I pay, and I know that I therefore have no right of withdrawal.',
            view: 'View the Contract',
            viewLabel: (reference: string) => `View the contract of payment ${reference}`,
            viewerTitle: 'Contract',
            acceptedAt: 'Accepted',
            reference: 'Payment Reference',
        },
        closed: 'Packages cannot be bought here yet. You can leave a request to change package.',
        switched: 'Your package has been changed.',
        pending: {
            title: 'Waiting for Payment',
            transferBody:
                'Send the transfer to the account below with the reference in its description. Your package starts as soon as it is approved.',
            cardBody: 'The card payment has not been completed.',
            holder: 'Account Holder',
            bank: 'Bank',
            iban: 'IBAN',
            reference: 'Reference',
            referenceHint: 'Write this reference in the transfer description.',
            cancel: 'Cancel the Payment',
            confirmCancel: {
                title: 'The Payment Will Be Cancelled',
                body: 'The transfer instructions will no longer apply. A transfer already sent is refunded by our team.',
                action: 'Cancel It',
            },
        },
        history: 'Payment History',
        historyEmpty: 'No Payments Yet',
        columns: {
            date: 'Date',
            plan: 'Package',
            amount: 'Amount',
            method: 'Method',
            status: 'Status',
            reference: 'Reference',
            invoice: 'Invoice',
            agreement: 'Contract',
        },
        // A paid package's invoice (ADR 0016 §9): downloadable once issued, and
        // being prepared until then, whatever holds it up on our side.
        downloadInvoice: 'Download Invoice',
        invoicePreparing: 'Being Prepared',
        // held: received while a change of package waited, and not yet applied (ADR 0017 §8).
        chargeStatus: { pending: 'Awaiting Approval', paid: 'Paid', held: 'On Hold', failed: 'Failed', cancelled: 'Cancelled', refunded: 'Refunded' },
        // A card waits on its service, never on staff.
        cardPending: 'Not Completed',
        companyIncomplete: 'To buy a package, your company details need a legal name, a tax number, an address and a phone.',
        completeCompany: 'Complete Your Company Details',
        card: {
            paid: 'Your payment has been received. Your package has started.',
            failed: 'Your payment did not go through. You can try again by card.',
            failedBecause: (reason: string) => `Your payment did not go through: ${reason}`,
            cancelled: 'You did not complete the payment. Nothing was taken from your card.',
            held: 'Your payment has been received. It is not applied yet, because a change of package is waiting on your account; our support team will contact you.',
            waiting: 'Waiting for the payment service to confirm. This takes a few seconds.',
        },
        // Safe operation (ADR 0017 §4): orders keep moving, growth waits for the payment.
        banner: {
            pastDue:
                'Your subscription period has ended. If it is not paid within three days, orders, invoices and stock keep working but nothing new can be added.',
            expired:
                'Your subscription has expired. Orders, shipping, invoices, returns and stock keep working; new products, marketplace accounts, users, suppliers and automation wait for your payment, and open as soon as it is received.',
            action: 'Go to Subscription',
        },
        statuses: {
            trialing: 'Trial',
            active: 'Active',
            past_due: 'Payment Due',
            canceled: 'Canceled',
            expired: 'Expired',
        },
    },

    // A limit the package sets, met where something is added (ADR 0017 §3, docs/15 §2).
    planLimits: {
        reached: {
            products:
                'You have reached the product limit your package includes. Your products and orders keep working. To add a product, remove one or choose another package.',
            channels:
                'You have reached the marketplace account limit your package includes. Your connected accounts and their orders keep working. To connect another, remove a connection or choose another package.',
            users: 'You have reached the user limit your package includes. Your team keeps working. To add someone, disable a member or choose another package.',
        },
        over: {
            products: (used: string, limit: string) =>
                `Your product count is above your package’s limit (${used} / ${limit}). What you have is kept; nothing new can be added.`,
            channels: (used: string, limit: string) =>
                `Your marketplace account count is above your package’s limit (${used} / ${limit}). What you have is kept; nothing new can be connected.`,
            users: (used: string, limit: string) =>
                `Your user count is above your package’s limit (${used} / ${limit}). What you have is kept; nothing new can be added.`,
        },
    },

    bulk: {
        export: 'Export for Excel',
        importProducts: 'Import Products',
        importTitle: 'Import Products',
        importSubtitle:
            'Add products in bulk, with their prices, stock and pictures, from an Excel file (.xlsx) or one saved from Excel as CSV. Download the template first.',
        downloadTemplate: 'Download Template',
        preview: 'Preview',
        commit: 'Import',
        previewNote: 'This is a preview; nothing is saved.',
        resultCreated: 'Created',
        // A row naming a product the catalogue holds is left as it is, and a row past
        // the package's product limit is left out (ADR 0017 §2).
        resultExisting: 'Already in Catalogue',
        resultOverLimit: 'Past the Package Limit',
        existing: 'Already in catalogue',
        rowOverLimit: 'Not added: the package’s product limit.',
        rowLapsed: 'Not added: the subscription has expired.',
        // A row bringing a price or a stock the role may not write (ADR 0014 §6).
        rowNotPermitted: 'Not added: your role cannot write the price or the stock on this row.',
        overLimitNotice: (count: number, committed: boolean) => `${count} row(s) ${committed ? 'were' : 'will be'} left out.`,
        resultFailed: 'Failed',
        resultTotal: 'Total Rows',
        line: 'Line',
        sku: 'SKU',
        // What a row that adds a product brings (ADR 0019 §10).
        salePrice: 'Sale Price',
        listPrice: 'List Price',
        stock: 'Stock',
        images: 'Pictures',
        status: 'Status',
        ok: 'OK',
        pickFirst: 'Choose a file first.',
        // What the preview read the file as (ADR 0019 §7).
        detectedWorkbook: 'File read: an Excel file (.xlsx), its first sheet.',
        detectedText: (separator: string, encoding: string) =>
            `File read: saved from Excel as CSV; separator ${separator}, character encoding ${encoding}.`,
        separators: { semicolon: 'semicolon (;)', comma: 'comma (,)', tab: 'tab' },
        encodings: { 'utf-8': 'UTF-8', 'utf-16': 'UTF-16 (Unicode Text)', 'windows-1254': 'Windows-1254 (Turkish)' },
        columnsTitle: 'Columns',
        columnsHint: 'The first row names the columns, as in the template. Other columns are left out.',
        columns: {
            sku: 'Product code. Required, at most 64 characters, once per file.',
            title: 'Product name. Required, at most 300 characters.',
            description: 'Description. Optional.',
            taxRate: 'VAT rate: 0, 1, 10 or 20. Left blank, it is 0.',
            salePrice: 'Sale price. Optional; each sales channel can use this price or take one of its own.',
            listPrice: 'List price (the struck-through one). Optional; only beside a sale price, and not below it.',
            currency: 'The price’s currency: ₺, USD, EUR or GBP. Left blank, it is ₺.',
            stock: 'Opening stock, a whole number counted into the default warehouse. Optional.',
            images: 'Picture addresses, one beginning with https in each column. The first is the main picture; the pictures arrive within minutes of the product.',
        },
        limitsHint: 'A file holds at most 5,000 rows and 16 MB. Formulas are not calculated; the value Excel saved is read.',
        lettersHint: 'If Turkish letters look wrong, save the file in Excel as .xlsx.',
        // A row's product already in the catalogue is left as it is (ADR 0019 §10).
        existingHint:
            'A row whose code is already in the catalogue changes nothing: the product keeps its price, stock and pictures. Change a price or a stock on the Bulk Edit page or on the product’s Stock and Price tab.',
        draftHint:
            'Imported products are added as Draft and sent to no sales channel. Your shop shows active products only: on the Products page, select them and make them Active, and add them to your sales channels with Add to Channel.',
        // The supplier route beside the file (docs/11 §8): a supplier's product list is added under Suppliers.
        supplier: {
            title: 'Supplier Product List',
            open: 'If your supplier publishes their products at an address (an XML link, say), add that product list on the Suppliers page. The products are imported from there and kept up to date each time the list is read.',
            link: 'Go to Suppliers',
            notInPlan: 'Product lists your supplier publishes at an address (an XML link, say) are not in your package.',
            includedIn: (plan: string) => `The ${plan} package includes them.`,
        },
        selectAll: 'Select all',
        selectRow: 'Select row',
        selected: 'selected',
        selectAllMatching: (n: number) => `Select All ${n} Matching`,
        allSelected: (n: number) => `All ${n} matching selected`,
        activate: 'Activate',
        archive: 'Archive',
        clear: 'Clear',
        undo: 'Undo',
        statusDone: (count: number, status: string) => `${count} product(s) set to ${status}.`,
        statusDoneWithdrawn: (count: number, status: string) =>
            `${count} product(s) set to ${status}. Taking them off sale on your sales channels is queued.`,
        deleteDone: (count: number) => `${count} product(s) deleted.`,
        categoryDone: (count: number, category: string) => `${count} product(s) moved to the “${category}” category.`,
        categoryCleared: (count: number) => `${count} product(s) no longer have a category.`,
        undone: (count: number) => `Bulk edit undone. ${count} product(s) are back as they were.`,
        rematch: 'Rematch',
        issueInvoices: 'Issue Invoices',
        confirmIssue: {
            title: (count: number) => `${count} Orders Will Be Invoiced`,
            body: 'Each is issued through the default e-invoice service. An issued invoice is a legal document: it cannot be withdrawn, only reversed by a return.',
            action: 'Issue Invoices',
        },
        issued: (issued: number, failed: number) =>
            failed === 0 ? `${issued} invoice(s) issued.` : `${issued} invoice(s) issued, ${failed} order(s) could not be invoiced.`,
        issuing: (done: number, total: number) => `Invoices are being issued in the background: ${done} of ${total} order(s) done.`,
        issueRefusedQuoted: (count: number) =>
            `The e-invoice service refused ${count} order(s) for reasons of its own. Issue the invoice again from the order's page to read its reason.`,
        advance: 'Change Status',
        advanceHint: (count: number) => `Every package of the ${count} selected order(s) will be sent this status.`,
        advanceStatusHint:
            'Only the statuses every selected order’s channel accepts. A status that asks who received a delivery is set on the order itself.',
        advanceNone: 'The selected orders’ channels share no status that can be set for all of them at once. Narrow the selection to one channel.',
        advanced: (advanced: number, skipped: number, failed: number) =>
            `${advanced} order(s) sent, ${skipped} order(s) had no package, ${failed} order(s) refused.`,
        // Its own name, because the page header carries a menu too and a
        // screen reader reading "More Actions" twice cannot say which is which.
        more: 'More for the Selection',
        printSlips: 'Print Package Slips',
        slipsReady: (count: number) => `${count} sheet(s) ready.`,
        slipsFailed: (ready: number, failed: number) => `${ready} sheet(s) ready, ${failed} could not be made.`,
        slipsOpen: 'Open each sheet to print it.',
        slipPerOrder: 'One Sheet per Order',
        slipPerOrderHint: 'Where the marketplace split the order across packages, this puts them on one sheet. The barcode is the order number.',
        // A sheet is named by the order it belongs to, because that is the
        // only thing on it a seller can match to a parcel on the table
        // (EPZ-0059). The number after it separates one order's parcels.
        slipName: (orderNumber: string) => `Package Slip - ${orderNumber}`,
        slipNameNumbered: (orderNumber: string, index: number) => `Package Slip - ${orderNumber} - ${index}`,
    },

    account: {
        title: 'My Account',
        subtitle: 'Your name, your e-mail address and your password.',
        company: {
            title: 'Company Details',
            hint: 'What appears on invoices, in your shop and in marketplace records, and what buying a package needs.',
            name: 'Company Name',
            legalName: 'Legal Name',
            legalNameHint: 'The full registered name. The company name is used when this is blank.',
            taxNumber: 'Tax Number',
            taxOffice: 'Tax Office',
            addressLine: 'Address',
            city: 'Province',
            district: 'District',
            choose: 'Choose',
            phone: 'Phone',
            saved: 'Saved.',
            addressNote: (address: string) => `Your shop is at ${address}. It stays the same when the company details change.`,
            exportAll: 'Download All Data',
            exportAllHint:
                'The products, orders, invoices, customers, returns and newsletter subscribers you may see, as files Excel opens, in one ZIP file.',
        },
        profile: {
            title: 'Your Details',
            hint: 'The name shown in the panel and on the mail you send.',
            name: 'Full Name',
            saved: 'Saved.',
        },
        email: {
            title: 'E-mail Address',
            hint: 'The address you sign in with.',
            current: 'Current Address',
            unverified: 'not verified',
            next: 'New Address',
            nextHint: 'We send a confirmation link to the new address. Your account stays on the old one until you follow it.',
            password: 'Your Password',
            passwordHint: 'To confirm the account is yours.',
            submit: 'Send the Confirmation Link',
            sent: (email: string) => `We sent a confirmation link to ${email}.`,
        },
        notificationEmail: {
            title: 'E-mail Notifications',
            hint: (company: string) =>
                `Choose which notifications in your inbox are also sent to your e-mail address. The choice applies to ${company} only; each company you belong to has its own.`,
            unverified: 'Your e-mail address is not verified yet, so nothing is sent to it. Follow the link we e-mailed you to verify it.',
            empty: 'No Notifications to Choose',
            emptyHint: 'Your role does not let you read any of the notifications that can be sent by e-mail.',
            saved: 'Saved.',
            types: {
                'order.received': { label: 'New Order', hint: 'When an order arrives from a sales channel or from your shop.' },
                'question.received': { label: 'New Customer Question', hint: 'When a customer asks a question about one of your products.' },
                'claim.opened': { label: 'New Return or Cancellation Request', hint: 'When a customer opens a return or cancellation request.' },
                'invoice.failed': { label: 'Invoice Not Issued', hint: 'When the e-invoice service cannot issue an invoice.' },
                'listing.rejected': { label: 'Rejected Products', hint: 'When a sales channel rejects products you sent.' },
                'listing.prices_sent_back': {
                    label: 'Prices and Stock Sent Again',
                    hint: 'When a sales channel showed a different price from yours, or more stock than your shelf, and yours is sent again.',
                },
                'listing.buybox_lost': { label: 'Lost Buy Box', hint: 'When you lose first place in a marketplace’s buy box.' },
                'channel.failed': { label: 'Sales Channel Connection Problem', hint: 'When the connection to a sales channel fails.' },
                'subscription.changed': { label: 'Subscription Change', hint: 'When our team updates your subscription.' },
                'stock.low': { label: 'Low Stock', hint: 'When the sellable stock of a product runs low.' },
                'product.loss': { label: 'Products Sold at a Loss', hint: 'When products lost money on the orders settled the day before.' },
            },
        },
        password: {
            title: 'Password',
            hint: 'Changing your password ends every other session you have open.',
            current: 'Current Password',
            next: 'New Password',
            nextHint: 'At least 12 characters.',
            again: 'New Password Again',
            mismatch: 'The two passwords do not match.',
            submit: 'Change the Password',
            saved: 'Your password has changed.',
        },
        twoFactor: {
            title: 'Two-Step Verification',
            hint: 'Signing in asks for a code from an authenticator app on your phone as well as your password.',
            on: 'On',
            off: 'Off',
            enable: 'Turn On',
            disable: 'Turn Off',
            setupTitle: 'Turn On Two-Step Verification',
            scan: 'Scan this code with an authenticator app such as Google Authenticator or Microsoft Authenticator, then enter the code it shows.',
            qrLabel: 'QR code for the authenticator app',
            setupKey: 'Setup Key',
            setupKeyHint: 'If you cannot scan the code, enter this key in the app.',
            code: 'Verification Code',
            confirm: 'Turn On',
            recoveryTitle: 'Your Recovery Codes',
            recoveryHint: 'Each code signs you in once if you lose your phone. Keep them somewhere safe: this is the only time they are shown.',
            done: 'I Saved Them',
            disableTitle: 'Turn Off Two-Step Verification',
            disableHint: 'Enter your password and a code from your authenticator app, or a recovery code.',
            password: 'Password',
            disableConfirm: 'Turn Off',
            copy: { copy: 'Copy', copied: 'Copied' },
        },
        sessions: {
            title: 'Open Sessions',
            hint: 'Where your account is signed in. End any you do not recognise.',
            empty: 'No Other Session Is Open',
            thisOne: 'This Device',
            end: 'End Session',
            endTitle: 'End Session',
            endBody: 'That device will be signed out and will have to sign in again.',
            unknownDevice: 'Unknown Device',
            unknownPlace: 'unknown place',
            endOthers: 'End Other Sessions',
            endOthersTitle: 'Other Sessions Will Be Ended',
            endOthersBody: 'Every device except this one will be signed out and will have to sign in again.',
            endedOthers: 'Other sessions ended.',
        },
    },

    questions: {
        allSources: 'All Sources',
        sourceLabel: 'Source',
        sourceManual: 'Logged by Hand',
        overdueOnly: 'Overdue Only',
        searchPlaceholder: 'Search questions',
        showing: (shown: number, total: number) => `${shown} of ${total}`,
        title: 'Questions',
        subtitle: 'Open questions first, soonest-due at the top.',
        open: 'Open',
        answered: 'Answered',
        all: 'All',
        empty: 'No Questions Waiting',
        emptyFiltered: 'No Question Matches the Filters',
        answerPlaceholder: 'Write your answer…',
        send: 'Send',
        cannedInsert: 'Insert Saved Answer',
        overdue: 'Overdue',
        askedAt: 'Asked',
        timeLeft: 'Time Left to Answer',
        underAnHour: 'Under an hour',
        hoursLeft: (hours: number) => (hours === 1 ? '1 hour' : `${hours} hours`),
        daysLeft: (days: number) => (days === 1 ? '1 day' : `${days} days`),
        from: 'From',
        answeredLabel: 'Answer',
        subject: 'Question',
        product: 'Product',
        statusColumn: 'Status',
        channelFailed: 'Not Sent to the Marketplace',
        channelFailedHint: 'This answer did not reach the marketplace. Post it there by hand.',
        channelFailedResend: 'This answer did not reach the marketplace. Correct the answer above and send it again.',
        statusAnswered: 'Answered',
        statusSending: 'Sending to the Marketplace',
        statusClosed: 'Closed',
        resend: 'Send Again',
        reply: 'Reply',
        refused: 'Not delivered',
        conversation: 'Conversation',
        fromBuyer: 'Customer',
        fromSeller: 'You',
        answerLimit: (min: number, max: number) => `${min}–${max} characters`,
        answerCount: (length: number, max: number) => `${length} / ${max} characters`,
        answerTooShort: (min: number) => `This marketplace asks for at least ${min} characters.`,
    },

    sizeGuides: {
        title: 'Size Charts',
        subtitle: 'A chart filed under a category is linked from the product pages of that category and those beneath it, in your shop.',
        add: 'Create a Size Chart',
        edit: 'Edit the Size Chart',
        name: 'Title',
        nameHint: 'Shoppers see it above the table, e.g. "Women\'s Tops".',
        table: 'Table',
        tableHint: 'The first column is usually the size; the others what is measured. An empty row is left out.',
        column: (index: number) => `Column ${index} heading`,
        cell: (row: number, column: number) => `Row ${row}, column ${column}`,
        addColumn: 'Add a Column',
        addRow: 'Add a Row',
        removeColumn: (index: number) => `Remove column ${index}`,
        removeRow: (index: number) => `Remove row ${index}`,
        note: 'How to Measure',
        noteHint: 'Optional. Shown under the table.',
        categories: 'Categories',
        categoriesHint: 'Its subcategories show it too, unless one has a chart of its own. A category has one chart.',
        save: 'Save',
        delete: 'Delete',
        empty: 'No Size Charts Yet',
        emptyHint: 'Write a chart and file it under a category; its product pages link to it.',
        filedUnder: (count: number) => (count === 0 ? 'Not filed under a category' : count === 1 ? '1 category' : `${count} categories`),
        sizes: (count: number) => (count === 1 ? '1 size' : `${count} sizes`),
        updated: 'Updated',
        starter: { size: 'Size', chest: 'Chest (cm)', waist: 'Waist (cm)' },
        confirmDelete: {
            title: 'Size Chart Will Be Deleted',
            body: (title: string) => `"${title}" is removed and its categories' product pages no longer link to it.`,
            action: 'Delete',
        },
    },

    reviews: {
        title: 'Reviews',
        subtitle: 'Customers write reviews in the shop; the ones you approve show on the product page.',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        empty: 'No Reviews in This State',
        product: 'Product',
        author: 'By',
        rating: 'Rating',
        outOfFive: (value: number) => `${value} out of 5`,
        date: 'Date',
        approve: 'Approve',
        reject: 'Reject',
        reply: 'Reply',
        replyPlaceholder: 'A short reply the customer sees…',
        replySave: 'Save Reply',
        verifiedPurchase: (orderNumber: string) => `Verified purchase · ${orderNumber}`,
        pendingBadge: (count: number) => `${count} Pending`,
        photos: 'Photos',
        photo: (index: number) => `Photo ${index}`,
        hiddenPhoto: 'Hidden',
        hidePhoto: 'Hide',
        showPhoto: 'Show',
        photosHint: 'A photo you hide stays with the review and is not shown in the shop.',
    },

    notifications: {
        title: 'Notifications',
        subtitle: 'Platform announcements and system events in one place.',
        empty: 'No Notifications',
        markRead: 'Mark as Read',
        markAll: 'Mark All Read',
        unreadCount: (count: number) => (count === 1 ? '1 unread' : `${count} unread`),
        allRead: 'All read.',
        level: {
            info: 'Info',
            success: 'Success',
            warning: 'Warning',
            error: 'Error',
        },
    },

    newsletter: {
        title: 'Newsletter',
        subtitle: 'The campaigns you write to your shop’s subscribers, the subscriber list, and the people waiting for stock.',
        tabs: {
            campaigns: 'Campaigns',
            subscribers: 'Subscribers',
            waits: 'Waiting for Stock',
        },
        campaignsHint:
            'A campaign goes to every subscriber who has not withdrawn consent. Each e-mail carries your shop’s name and address and a one-click unsubscribe link.',
        add: 'New Campaign',
        edit: 'Edit the Campaign',
        empty: 'No Campaigns Yet',
        emptyHint: 'Write your first campaign and send it to yourself as a try first.',
        subject: 'Subject',
        subjectHint: 'The title shown in the inbox.',
        body: 'Campaign Text',
        editor: {
            editor: 'Campaign Text',
            block: 'Text Style',
            paragraph: 'Paragraph',
            heading2: 'Heading',
            heading3: 'Subheading',
            bold: 'Bold',
            italic: 'Italic',
            underline: 'Underline',
            strike: 'Strikethrough',
            bulletList: 'Bulleted List',
            orderedList: 'Numbered List',
            quote: 'Quote',
            link: 'Add Link',
            linkPrompt: 'Address to link to (leave empty to remove)',
            linkApply: 'Apply',
            linkCancel: 'Cancel',
            source: 'HTML',
        },
        status: 'Status',
        statuses: {
            draft: 'Draft',
            sending: 'Sending',
            sent: 'Sent',
        },
        recipients: 'Delivery',
        progress: (sent: number, total: number, failed: number, skipped: number) =>
            [`${sent} / ${total} sent`, failed > 0 ? `${failed} failed` : '', skipped > 0 ? `${skipped} skipped` : ''].filter(Boolean).join(' · '),
        date: 'Date',
        send: 'Send',
        confirmSend: {
            title: 'The Campaign Will Be Sent',
            body: (subject: string, count: number) => `"${subject}" will be sent to ${count} subscribers. Once sent it cannot be changed or deleted.`,
            action: 'Send',
        },
        save: 'Save',
        test: 'Send a Try',
        testSent: 'A try was sent to your own address.',
        delete: 'Delete',
        confirmDelete: {
            title: 'The Draft Will Be Deleted',
            body: 'This campaign draft will be deleted.',
            action: 'Delete',
        },
        search: 'Search E-mail',
        all: 'All',
        subscriberStatus: {
            active: 'Subscribed',
            left: 'Left',
            removed: 'Removed',
        },
        download: 'Download the List',
        counts: (active: number, left: number) => `${active} subscribed · ${left} left`,
        email: 'E-mail',
        source: 'Source',
        sources: {
            footer: 'Footer',
            signup: 'Sign-up Form',
            account: 'Hesabım',
            other: 'Other',
        },
        consentedAt: 'Consent Date',
        withdraw: 'Remove from the List',
        confirmWithdraw: {
            title: 'The Subscriber Will Be Removed',
            body: (email: string) => `${email} will no longer receive campaigns. The record is kept with the date consent was withdrawn.`,
            action: 'Remove from the List',
        },
        noSubscribers: 'No Subscribers Yet',
        noSubscribersHint: 'People who subscribe from your shop’s footer or its sign-up form appear here.',
        more: 'Show More',
        waitsHint: 'People who asked "Gelince Haber Ver" for a sold-out product. Each is e-mailed when stock arrives.',
        noWaits: 'Nobody Is Waiting for Stock',
        noWaitsHint: 'People who leave an e-mail on a sold-out product’s page appear here.',
        product: 'Product',
        sku: 'SKU',
        waiting: 'Waiting',
        since: 'First Request',
    },
    coupons: {
        title: 'Discount Codes',
        subtitle: (ownStore: string) => `Discount codes for ${ownStore}. Applied at checkout.`,
        add: 'Create a Code',
        edit: 'Edit the Code',
        code: 'Code',
        codeHint: 'The code a customer types at checkout, e.g. SUMMER10.',
        codeLocked: 'A code cannot be changed once it is created.',
        kind: 'Discount Type',
        percentage: 'Percentage (%)',
        fixed: 'Fixed Amount',
        valuePercentage: 'Discount (%)',
        valueFixed: 'Discount Amount',
        minSubtotal: 'Minimum Basket',
        minSubtotalHint: 'The code does not apply below this amount. Leave blank to accept any basket.',
        usageLimit: 'Usage Limit',
        usageLimitHint: 'The code works for at most this many orders. Leave blank for no limit.',
        startsOn: 'Starts On',
        endsOn: 'Ends On',
        windowHint: 'Leave both empty and the code runs until you switch it off.',
        freeShipping: 'Free Shipping',
        save: 'Save',
        empty: 'No Codes Yet',
        count: (count: number) => (count === 1 ? '1 discount code' : `${count} discount codes`),
        discount: 'Discount',
        usage: 'Usage',
        active: 'Active',
        inactive: 'Inactive',
        notStarted: 'Not Started',
        expired: 'Expired',
        usedUp: 'Used Up',
        delete: 'Delete',
        confirmDelete: {
            title: 'The Discount Code Will Be Deleted',
            body: (code: string) => `${code} will no longer be accepted at checkout. Orders that used it do not change.`,
            action: 'Delete Code',
        },
    },

    promotions: {
        title: 'Automatic Discounts',
        subtitle: (ownStore: string) => `Discounts ${ownStore} applies by itself, with no code. A discount code applies to what they leave.`,
        add: 'Create a Discount',
        edit: 'Edit the Discount',
        name: 'Name',
        nameHint: 'Shoppers see it in the basket, e.g. "Kitchen Week".',
        kind: 'Discount Type',
        percentage: 'Percentage Discount',
        buyXPayYKind: 'Buy X Pay Y',
        percent: 'Discount (%)',
        percentHint:
            'Taken off each unit and shown as the price. The price struck through is the lowest your shop charged in the ten days before the discount began; where that is no saving, none is struck.',
        begunHint:
            'This discount has begun. Changing its discount, its quantities, what it applies to or moving its start later ends it now and begins a new one with the new terms.',
        buy: 'Buy',
        pay: 'Pay For',
        buyXPayYHint: 'Of every set the shopper buys from this group, the cheapest units are free.',
        buyXPayY: (buy: number, pay: number) => `Buy ${buy} Pay ${pay}`,
        scope: 'Applies To',
        onCategory: 'A Category',
        onProducts: 'Chosen Products',
        category: 'Category',
        categoryHint: 'Its subcategories are covered too.',
        pickCategory: 'Choose a category',
        products: 'Products',
        searchProducts: 'Search by product name or code',
        searchResults: 'Products found',
        chosenProducts: 'Chosen products',
        productsNeedCatalogue: 'Searching for products needs access to the product list.',
        noProducts: 'No products chosen yet.',
        pick: 'Add',
        added: 'Added',
        remove: 'Remove',
        addProduct: (title: string) => `Add ${title}`,
        removeProduct: (title: string) => `Remove ${title}`,
        productCount: (count: number) => (count === 1 ? '1 product' : `${count} products`),
        startsOn: 'Starts On',
        endsOn: 'Ends On',
        endsOnHint: 'Leave blank and it runs until you end it.',
        save: 'Save',
        empty: 'No Automatic Discounts Yet',
        emptyHint: 'Create a percentage discount on a category or products, or a buy X pay Y offer.',
        count: (count: number) => (count === 1 ? '1 automatic discount' : `${count} automatic discounts`),
        discount: 'Discount',
        active: 'Active',
        notStarted: 'Not Started',
        ended: 'Ended',
        end: 'End',
        confirmEnd: {
            title: 'The Discount Will End',
            body: (name: string) => `${name} stops applying in the shop now. Orders it priced do not change.`,
            action: 'End the Discount',
        },
    },

    apiTokens: {
        title: 'Keys',
        count: (count: number) => (count === 1 ? '1 key' : `${count} keys`),
        subtitle: 'Create keys that let your own software reach your account. Each key can do only what you allow.',
        add: 'Create Key',
        name: 'Key Name',
        nameHint: 'So you remember which software uses it, e.g. "Accounting software".',
        scopes: 'Permissions',
        scopesHint: 'A key can only be given permissions you hold yourself. If you lose a permission, your keys lose it too.',
        create: 'Create',
        createdTitle: 'Key Created — Shown Only Now',
        createdNote: 'Copy this key somewhere safe. It will not be shown again.',
        empty: 'No Keys Yet',
        emptyHint: 'Create a key to connect your own software to your account.',
        prefix: 'Key',
        lastUsed: 'Last Used',
        never: 'Never',
        revoked: 'Revoked',
        revoke: 'Revoke',
        confirmRevoke: {
            title: 'The Key Will Be Revoked',
            body: 'Software using this key can no longer reach your account. This cannot be undone.',
            action: 'Revoke',
        },
    },

    webhooks: {
        title: 'Notification Addresses',
        count: (count: number) => (count === 1 ? '1 address' : `${count} addresses`),
        subtitle: 'Tell your own software the moment something happens, such as a new order.',
        add: 'Add Address',
        url: 'Address',
        urlHint: 'The public address that receives the notifications, starting with https.',
        secret: 'Secret Key',
        secretHint:
            'At least 8 characters. Every notification is signed with it; your software checks the signature in the X-Webhook-Signature header to know the notification came from us.',
        events: 'Events',
        eventsHint: 'Separate with commas, e.g. order.created. Leave empty to receive every event.',
        allEvents: 'All Events',
        save: 'Save',
        failed: 'The address was not saved. Check that it starts with https and the secret key has at least 8 characters.',
        sendTest: 'Send a Test Notification',
        testSent: 'Test notification sent. Its result appears under Recent notifications.',
        endpointsEmpty: 'No Notification Addresses Yet',
        endpointsEmptyHint: 'Add an address to tell your own software when something happens.',
        deliveries: 'Recent Notifications',
        deliveriesEmpty: 'No Notifications Sent Yet',
        event: 'Event',
        eventNames: {
            'order.created': 'New Order',
            ping: 'Test Notification',
        },
        status: 'Status',
        attempts: 'Attempts',
        response: 'Response Code',
        when: 'Date',
        delete: 'Delete',
        confirmDelete: {
            title: 'The Notification Address Will Be Deleted',
            body: 'Nothing will be sent to this address any more.',
            action: 'Delete',
        },
        statuses: {
            pending: 'Pending',
            success: 'Success',
            failed: 'Failed',
        },
    },

    roles: {
        owner: 'Owner',
        admin: 'Admin',
        operations: 'Operations',
        finance: 'Finance',
        readonly: 'Read Only',
    },
} as const;

/**
 * The message contract every locale must satisfy.
 *
 * Mapped rather than `typeof en` directly, so translations are not forced to
 * repeat the literal English strings that `as const` would otherwise demand.
 *
 * Recursive, because sections nest: a group holding a message function has to
 * keep that function's signature rather than collapsing it to a plain string.
 */
type Translated<Source> = Source extends (...args: infer Args) => string
    ? (...args: Args) => string
    : Source extends string
      ? string
      : { readonly [Key in keyof Source]: Translated<Source[Key]> };

export type Messages = Translated<typeof en>;

/** Nested sections addressed by name, e.g. `Section<'products'>`. */
export type Section<Name extends keyof Messages> = Messages[Name];
